// ═══════════════════════════════════════════════════════════════════════════════
// MOBILE ORGANIZER DASHBOARD - 6,166 lines
// ═══════════════════════════════════════════════════════════════════════════════
// NAVIGATION: Use Cmd+Shift+O to jump to regions/functions
// 
// STRUCTURE:
//   [IMPORTS] - Dependencies and components
//   [TYPES] - TypeScript interfaces
//   [COMPONENT] - Main MobileOrganizerDashboard
//   [STATE] - React state declarations
//   [EFFECTS] - useEffect hooks  
//   [HANDLERS] - Event handlers and functions
//   [RENDER] - JSX render logic
// ═══════════════════════════════════════════════════════════════════════════════

//#region IMPORTS
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { NotificationPanel } from '@/components/NotificationPanel';
import TodoListPIP from '@/components/TodoListPIP';
import MusicPlayerDock from '@/components/MusicPlayerDock';
import LateNightSnackPanel from '@/components/LateNightSnackPanel';
import OrganizerAIPanel from '@/components/OrganizerAIPanel';
import BudgetAssistant from '@/components/BudgetAssistant';
import WhiptVideoAd from '@/components/WhiptVideoAd';
import AppsIntegrationPanel from '@/components/AppsIntegrationPanel';
import CalendarImportModal from '@/components/CalendarImportModal';
import EventAssignmentModal, { EventAssignment } from '@/components/EventAssignmentModal';
import { SuggestionBox } from '@/components/SuggestionBox';
import { DigitalWalletPanel } from '@/components/DigitalWalletPanel';
import { GmailImportModal } from '@/components/GmailImportModal';
import InstallPWAPrompt from '@/components/InstallPWAPrompt';
import { useEntity } from '@/hooks/useEntity';
import { useUnreadMessageCount } from '@/hooks/useUnreadMessageCount';
import { DueDateNotificationService } from '@/services/dueDateNotifications';
import { MeetingReminderService } from '@/services/meetingReminders';
import { PushNotificationService } from '@/services/pushNotifications';
import { notificationScheduler } from '@/services/notificationScheduler';
import { snackSpotEntityConfig } from '@/entities/SnackSpot';
import { organizerItemEntityConfig } from '@/entities/OrganizerItem';
import { documentEntityConfig } from '@/entities/Document';
import { userEntityConfig } from '@/entities/User';
import { getCalendarEvents, createCalendarEvent, updateCalendarEvent, deleteCalendarEvent } from '@/lib/calendarRepository';
import type { CalendarEvent } from '@/lib/calendarRepository';
import type { SnackSpot, SnackSpotDraft } from '@/components/LateNightSnackPanel';
import StudentVirtualRoom, { type StudentVirtualRoomProps } from '@/components/StudentVirtualRoom';
import MobileVirtualRoomWrapper from '@/components/MobileVirtualRoomWrapper';
import { VirtualRoomPractice } from '@/components/VirtualRoomPractice';
import SimpleVideoRoomPractice from '@/components/SimpleVideoRoomPractice';
import { UnifiedVirtualRoomCard } from '@/components/UnifiedVirtualRoomCard';
import TabTemplateModal from '@/components/TabTemplateModal';
import WhiptTranscriptsPanel from '@/components/WhiptTranscriptsPanel';
import SessionRecordingsPanel from '@/components/SessionRecordingsPanel';
import { loadNotebooks, saveNotebooks, loadMeetingTranscripts, loadScheduledSessions, saveScheduledSessions } from '@/lib/organizerRepository';
import { supabase } from '@/lib/supabase';
import { categorizeWalletDocument, extractIdentifiers } from '@/lib/walletCategorizer';
import { turso } from '@/database/client';

// Home/Work/Travel Forms
import ShoppingListForm from '@/components/ShoppingListForm';
import BillsTrackerForm from '@/components/BillsTrackerForm';
import RecipeForm from '@/components/RecipeForm';
import HomeImprovementForm from '@/components/HomeImprovementForm';
import MaintenanceReminderForm from '@/components/MaintenanceReminderForm';
import MeetingForm from '@/components/MeetingForm';
import DeadlineForm from '@/components/DeadlineForm';
import PackingListForm from '@/components/PackingListForm';
import ItineraryForm from '@/components/ItineraryForm';
import TravelBudgetForm from '@/components/TravelBudgetForm';
import ClassItemForm from '@/components/ClassItemForm';
//#endregion

//#region TYPES
type VirtualRoomProfile = Omit<StudentVirtualRoomProps, 'onClose'> & {
  id: string;
};

const VIRTUAL_ROOM_PROFILES: Record<string, VirtualRoomProfile> = {
  discussion: {
    id: 'discussion',
    tabLabel: 'Discussion',
    roomTitle: 'Discussion Session',
    description: 'Host a text-friendly salon with live captions for peers who prefer written dialogue.',
    agenda: [],
    accessibilityNote: 'Live captions + parallel chat stay pinned so hard-of-hearing students never miss the verbal thread.',
    participants: [
      { id: '1', name: 'You', role: 'Participant', initials: 'YOU', online: true },
    ],
    resources: [],
  },
  assignment: {
    id: 'assignment',
    tabLabel: 'Assignment',
    roomTitle: 'Assignment Session',
    description: 'Sprint on drafts with screenshare and shared text-only check-ins.',
    agenda: [],
    accessibilityNote: 'Everyone can mute audio and collaborate via captions + chat without leaving the room.',
    participants: [
      { id: '1', name: 'You', role: 'Participant', initials: 'YOU', online: true },
    ],
    resources: [],
  },
  experiential: {
    id: 'experiential',
    tabLabel: 'Experiential',
    roomTitle: 'Experiential Session',
    description: 'Upload recordings, narrate observations, and capture captions for community partners.',
    agenda: [],
    accessibilityNote: 'Voice, ASL, and typed updates all sync to the same record for later export.',
    participants: [
      { id: '1', name: 'You', role: 'Participant', initials: 'YOU', online: true },
    ],
    resources: [],
  },
  journal: {
    id: 'journal',
    tabLabel: 'Journal',
    roomTitle: 'Journal Session',
    description: 'Private reflection space with optional AI transcription.',
    agenda: [],
    accessibilityNote: 'Fully private journaling with voice or text input.',
    participants: [
      { id: '1', name: 'You', role: 'Participant', initials: 'YOU', online: true },
    ],
    resources: [],
  },
  video: {
    id: 'video',
    tabLabel: 'Social/Family',
    roomTitle: 'Social & Family',
    description: 'Plan events, share moments, and coordinate with friends and family.',
    agenda: [],
    accessibilityNote: 'Share photos, videos, and coordinate gatherings with loved ones.',
    participants: [
      { id: '1', name: 'You', role: 'Participant', initials: 'YOU', online: true },
    ],
    resources: [],
  },
  home: {
    id: 'home',
    tabLabel: 'Home',
    roomTitle: 'Home & Living',
    description: 'Manage household tasks, maintenance, and home projects.',
    agenda: [],
    accessibilityNote: 'Keep track of home maintenance, repairs, and household to-dos.',
    participants: [
      { id: '1', name: 'You', role: 'Participant', initials: 'YOU', online: true },
    ],
    resources: [],
  },
  work: {
    id: 'work',
    tabLabel: 'Work',
    roomTitle: 'Work & Career',
    description: 'Track projects, meetings, and professional development.',
    agenda: [],
    accessibilityNote: 'Manage work tasks, deadlines, and career goals.',
    participants: [
      { id: '1', name: 'You', role: 'Participant', initials: 'YOU', online: true },
    ],
    resources: [],
  },
  travel: {
    id: 'travel',
    tabLabel: 'Travel',
    roomTitle: 'Travel & Adventures',
    description: 'Plan trips, store boarding passes, and track travel documents.',
    agenda: [],
    accessibilityNote: 'Keep all travel documents, itineraries, and memories in one place.',
    participants: [
      { id: '1', name: 'You', role: 'Participant', initials: 'YOU', online: true },
    ],
    resources: [],
  },
};

// Types from OrganizerPage
type OrganizerTone = "experiential" | "assignment" | "meeting";

type OrganizerItem = {
  id: number;
  title: string;
  tone: OrganizerTone;
  course?: string;
  instructor?: string;
  dueDate?: string;
  notes?: string;
  created_at: string;
};

type NotebookDocument = {
  id: number;
  title: string;
  content: string;
  category: string;
  created_at: string;
};

type OrganizerUser = {
  id: number;
  name: string;
  email: string;
};

type ClassNotebook = {
  id: string;
  name: string;
  courseCode: string;
  instructor: string;
  teacherPhoto?: string; // Base64 encoded image or URL
  categories: Array<{
    id: string;
    name: string;
    color: string;
    icon: string;
  }>;
};
//#endregion

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export const MobileOrganizerDashboard: React.FC = () => {
  //#region STATE DECLARATIONS
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(user?.show_online_status ?? true);
  
  // Update local state when user changes
  React.useEffect(() => {
    if (user) {
      setIsOnline(user.show_online_status ?? true);
    }
  }, [user]);
  
  const toggleOnlineStatus = async () => {
    if (!user) return;
    
    try {
      const newStatus = !isOnline;
      setIsOnline(newStatus); // Optimistic update
      
      // Check if using localStorage fallback
      const users = localStorage.getItem('whipt_dev_users');
      if (users) {
        const userList = JSON.parse(users);
        const userIndex = userList.findIndex((u: any) => u.id === user.id);
        if (userIndex !== -1) {
          userList[userIndex].show_online_status = newStatus;
          localStorage.setItem('whipt_dev_users', JSON.stringify(userList));
        }
      } else {
        // Update in database
        const { turso } = await import('@/database/client');
        await turso.execute({
          sql: 'UPDATE users SET show_online_status = ? WHERE id = ?',
          args: [newStatus ? 1 : 0, user.id]
        });
      }
      
      await refreshUser();
      playSound('click');
    } catch (error) {
      console.error('[OnlineStatus] Failed to toggle:', error);
      setIsOnline(!isOnline); // Revert on error
    }
  };
  const [tabOverlayOpen, setTabOverlayOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('');
  
  // Mobile overlay state
  const [showMobileOverlay, setShowMobileOverlay] = useState(false);
  const [mobileOverlayData, setMobileOverlayData] = useState<{
    type: string | null;
    className: string;
    classId: string;
    categoryId: string;
  }>({ type: null, className: '', classId: '', categoryId: '' });
  
  // Form selector state
  const [showFormSelector, setShowFormSelector] = useState(false);
  const [activeFormType, setActiveFormType] = useState<string | null>(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState<'current' | 'all'>('current'); // current category or all categories
  const [showMusicPlayer, setShowMusicPlayer] = useState(false);
  const [showTodoList, setShowTodoList] = useState(false);
  const [showSnackPanel, setShowSnackPanel] = useState(false);
  const [showAIPanel, setShowAIPanel] = useState(false);

  // Runtime check: Alert if TodoListPIP is accidentally disabled
  useEffect(() => {
    const checkTodoComponent = () => {
      const todoExists = document.querySelector('[class*="TodoListPIP"]') || 
                        document.querySelector('[data-component="TodoListPIP"]');
      if (!todoExists && showTodoList) {
        console.error('⚠️ CRITICAL: TodoListPIP component may be commented out! Check MobileOrganizerDashboard.tsx line ~4858');
      }
    };
    const timer = setTimeout(checkTodoComponent, 2000);
    return () => clearTimeout(timer);
  }, [showTodoList]);

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showBudgetAssistant, setShowBudgetAssistant] = useState(false);
  const [showAppsPanel, setShowAppsPanel] = useState(false);
  const [showCalendarImport, setShowCalendarImport] = useState(false);
  const [importedEvents, setImportedEvents] = useState<any[]>([]);
  const [showEventAssignment, setShowEventAssignment] = useState(false);
  const [showDigitalWallet, setShowDigitalWallet] = useState(false);
  const [showGmailImport, setShowGmailImport] = useState(false);
  const [session, setSession] = useState<any>(null);
  const [showCategorySelector, setShowCategorySelector] = useState(false);
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadTabSelection, setUploadTabSelection] = useState<string>('');
  const [showTabTemplateModal, setShowTabTemplateModal] = useState(false);
  const [virtualRoomOpen, setVirtualRoomOpen] = useState(false);
  const [virtualRoomMinimized, setVirtualRoomMinimized] = useState(false);
  const [virtualRoomContext, setVirtualRoomContext] = useState<VirtualRoomProfile | null>(null);
  const [tickerIndex, setTickerIndex] = useState(0);
  const [classNotebooks, setClassNotebooks] = useState<any[]>([]);
  const [notebooksLoading, setNotebooksLoading] = useState(true);
  const [selectedItemMenu, setSelectedItemMenu] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [editItemTitle, setEditItemTitle] = useState('');
  const [showAddClassModal, setShowAddClassModal] = useState(false);
  const [editingClassId, setEditingClassId] = useState<string | null>(null);
  const [newClassName, setNewClassName] = useState('');
  const [newProfessorName, setNewProfessorName] = useState('');
  const [newTeacherPhoto, setNewTeacherPhoto] = useState<string>('');
  const [showClassSelector, setShowClassSelector] = useState<string | null>(null);
  const [deletedItems, setDeletedItems] = useState<any[]>([]);
  const [showTrash, setShowTrash] = useState(false);
  const [openMoveMenuId, setOpenMoveMenuId] = useState<string | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('whipt_sound_enabled');
    return saved !== null ? saved === 'true' : true;
  });
  const [activeSemester, setActiveSemester] = useState(() => {
    const saved = localStorage.getItem('whipt_active_semester');
    return saved || `Spring ${new Date().getFullYear()}`;
  });
  const [calendarDate, setCalendarDate] = useState(new Date()); // Current month/year being viewed
  const [showSemesterModal, setShowSemesterModal] = useState(false);
  const [newSemesterName, setNewSemesterName] = useState('Spring');
  const [newSemesterYear, setNewSemesterYear] = useState(new Date().getFullYear().toString());
  const [showArchivedClasses, setShowArchivedClasses] = useState(false); // Toggle archived classes visibility
  const [showTestNotificationModal, setShowTestNotificationModal] = useState(false);
  const [testNotificationResults, setTestNotificationResults] = useState<any[]>([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showAgendaModal, setShowAgendaModal] = useState(false);
  const [scheduleForRoom, setScheduleForRoom] = useState<string>('');
  const [agendaForRoom, setAgendaForRoom] = useState<string>('');
  const [roomAgendas, setRoomAgendas] = useState<Record<string, string>>({});
  const [showLinkCopiedModal, setShowLinkCopiedModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState('');
  const [copiedRoomName, setCopiedRoomName] = useState('');
  const [showTranscriptsPanel, setShowTranscriptsPanel] = useState(false);
  const [transcriptsPanelMinimized, setTranscriptsPanelMinimized] = useState(false);
  const [showRecordingsPanel, setShowRecordingsPanel] = useState(false);
  const [recordingsPanelMinimized, setRecordingsPanelMinimized] = useState(false);
  const [showPracticeRoom, setShowPracticeRoom] = useState(false);
  const [activeItemFilter, setActiveItemFilter] = useState<string>('all'); // Filter for Home/Work/Travel items
  const [showHistoryView, setShowHistoryView] = useState(false); // Show completed items history
  const [showTemplatesView, setShowTemplatesView] = useState(false); // Show templates view
  const [currentListName, setCurrentListName] = useState<string>(''); // Name for current list
  const [showHiddenHistory, setShowHiddenHistory] = useState(false); // Toggle to show hidden history items
  const [showAllDocuments, setShowAllDocuments] = useState(false); // Toggle to show all documents in wallet
  const [listTemplates, setListTemplates] = useState<any[]>(() => {
    // Load templates from localStorage
    const saved = localStorage.getItem('whipt_list_templates');
    return saved ? JSON.parse(saved) : [];
  });

  // Get unread message count for badge
  const unreadMessageCount = useUnreadMessageCount();

  // Sound effects
  const playSound = (type: 'click' | 'notification' | 'alert' | 'success') => {
    if (!soundEnabled) return;
    
    const sounds = {
      click: { frequency: 800, duration: 50 },
      notification: { frequency: 1200, duration: 100 },
      alert: { frequency: 600, duration: 150 },
      success: { frequency: 1000, duration: 200 }
    };
    
    const sound = sounds[type];
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = sound.frequency;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + sound.duration / 1000);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + sound.duration / 1000);
  };

  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('whipt_sound_enabled', String(newValue));
    playSound('click');
  };

  // Entity hooks - synced with OrganizerPage
  const { items: organizerItems } = useEntity<OrganizerItem>(organizerItemEntityConfig);
  const { items: documents, create: createDocument, reload: reloadDocuments, delete: deleteDocument } = useEntity<NotebookDocument>(documentEntityConfig);
  const { items: users } = useEntity<OrganizerUser>(userEntityConfig);
  
  // Force reload on mount
  useEffect(() => {
    reloadDocuments();
  }, [reloadDocuments]);
  
  // Load notebooks from database (NO user_id filter - data created before userId system)
  useEffect(() => {
    const loadNotebooksData = async () => {
      try {
        if (!user?.id) {
          setClassNotebooks([]);
          setNotebooksLoading(false);
          return;
        }
        // Load notebooks for current user only
        const result = await turso.execute({
          sql: `SELECT * FROM notebooks WHERE user_id = ? ORDER BY created_at DESC LIMIT 1`,
          args: [user.id]
        });
        if (result.rows.length === 0) {
          setClassNotebooks([]);
        } else {
          // Parse the JSON content from the user's row
          const userRow = result.rows[0];
          const notebooks = JSON.parse(String(userRow.content));
          console.log('📚 [MOBILE] Classes:', notebooks.map((nb: any) => nb.title).join(', '));
          console.log('📚 [MOBILE] First class full structure:', JSON.stringify(notebooks[0], null, 2));
          
          setClassNotebooks(notebooks);
          
          // Set active class to first notebook if not set
          if (notebooks.length > 0 && !activeClassId) {
            setActiveClassId(notebooks[0].id);
          }
        }
      } catch (error) {
        console.error('❌ Error loading notebooks:', error);
        setClassNotebooks([]);
      } finally {
        setNotebooksLoading(false);
      }
    };
    
    loadNotebooksData();
  }, []); // No dependency on user.id since we're loading ALL
  
  // Auto-refresh notebooks when window gains focus (for desktop/mobile sync)
  useEffect(() => {
    const handleFocus = async () => {
      if (user?.id) {
        try {
          // Reload notebooks
          const notebooks = await loadNotebooks(user.id);
          if (notebooks.length > 0) {
            setClassNotebooks(notebooks);
            console.log('🔄 Synced notebooks on focus');
          }
          
          // Also reload calendar events
          if (typeof loadCalendarEvents === 'function') {
            await loadCalendarEvents();
            console.log('🔄 Synced calendar events on focus');
          }

          // Also reload scheduled sessions
          try {
            const sessions = await loadScheduledSessions(user.id);
            if (sessions.length > 0) {
              setScheduledSessions(sessions);
              console.log('🔄 Synced scheduled sessions on focus');
            }
          } catch (sessErr) {
            console.warn('Could not load scheduled sessions on focus:', sessErr);
          }
        } catch (error) {
          console.error('Error syncing on focus:', error);
        }
      }
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user?.id]);
  
  // Get Supabase session for Gmail access
  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
    };
    getSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);
  
  // Start due date notification checker when user logs in
  useEffect(() => {
    if (user?.id) {
      DueDateNotificationService.startDueDateChecker(user.id);
      MeetingReminderService.startMeetingChecker(user.id);
      notificationScheduler.start(60000); // Check every minute
      
      return () => {
        DueDateNotificationService.stopDueDateChecker();
        MeetingReminderService.stopMeetingChecker();
        notificationScheduler.stop();
      };
    }
  }, [user?.id]);
  
  // Log loaded data
  useEffect(() => {
  }, [documents.length, organizerItems.length, users.length]);
  
  // Snack Spots Entity
  const {
    items: snackSpots,
    loading: snackLoading,
    create: createSnackSpot,
    update: updateSnackSpot,
    delete: deleteSnackSpot,
    reload: reloadSnackSpots,
  } = useEntity<SnackSpot>(snackSpotEntityConfig);

  // State for class notebooks (loaded from database) - already declared above near line 130
  const [activeClassId, setActiveClassId] = useState("");
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [scheduledSessions, setScheduledSessions] = useState<any[]>([]);
  const [showCalendarPopup, setShowCalendarPopup] = useState(false);
  const [calendarPopupItems, setCalendarPopupItems] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [eventFormData, setEventFormData] = useState({
    title: '',
    type: 'meeting' as 'meeting' | 'appointment' | 'reminder' | 'all-day',
    date: '',
    time: '',
    duration: 60,
    location: '',
    attendees: '',
    notes: '',
    reminder: '15min',
    category: 'personal'
  });
  const [tickerItems, setTickerItems] = useState<Array<{ text: string; date: string }>>([
    { text: 'Loading events...', date: '' }
  ]);
  const [calendarSyncState, setCalendarSyncState] = useState<'idle' | 'syncing' | 'ok' | 'error'>('idle');
  const [calendarLastSyncedAt, setCalendarLastSyncedAt] = useState<string | null>(null);
  const [calendarSyncError, setCalendarSyncError] = useState<string | null>(null);

  const parseCalendarSource = (notes?: string) => {
    if (!notes) return null;
    try {
      const parsed = JSON.parse(notes);
      return typeof parsed === 'object' && parsed ? parsed : null;
    } catch {
      return null;
    }
  };

  const findCalendarEventBySourceItemId = (sourceItemId: string) => {
    return calendarEvents.find((event) => {
      const source = parseCalendarSource(event.notes);
      return source?.sourceItemId === sourceItemId;
    });
  };

  const loadCalendarEvents = async () => {
    if (!user?.id) {
      setCalendarEvents([]);
      setCalendarSyncState('idle');
      setCalendarLastSyncedAt(null);
      setCalendarSyncError(null);
      return;
    }

    setCalendarSyncState('syncing');
    setCalendarSyncError(null);

    try {
      const events = await getCalendarEvents(String(user.id));
      setCalendarEvents(events);
      setCalendarSyncState('ok');
      setCalendarLastSyncedAt(new Date().toISOString());
    } catch (error) {
      console.error('[MobileCalendar] Failed to load events:', error);
      setCalendarSyncState('error');
      setCalendarSyncError(error instanceof Error ? error.message : 'Unknown sync error');
    }
  };

  const getCalendarSyncStatusText = () => {
    if (calendarSyncState === 'syncing') return 'Syncing…';
    if (calendarSyncState === 'error') return 'Sync issue';
    if (calendarSyncState === 'idle') return 'Not synced';
    if (!calendarLastSyncedAt) return 'Synced';

    const elapsedMs = Date.now() - new Date(calendarLastSyncedAt).getTime();
    const elapsedMinutes = Math.max(0, Math.floor(elapsedMs / 60000));

    if (elapsedMinutes < 1) return 'Synced now';
    if (elapsedMinutes < 60) return `Synced ${elapsedMinutes}m ago`;

    const elapsedHours = Math.floor(elapsedMinutes / 60);
    return `Synced ${elapsedHours}h ago`;
  };

  useEffect(() => {
    loadCalendarEvents();
  }, [user?.id]);

  // Load ticker items from category items with due dates
  useEffect(() => {
    const loadTickerItems = async () => {
      if (!activeClassId || classNotebooks.length === 0) return;
      
      try {
        // Find the active notebook
        const activeNotebook = classNotebooks.find(nb => nb.id === activeClassId);
        if (!activeNotebook) {
          setTickerItems([{ text: 'No class selected', date: 'Select a class to see upcoming items' }]);
          return;
        }

        // Extract all items from categories with due dates
        const allItems: any[] = [];
        if (activeNotebook.categories) {
          activeNotebook.categories.forEach((category: any) => {
            if (category.buckets) {
              Object.values(category.buckets).forEach((bucket: any) => {
                if (Array.isArray(bucket)) {
                  bucket.forEach((item: any) => {
                    if (item.dueDate) {
                      allItems.push({
                        ...item,
                        categoryLabel: category.label
                      });
                    }
                  });
                }
              });
            }
          });
        }

        // Sort by due date and get next 4 upcoming items
        const upcoming = allItems
          .filter(item => {
            const dueDate = new Date(item.dueDate);
            return dueDate >= new Date(); // Only future/today items
          })
          .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
          .slice(0, 4)
          .map(item => ({
            text: `${item.categoryLabel || 'Event'} • ${item.title}`,
            date: new Date(item.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          }));
        
        if (upcoming.length > 0) {
          setTickerItems(upcoming);
        } else {
          setTickerItems([{ text: 'No upcoming items', date: 'Add items to your categories' }]);
        }
      } catch (error) {
        console.error('Failed to load ticker items:', error);
        setTickerItems([{ text: 'Unable to load items', date: 'Check connection' }]);
      }
    };
    
    loadTickerItems();
  }, [activeClassId, classNotebooks]);

  // Load class notebooks from database
  useEffect(() => {
    // TODO: Load from database when class entity is created
    // For now, using placeholder
    const loadedClasses: ClassNotebook[] = [];
    setClassNotebooks(loadedClasses);
  }, []);
  useEffect(() => {
    reloadSnackSpots();
  }, [reloadSnackSpots]);

  // Handle deep links for virtual room invitations
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const roomId = params.get('room');
    const classId = params.get('class');
    const autoJoin = params.has('autojoin') || params.get('join') === '1';
    
    if (!roomId || !autoJoin || virtualRoomOpen) return;
    
    // Set active class if provided and notebooks are loaded
    if (classId && classNotebooks.length > 0 && classNotebooks.some(nb => nb.id === classId)) {
      setActiveClassId(classId);
    }
    
    // Open the room (works for both class and personal meetings)
    const profile = VIRTUAL_ROOM_PROFILES[roomId as keyof typeof VIRTUAL_ROOM_PROFILES];
    if (profile) {
      setVirtualRoomContext(profile);
      setVirtualRoomOpen(true);
      playSound('success');
      
      // Clean up URL after opening room
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      // Custom/dynamic room ID (e.g., work-123456-abc, home-123456-xyz)
      // Extract category from room ID if possible
      const categoryMatch = roomId.match(/^(work|home|travel|class)-/);
      const category = categoryMatch ? categoryMatch[1] : 'work';
      
      const categoryLabels: Record<string, string> = {
        work: 'Work Meeting',
        home: 'Home Meeting',
        travel: 'Travel Meeting',
        class: 'Class Meeting'
      };
      
      const genericProfile: VirtualRoomProfile = {
        id: roomId,
        tabLabel: categoryLabels[category] || 'Meeting Room',
        roomTitle: categoryLabels[category] || 'Virtual Meeting',
        description: 'Join the meeting',
        agenda: [],
        accessibilityNote: 'Live captions available',
        participants: [],
        resources: []
      };
      setVirtualRoomContext(genericProfile);
      setVirtualRoomOpen(true);
      playSound('success');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [classNotebooks, virtualRoomOpen, setActiveClassId, setVirtualRoomContext, setVirtualRoomOpen, playSound]);

  // Snack spot handlers
  const handleCreateSnackSpot = async (payload: SnackSpotDraft) => {
    await createSnackSpot(payload as any);
    await reloadSnackSpots();
  };

  const handleUpdateSnackSpot = async (id: number, payload: Partial<SnackSpot>) => {
    await updateSnackSpot(String(id), payload);
    await reloadSnackSpots();
  };

  const handleDeleteSnackSpot = async (id: number) => {
    await deleteSnackSpot(String(id));
    await reloadSnackSpots();
  };

  const handleMarkSnackOrdered = async (id: number) => {
    await updateSnackSpot(String(id), { lastOrderedAt: new Date().toISOString() });
    await reloadSnackSpots();
  };

  // Auto-scroll ticker every 4 seconds
  React.useEffect(() => {
    if (tickerItems.length === 0) return;
    
    const timer = setInterval(() => {
      setTickerIndex((prev) => (prev + 1) % tickerItems.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [tickerItems.length]);

  // Prevent body scroll when sidebar is open
  React.useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  // Handle swipe gestures
  React.useEffect(() => {
    let touchStartX = 0;
    let touchStartY = 0;
    let touchEndX = 0;
    let touchEndY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.changedTouches[0].screenX;
      touchStartY = e.changedTouches[0].screenY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      const currentX = e.changedTouches[0].screenX;
      const currentY = e.changedTouches[0].screenY;
      const diffX = Math.abs(currentX - touchStartX);
      const diffY = Math.abs(currentY - touchStartY);

      // If horizontal swipe is detected, prevent default to block browser navigation
      if (diffX > diffY && diffX > 10) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      touchEndX = e.changedTouches[0].screenX;
      touchEndY = e.changedTouches[0].screenY;
      handleSwipe();
    };

    const handleSwipe = () => {
      const swipeThreshold = 50;
      const diff = touchEndX - touchStartX;
      const verticalDiff = Math.abs(touchEndY - touchStartY);

      // Only handle if it's primarily a horizontal swipe
      if (Math.abs(diff) > verticalDiff) {
        // Swipe right to open (only from left edge)
        if (diff > swipeThreshold && touchStartX < 50 && !sidebarOpen) {
          setSidebarOpen(true);
        }
        // Swipe left to close
        else if (diff < -swipeThreshold && sidebarOpen) {
          setSidebarOpen(false);
        }
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [sidebarOpen]);

  const openTabOverlay = (tabName: string) => {
    const activeClass = classNotebooks.find(nb => nb.id === activeClassId);
    setMobileOverlayData({
      type: tabName,
      className: activeClass?.title || activeClass?.name || 'Class',
      classId: activeClassId,
      categoryId: tabName
    });
    setShowMobileOverlay(true);
    setActiveTab(tabName);
    setActiveItemFilter('all');
    setTabOverlayOpen(true);
  };

  const closeTabOverlay = () => {
    setShowMobileOverlay(false);
    setTabOverlayOpen(false);
    setActiveTab('');
    setActiveItemFilter('all');
    setShowFormSelector(false);
    setActiveFormType(null);
  };
  
  // Helper functions for overlay
  const filterOutVirtualRoomItems = (items: any[]) => {
    return items.filter(item => {
      if (item.eventType === 'meeting') return false;
      if (item.type === 'meeting') return false;
      const title = (item.title || '').toLowerCase();
      if (title.includes('session') || title.includes('virtual room') || title.includes('transcript')) return false;
      return true;
    });
  };
  
  const sortByDueDate = (items: any[]) => {
    return [...items].sort((a, b) => {
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    });
  };

  // Filter documents by category for active tab
  const getFilteredDocuments = () => {
    if (!activeTab) return [];
    
    // Map tab names to notebookTabId values (for Documents)
    const tabIdMap: Record<string, string> = {
      'discussion': 'discussion',
      'assignment': 'assignment',
      'experiential': 'experiential',
      'journal': 'journal',
      'home': 'home',
      'work': 'work',
      'travel': 'travel'
    };
    
    const targetTabId = tabIdMap[activeTab];
    
    // Filter documents by notebookTabId (ignore notebookId to show ALL classes)
    const filteredDocs = documents.filter(doc => 
      doc.notebookTabId === targetTabId
    );
    
    console.log('🔍 Filtering documents:', {
      activeTab,
      targetTabId,
      totalDocuments: documents.length,
      filteredDocsCount: filteredDocs.length,
      allTabIds: documents.map(d => d.notebookTabId)
    });
    
    return filteredDocs;
  };

  // Filter organizer items by tone/category - extract from notebooks structure
  const getFilteredOrganizerItems = () => {
    if (!activeTab || !activeClassId) return [];
    // Find the active notebook
    const activeNotebook = classNotebooks.find(nb => nb.id === activeClassId);
    if (!activeNotebook) {
      return [];
    }
    // Extract all items from the active notebook
    const allItems: any[] = [];
    if (activeNotebook.categories) {
      activeNotebook.categories.forEach((category: any) => {
        // Each category has buckets (phases)
        if (category.buckets) {
          Object.values(category.buckets).forEach((bucket: any) => {
            if (Array.isArray(bucket)) {
              allItems.push(...bucket.map((item: any) => ({
                ...item,
                categoryId: category.id,
                categoryLabel: category.label,
                notebookId: activeNotebook.id
              })));
            }
          });
        }
      });
    }
    // DEBUG: Show sample of items with their categories
    if (allItems.length > 0) {
      console.log('📊 Sample items with categories:', allItems.slice(0, 5).map(item => ({
        title: item.title,
        categoryId: item.categoryId,
        categoryLabel: item.categoryLabel
      })));
    }
    
    // Map tab names to category IDs
    const categoryIdMap: Record<string, string> = {
      'discussion': 'discussion',
      'assignment': 'assignment',
      'experiential': 'experiential',
      'journal': 'journal',
      'home': 'home',
      'work': 'work',
      'travel': 'travel'
    };
    
    const targetCategoryId = categoryIdMap[activeTab];
    const filtered = allItems.filter(item => 
      item.categoryId === targetCategoryId
    );
    // Filter OUT virtual room notes (they have their own section)
    const itemsWithoutTranscripts = filtered.filter(item => {
      const title = item.title?.toLowerCase() || '';
      const description = item.description?.toLowerCase() || '';
      const id = item.id?.toString() || '';
      
      // Exclude items that look like virtual room transcripts or test items
      const isTranscript = title.includes('session transcription') || 
                           title.includes('virtual room') ||
                           title.includes('meeting notes') ||
                           description.includes('session transcription') ||
                           description.includes('virtual room') ||
                           // Filter out test items created from virtual room
                           (title.includes('session') && (title.includes('beta') || title.includes('test'))) ||
                           // Filter by ID pattern (numeric IDs are user-created, not imported)
                           (id && /^\d+$/.test(id));
      return !isTranscript;
    });
    return itemsWithoutTranscripts;
  };

  const filteredDocuments = getFilteredDocuments();
  const filteredOrganizerItems = getFilteredOrganizerItems();
  
  // Combine both documents and organizer items for display
  let allFilteredItems = [
    ...filteredDocuments.map(doc => ({
      id: doc.id,
      type: 'document' as const,
      title: doc.title,
      content: doc.content,
      created_at: doc.created_at,
      data: doc
    })),
    ...filteredOrganizerItems.map(item => ({
      id: item.id,
      type: 'organizer' as const,
      title: item.title,
      content: item.notes,
      created_at: item.dueDate,
      data: item
    }))
  ];

  // Add calendar events when viewing Work > Meetings
  if (activeTab === 'work' && (activeItemFilter === 'meeting' || activeItemFilter === 'all')) {
    const workCalendarEvents = calendarEvents
      .filter(event => event.category === 'work' || event.category === 'meeting')
      .map(event => ({
        id: event.id,
        type: 'calendar-event' as const,
        title: event.title,
        content: event.description || event.notes,
        created_at: event.due_date,
        data: {
          ...event,
          status: event.status || 'upcoming',
          metadata: {
            type: 'meeting',
            isCalendarEvent: true
          }
        }
      }));
    
    allFilteredItems = [...allFilteredItems, ...workCalendarEvents];
  }

  // Apply Home/Work/Travel item type filter
  if (['home', 'work', 'travel'].includes(activeTab) && activeItemFilter !== 'all') {
    allFilteredItems = allFilteredItems.filter(item => {
      if (item.type === 'organizer' && item.data?.metadata?.type) {
        return item.data.metadata.type === activeItemFilter;
      }
      // Keep calendar events if they match the filter
      if (item.type === 'calendar-event' && item.data?.metadata?.type) {
        return item.data.metadata.type === activeItemFilter;
      }
      return false;
    });
  }

  // Apply search filter
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase().trim();
    if (searchMode === 'all') {
      // Search ALL categories in the active class
      const allCategoryItems: typeof allFilteredItems = [];
      const activeNotebook = classNotebooks.find(nb => nb.id === activeClassId);
      if (activeNotebook?.categories) {
        activeNotebook.categories.forEach((category: any) => {
          if (category.buckets) {
            const categoryItems = [
              ...(category.buckets.upcoming || []),
              ...(category.buckets.completed || [])
            ];
            categoryItems.forEach((item: any) => {
              const title = (item.title || '').toLowerCase();
              const notes = (item.notes || '').toLowerCase();
              const description = (item.description || '').toLowerCase();
              const dueDate = (item.dueDate || '').toLowerCase();
              
              const matchesSearch = 
                title.includes(query) ||
                notes.includes(query) ||
                description.includes(query) ||
                dueDate.includes(query);
              
              if (matchesSearch) {
                allCategoryItems.push({
                  id: item.id,
                  type: 'organizer' as const,
                  title: item.title,
                  content: item.notes || item.description,
                  created_at: item.dueDate,
                  data: { ...item, categoryLabel: category.label || category.id }
                });
              }
            });
          }
        });
      }
      allFilteredItems = allCategoryItems;
    } else {
      // Search only current category
      const beforeFilter = allFilteredItems.length;
      allFilteredItems = allFilteredItems.filter(item => {
        const title = (item.title || '').toLowerCase();
        const content = (item.content || '').toLowerCase();
        const notes = (item.data?.notes || '').toLowerCase();
        const description = (item.data?.description || '').toLowerCase();
        const dueDate = (item.data?.dueDate || '').toLowerCase();
        
        const matchesTitle = title.includes(query);
        const matchesContent = content.includes(query);
        const matchesNotes = notes.includes(query);
        const matchesDescription = description.includes(query);
        const matchesDueDate = dueDate.includes(query);
        
        return matches;
      });
    }
  }

  console.log('📋 Combined items:', {
    documents: filteredDocuments.length,
    organizerItems: filteredOrganizerItems.length,
    total: allFilteredItems.length,
    searchActive: !!searchQuery.trim(),
    searchMode
  });

  // Move item to different category
  const handleMoveItem = (itemId: string, fromCategoryId: string, toCategoryId: string) => {
    setClassNotebooks((prev) => {
      const updated = prev.map((notebook) => {
        // Find the item to move
        let itemToMove: any | null = null;
        const fromCategory = notebook.categories?.find((cat: any) => cat.id === fromCategoryId);
        if (fromCategory?.buckets) {
          itemToMove = [...(fromCategory.buckets.upcoming || []), ...(fromCategory.buckets.completed || [])]
            .find((item: any) => item.id === itemId) || null;
        }
        
        if (!itemToMove) return notebook;
        
        // Remove from old category and add to new category
        return {
          ...notebook,
          categories: notebook.categories.map((category: any) => {
            if (category.id === fromCategoryId) {
              // Remove from this category
              return {
                ...category,
                buckets: {
                  upcoming: (category.buckets.upcoming || []).filter((item: any) => item.id !== itemId),
                  completed: (category.buckets.completed || []).filter((item: any) => item.id !== itemId),
                },
              };
            } else if (category.id === toCategoryId) {
              // Add to this category (keep same status)
              const status = itemToMove.status || 'upcoming';
              return {
                ...category,
                buckets: {
                  ...category.buckets,
                  [status]: [...(category.buckets[status] || []), itemToMove],
                },
              };
            }
            return category;
          }),
        };
      });
      
      // Save to database
      if (user?.id) {
        saveNotebooks(String(user.id), updated);
      }
      return updated;
    });
    
    setSelectedItemMenu(null);
    playSound('success');
  };

  // Update item title
  const handleUpdateItem = (itemId: string, categoryId: string, newTitle: string) => {
    setClassNotebooks((prev) => {
      const updated = prev.map((notebook) => {
        return {
          ...notebook,
          categories: notebook.categories.map((category: any) => {
            if (category.id === categoryId) {
              return {
                ...category,
                buckets: {
                  upcoming: (category.buckets.upcoming || []).map((item: any) =>
                    item.id === itemId ? { ...item, title: newTitle } : item
                  ),
                  completed: (category.buckets.completed || []).map((item: any) =>
                    item.id === itemId ? { ...item, title: newTitle } : item
                  ),
                },
              };
            }
            return category;
          }),
        };
      });
      
      // Save to database
      if (user?.id) {
        saveNotebooks(String(user.id), updated);
      }
      return updated;
    });
    
    setEditingItem(null);
    setEditItemTitle('');
    playSound('success');
  };

  // Toggle item completed status
  const handleToggleCompleted = async (itemId: string, categoryId: string) => {
    const activeNotebook = classNotebooks.find((notebook: any) => notebook.id === activeClassId);
    const activeCategory = activeNotebook?.categories?.find((category: any) => category.id === categoryId);
    const isCompleting = Boolean((activeCategory?.buckets?.upcoming || []).find((item: any) => item.id === itemId));

    setClassNotebooks((prev) => {
      const updated = prev.map((notebook) => {
        return {
          ...notebook,
          categories: notebook.categories.map((category: any) => {
            if (category.id === categoryId) {
              // Find item in upcoming or completed
              const upcomingItem = (category.buckets.upcoming || []).find((item: any) => item.id === itemId);
              const completedItem = (category.buckets.completed || []).find((item: any) => item.id === itemId);
              
              if (upcomingItem) {
                // Move to completed with metadata
                playSound('success');
                
                const now = new Date();
                const completedItemWithMetadata = {
                  ...upcomingItem,
                  status: 'completed',
                  completedAt: now.toISOString(),
                  metadata: {
                    ...upcomingItem.metadata,
                    groupDate: now.toISOString().split('T')[0], // YYYY-MM-DD for grouping
                    completedAt: now.toISOString(),
                    listName: currentListName || undefined // Store current list name
                  }
                };
                
                // Show completion notification
                const categoryLabel = category.label || category.name || 'Item';
                const emoji = categoryLabel.toLowerCase().includes('assignment') ? '📝' :
                             categoryLabel.toLowerCase().includes('discussion') ? '💬' :
                             categoryLabel.toLowerCase().includes('experiential') ? '🧪' : '📚';
                
                PushNotificationService.showNotification(
                  `✅ Great Job!`,
                  `${emoji} ${upcomingItem.title} marked complete!`,
                  () => window.location.href = '/organizer'
                );
                
                return {
                  ...category,
                  buckets: {
                    upcoming: category.buckets.upcoming.filter((item: any) => item.id !== itemId),
                    completed: [...(category.buckets.completed || []), completedItemWithMetadata],
                  },
                };
              } else if (completedItem) {
                // Move back to upcoming (remove completion metadata)
                const restoredItem = {
                  ...completedItem,
                  status: 'upcoming',
                  completedAt: undefined,
                  metadata: {
                    ...completedItem.metadata,
                    groupDate: undefined,
                    completedAt: undefined
                  }
                };
                
                return {
                  ...category,
                  buckets: {
                    upcoming: [...(category.buckets.upcoming || []), restoredItem],
                    completed: category.buckets.completed.filter((item: any) => item.id !== itemId),
                  },
                };
              }
            }
            return category;
          }),
        };
      });
      
      // Save to database
      if (user?.id) {
        saveNotebooks(String(user.id), updated);
      }
      return updated;
    });

    try {
      const sourceEvent = findCalendarEventBySourceItemId(itemId);
      if (sourceEvent) {
        await updateCalendarEvent(sourceEvent.id, {
          status: isCompleting ? 'completed' : 'pending'
        });
        await loadCalendarEvents();
      }
    } catch (error) {
      console.error('[MobileCalendar] Failed to sync completion status:', error);
    }
    
    playSound('click');
  };

  // Complete item from calendar popup (wrapper for handleToggleCompleted)
  const handleCompleteItem = (notebookId: string, categoryId: string, itemId: string) => {
    handleToggleCompleted(itemId, categoryId);
  };

  // Get completed items grouped by date for history view
  const getGroupedHistory = () => {
    if (!activeClassId || !['home', 'work', 'travel'].includes(activeTab)) return [];
    
    const activeNotebook = classNotebooks.find(nb => nb.id === activeClassId);
    if (!activeNotebook) return [];
    
    const category = activeNotebook.categories?.find((cat: any) => cat.id === activeTab);
    if (!category) return [];
    
    const completedItems = category.buckets?.completed || [];
    
    // Filter by active item filter type
    let filteredCompleted = activeItemFilter === 'all' 
      ? completedItems 
      : completedItems.filter((item: any) => item.metadata?.type === activeItemFilter);
    
    // Filter out hidden items unless showHiddenHistory is true
    if (!showHiddenHistory) {
      filteredCompleted = filteredCompleted.filter((item: any) => !item.metadata?.hidden);
    }
    
    // Group by date
    const grouped: Record<string, any> = {};
    filteredCompleted.forEach((item: any) => {
      const groupDate = item.metadata?.groupDate || item.completedAt?.split('T')[0] || 'Unknown';
      if (!grouped[groupDate]) {
        grouped[groupDate] = {
          items: [],
          listName: item.metadata?.listName || null, // Get list name from first item
          hidden: item.metadata?.hidden || false
        };
      }
      grouped[groupDate].items.push(item);
      // Use the first non-null list name found
      if (!grouped[groupDate].listName && item.metadata?.listName) {
        grouped[groupDate].listName = item.metadata.listName;
      }
    });
    
    // Convert to array and sort by date (newest first)
    return Object.entries(grouped)
      .map(([date, data]: [string, any]) => ({ 
        date, 
        items: data.items,
        listName: data.listName,
        hidden: data.hidden
      }))
      .sort((a, b) => b.date.localeCompare(a.date));
  };

  // Restore all items from a specific date group (with duplicate prevention)
  const handleRestoreGroup = (groupDate: string) => {
    if (!activeClassId || !activeTab) return;
    
    const activeNotebook = classNotebooks.find(nb => nb.id === activeClassId);
    if (!activeNotebook) return;
    
    const category = activeNotebook.categories?.find((cat: any) => cat.id === activeTab);
    if (!category) return;
    
    // Find items to restore
    const itemsToRestore = (category.buckets.completed || []).filter(
      (item: any) => item.metadata?.groupDate === groupDate
    );
    
    if (itemsToRestore.length === 0) return;
    
    // Check for duplicates
    const currentItems = category.buckets.upcoming || [];
    const duplicates = itemsToRestore.filter((restoreItem: any) =>
      currentItems.some((currentItem: any) => currentItem.title === restoreItem.title)
    );
    
    let confirmMessage = `Add ${itemsToRestore.length} item${itemsToRestore.length !== 1 ? 's' : ''} to your current list?`;
    if (duplicates.length > 0) {
      confirmMessage += `\n\n⚠️ Warning: ${duplicates.length} item${duplicates.length !== 1 ? 's' : ''} already exist${duplicates.length === 1 ? 's' : ''} in your list and will be skipped.`;
    }
    
    if (!window.confirm(confirmMessage)) return;
    
    setClassNotebooks((prev) => {
      const updated = prev.map((notebook) => {
        if (notebook.id !== activeClassId) return notebook;
        
        return {
          ...notebook,
          categories: notebook.categories.map((category: any) => {
            if (category.id !== activeTab) return category;
            
            // Find items from this group date
            const itemsToRestore = (category.buckets.completed || []).filter(
              (item: any) => item.metadata?.groupDate === groupDate
            );
            
            // Get current items to check for duplicates
            const currentItems = category.buckets.upcoming || [];
            
            // Remove completion metadata and filter out duplicates
            const restoredItems = itemsToRestore
              .filter((item: any) => !currentItems.some((current: any) => current.title === item.title))
              .map((item: any) => ({
                ...item,
                status: 'upcoming',
                completedAt: undefined,
                metadata: {
                ...item.metadata,
                groupDate: undefined,
                completedAt: undefined
              }
            }));
            
            return {
              ...category,
              buckets: {
                upcoming: [...(category.buckets.upcoming || []), ...restoredItems],
                completed: (category.buckets.completed || []).filter(
                  (item: any) => item.metadata?.groupDate !== groupDate
                ),
              },
            };
          }),
        };
      });
      
      if (user?.id) {
        saveNotebooks(String(user.id), updated);
      }
      
      playSound('success');
      setShowHistoryView(false); // Go back to current list
      return updated;
    });
  };

  // Rename a list in history
  const handleRenameList = (groupDate: string, currentName: string | null) => {
    const newName = window.prompt(
      'Enter a name for this list:',
      currentName || ''
    );
    
    if (newName === null) return; // Cancelled
    
    setClassNotebooks((prev) => {
      const updated = prev.map((notebook) => {
        if (notebook.id !== activeClassId) return notebook;
        
        return {
          ...notebook,
          categories: notebook.categories.map((category: any) => {
            if (category.id !== activeTab) return category;
            
            // Update all items in this group with the new list name
            const updatedCompleted = (category.buckets.completed || []).map((item: any) => {
              if (item.metadata?.groupDate === groupDate) {
                return {
                  ...item,
                  metadata: {
                    ...item.metadata,
                    listName: newName.trim() || undefined
                  }
                };
              }
              return item;
            });
            
            return {
              ...category,
              buckets: {
                ...category.buckets,
                completed: updatedCompleted
              }
            };
          }),
        };
      });
      
      if (user?.id) {
        saveNotebooks(String(user.id), updated);
      }
      
      playSound('click');
      return updated;
    });
  };

  // Hide/unhide a list in history
  const handleToggleHideList = (groupDate: string, currentlyHidden: boolean) => {
    if (!activeClassId || !activeTab) return;
    
    const action = currentlyHidden ? 'unhide' : 'hide';
    if (!window.confirm(`${action === 'hide' ? 'Hide' : 'Unhide'} this list?`)) return;
    
    setClassNotebooks((prev) => {
      const updated = prev.map((notebook) => {
        if (notebook.id !== activeClassId) return notebook;
        
        return {
          ...notebook,
          categories: notebook.categories.map((category: any) => {
            if (category.id !== activeTab) return category;
            
            // Update all items in this group with hidden flag
            const updatedCompleted = (category.buckets.completed || []).map((item: any) => {
              if (item.metadata?.groupDate === groupDate) {
                return {
                  ...item,
                  metadata: {
                    ...item.metadata,
                    hidden: !currentlyHidden,
                    hiddenAt: !currentlyHidden ? new Date().toISOString() : undefined
                  }
                };
              }
              return item;
            });
            
            return {
              ...category,
              buckets: {
                ...category.buckets,
                completed: updatedCompleted
              }
            };
          }),
        };
      });
      
      if (user?.id) {
        saveNotebooks(String(user.id), updated);
      }
      
      playSound('click');
      return updated;
    });
  };

  // Auto-cleanup old history lists
  const autoCleanupHistory = () => {
    const now = new Date();
    const threeMonthsAgo = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000)); // 90 days
    const sixMonthsAgo = new Date(now.getTime() - (180 * 24 * 60 * 60 * 1000)); // 180 days
    
    setClassNotebooks((prev) => {
      let hasChanges = false;
      
      const updated = prev.map((notebook) => {
        return {
          ...notebook,
          categories: notebook.categories.map((category: any) => {
            // Only process Home/Work/Travel categories
            if (!['home', 'work', 'travel'].includes(category.id)) return category;
            
            const updatedCompleted = (category.buckets?.completed || []).map((item: any) => {
              const completedDate = new Date(item.completedAt || item.metadata?.completedAt);
              const hasName = item.metadata?.listName && item.metadata.listName.trim().length > 0;
              const isHidden = item.metadata?.hidden;
              
              // Rule 1: Auto-hide unnamed lists older than 3 months
              if (!hasName && !isHidden && completedDate < threeMonthsAgo) {
                hasChanges = true;
                console.log(`[AUTO-CLEANUP] Auto-hiding list from ${completedDate.toDateString()}`);
                return {
                  ...item,
                  metadata: {
                    ...item.metadata,
                    hidden: true,
                    hiddenAt: now.toISOString(),
                    autoHidden: true
                  }
                };
              }
              
              return item;
            }).filter((item: any) => {
              // Rule 2: Delete hidden items older than 6 months
              const hiddenDate = item.metadata?.hiddenAt ? new Date(item.metadata.hiddenAt) : null;
              if (item.metadata?.hidden && hiddenDate && hiddenDate < sixMonthsAgo) {
                hasChanges = true;
                console.log(`[AUTO-CLEANUP] Deleting hidden list from ${hiddenDate.toDateString()}`);
                return false; // Remove from array
              }
              return true; // Keep item
            });
            
            // Rule 3: Named lists are never auto-hidden or deleted
            
            return {
              ...category,
              buckets: {
                ...category.buckets,
                completed: updatedCompleted
              }
            };
          }),
        };
      });
      
      if (hasChanges) {
        if (user?.id) {
          saveNotebooks(String(user.id), updated);
        }
      }
      
      return hasChanges ? updated : prev;
    });
  };

  // Run auto-cleanup on mount and when user changes
  useEffect(() => {
    if (user?.id && classNotebooks.length > 0) {
      autoCleanupHistory();
    }
  }, [user?.id]); // Only run when user logs in

  // Save current list as template
  const handleSaveAsTemplate = (groupDate?: string) => {
    const templateName = window.prompt(groupDate 
      ? `Name this template (from ${new Date(groupDate).toLocaleDateString()}):`
      : 'Name this template:');
    
    if (!templateName?.trim()) return;
    
    let itemsToSave: any[] = [];
    
    if (groupDate) {
      // Save from history
      const history = getGroupedHistory();
      const group = history.find(h => h.date === groupDate);
      if (group) {
        itemsToSave = group.items;
      }
    } else {
      // Save current list
      itemsToSave = allFilteredItems
        .filter(item => item.data.status !== 'completed')
        .map(item => ({
          title: item.title,
          notes: item.data.description || item.data.notes,
          metadata: item.data.metadata
        }));
    }
    
    if (itemsToSave.length === 0) {
      alert('No items to save!');
      return;
    }
    
    const newTemplate = {
      id: `template-${Date.now()}`,
      name: templateName.trim(),
      category: activeTab,
      itemType: activeItemFilter,
      items: itemsToSave,
      createdAt: new Date().toISOString(),
      createdFrom: groupDate || new Date().toISOString().split('T')[0],
      lastUsed: null
    };
    
    const updated = [...listTemplates, newTemplate];
    setListTemplates(updated);
    localStorage.setItem('whipt_list_templates', JSON.stringify(updated));
    
    playSound('success');
    alert(`✅ Template "${templateName}" saved!`);
  };

  // Load template and create items
  const handleLoadTemplate = (templateId: string) => {
    const template = listTemplates.find(t => t.id === templateId);
    if (!template) return;
    
    if (!window.confirm(`Load template "${template.name}"? This will add ${template.items.length} items to your current list.`)) return;
    
    // Add each item from template
    template.items.forEach((item: any) => {
      handleAddNotebookItem({
        title: item.title,
        notes: item.notes,
        metadata: item.metadata
      });
    });
    
    // Update last used
    const updated = listTemplates.map(t => 
      t.id === templateId ? { ...t, lastUsed: new Date().toISOString() } : t
    );
    setListTemplates(updated);
    localStorage.setItem('whipt_list_templates', JSON.stringify(updated));
    
    playSound('success');
    setShowTemplatesView(false); // Go back to current list
  };

  // Delete template
  const handleDeleteTemplate = (templateId: string) => {
    const template = listTemplates.find(t => t.id === templateId);
    if (!template) return;
    
    if (!window.confirm(`Delete template "${template.name}"?`)) return;
    
    const updated = listTemplates.filter(t => t.id !== templateId);
    setListTemplates(updated);
    localStorage.setItem('whipt_list_templates', JSON.stringify(updated));
    
    playSound('click');
  };

  // Check if item is due soon or now
  const getDueDateBadge = (dueDate?: string) => {
    if (!dueDate) return null;
    
    const due = new Date(dueDate);
    const now = new Date();
    const hoursUntilDue = (due.getTime() - now.getTime()) / (1000 * 60 * 60);
    
    if (hoursUntilDue < 0) return null; // Past due
    if (hoursUntilDue < 24) return { text: 'DUE NOW', color: 'bg-red-500/30 text-red-100 border-red-400/60' };
    if (hoursUntilDue < 72) return { text: 'DUE SOON', color: 'bg-yellow-500/30 text-yellow-100 border-yellow-400/60' };
    return null;
  };

  // Parse agenda text into array of strings
  const parseAgenda = (agendaText: string): string[] => {
    if (!agendaText || !agendaText.trim()) return [];
    
    // Split by newlines and filter out empty lines
    return agendaText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
  };

  // Unified Virtual Room handlers
  const handleLaunchRoom = (category: string) => {
    const profile = VIRTUAL_ROOM_PROFILES[category as keyof typeof VIRTUAL_ROOM_PROFILES];
    if (profile) {
      setVirtualRoomContext(profile);
      setVirtualRoomOpen(true);
    }
  };

  const handleCopyLink = async (category: string) => {
    const link = `${window.location.origin}/organizer?room=${category}&class=${activeClassId}&autojoin=1`;
    const roomName = category.charAt(0).toUpperCase() + category.slice(1);
    
    try {
      await navigator.clipboard.writeText(link);
      playSound('success');
      setCopiedLink(link);
      setCopiedRoomName(roomName);
      setShowLinkCopiedModal(true);
    } catch (err) {
      alert(`Failed to copy. Please copy manually:\n\n${link}`);
    }
  };

  const handleScheduleMeeting = (category: string) => {
    playSound('click');
    setScheduleForRoom(category);
    setShowScheduleModal(true);
  };

  const handleCreateAgenda = (category: string) => {
    playSound('click');
    setAgendaForRoom(category);
    setShowAgendaModal(true);
  };

  const handleAddToCalendar = (category: string) => {
    playSound('success');
    const roomName = category.charAt(0).toUpperCase() + category.slice(1);
    const roomUrl = `${window.location.origin}/organizer?room=${category}&class=${activeClassId}`;
    
    // Create .ics calendar file
    const now = new Date();
    const startTime = new Date(now.getTime() + 24 * 60 * 60 * 1000); // Tomorrow
    startTime.setHours(14, 0, 0, 0); // 2 PM
    const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // 1 hour later
    
    const formatDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//WHIPT//Virtual Room//EN',
      'BEGIN:VEVENT',
      `UID:${Date.now()}@whipt.app`,
      `DTSTAMP:${formatDate(now)}`,
      `DTSTART:${formatDate(startTime)}`,
      `DTEND:${formatDate(endTime)}`,
      `SUMMARY:${roomName} Session`,
      `DESCRIPTION:Join the ${roomName} virtual meeting.\\nRoom Link: ${roomUrl}`,
      `LOCATION:${roomUrl}`,
      'STATUS:CONFIRMED',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
    
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${roomName.replace(/\s+/g, '-')}-session.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    alert(`📅 Calendar event created for ${roomName}!\n\nThe .ics file has been downloaded. Open it to add to your calendar app.`);
  };

  // Open event form for new event (optionally with pre-filled date)
  const handleOpenEventForm = (date?: Date) => {
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      setEventFormData(prev => ({
        ...prev,
        date: `${year}-${month}-${day}`,
        time: '09:00'
      }));
    }
    setEditingEvent(null);
    setShowEventForm(true);
    playSound('click');
  };

  // Open event form for editing existing event
  const handleEditEvent = (event: CalendarEvent) => {
    setEditingEvent(event);
    setEventFormData({
      title: event.title,
      type: (event.category || 'meeting') as any,
      date: event.due_date.split(' ')[0] || event.due_date,
      time: event.due_date.includes(' ') ? event.due_date.split(' ')[1] : '',
      duration: 60,
      location: event.notes?.split('Location:')[1]?.split('\n')[0]?.trim() || '',
      attendees: event.notes?.split('Attendees:')[1]?.split('\n')[0]?.trim() || '',
      notes: event.description || '',
      reminder: '15min',
      category: event.category || 'personal'
    });
    setShowEventForm(true);
    playSound('click');
  };

  // Save calendar event (create or update)
  const handleSaveEvent = async () => {
    if (!eventFormData.title.trim() || !eventFormData.date) {
      alert('Please fill in title and date');
      return;
    }

    const dueDate = eventFormData.time 
      ? `${eventFormData.date} ${eventFormData.time}`
      : eventFormData.date;

    const notes = [
      eventFormData.notes,
      eventFormData.location && `Location: ${eventFormData.location}`,
      eventFormData.attendees && `Attendees: ${eventFormData.attendees}`,
      `Reminder: ${eventFormData.reminder}`
    ].filter(Boolean).join('\n');

    if (editingEvent) {
      // Update existing event
      try {
        await updateCalendarEvent(editingEvent.id, {
          title: eventFormData.title,
          due_date: dueDate,
          description: eventFormData.notes,
          category: eventFormData.type,
          notes: notes
        });
        
        // Reload events
        await loadCalendarEvents();
        
        playSound('success');
        setShowEventForm(false);
        setEditingEvent(null);
      } catch (error) {
        console.error('Failed to update event:', error);
        alert('Failed to update event. Please try again.');
      }
    } else {
      // Create new event
      try {
        await createCalendarEvent({
          user_id: String(user?.id),
          title: eventFormData.title,
          date: dueDate,
          description: eventFormData.notes,
          category: eventFormData.type,
          notes: notes,
          status: 'pending'
        });
        
        await loadCalendarEvents();
        
        playSound('success');
        setShowEventForm(false);
        
        // Reset form
        setEventFormData({
          title: '',
          type: 'meeting',
          date: '',
          time: '',
          duration: 60,
          location: '',
          attendees: '',
          notes: '',
          reminder: '15min',
          category: 'personal'
        });
      } catch (error) {
        console.error('Failed to create event:', error);
        alert('Failed to create event. Please try again.');
      }
    }
  };

  // Delete calendar event
  const handleDeleteEvent = async (eventId: string) => {
    if (!window.confirm('Delete this event?')) return;

    try {
      await deleteCalendarEvent(eventId);

      await loadCalendarEvents();
      
      playSound('click');
      setShowCalendarPopup(false);
    } catch (error) {
      console.error('Failed to delete event:', error);
      alert('Failed to delete event. Please try again.');
    }
  };

  // Handle file upload
  const handleFileUpload = async (files: FileList) => {
    const activeClass = classNotebooks.find(nb => nb.id === activeClassId);
    if (!activeClass || !uploadTabSelection) {
      alert('Please select a tab first');
      return;
    }

    setUploadingFiles(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name} is too large. Max 10MB per file.`);
        continue;
      }

      try {
        // Read file as base64
        const reader = new FileReader();
        await new Promise((resolve, reject) => {
          reader.onload = async () => {
            try {
              const base64Content = reader.result as string;
              
              // Create document in database
              await createDocument({
                title: file.name,
                content: base64Content,
                notebookId: activeClass.id,
                notebookTabId: uploadTabSelection,
                userId: user?.id || '',
              });
              
              resolve(null);
            } catch (error) {
              reject(error);
            }
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      } catch (error) {
        console.error('Error uploading file:', error);
        alert(`Failed to upload ${file.name}`);
      }
    }

    setUploadingFiles(false);
    await reloadDocuments();
    playSound('success');
    alert(`Successfully uploaded ${files.length} file(s)!`);
    setShowUploadPanel(false);
    setUploadTabSelection('');
  };

  // Handle add new tab
  const handleAddNotebookTab = () => {
    playSound('click');
    setShowTabTemplateModal(true);
  };

  const handleTemplateSelected = (template: any, customLabel?: string) => {
    const activeClass = classNotebooks.find(nb => nb.id === activeClassId);
    if (!activeClass) return;
    
    const label = customLabel || template.defaultLabel || template.name;
    const newCategory = {
      id: `tab-${Date.now()}`,
      label,
      icon: template.icon,
      accent: template.accent,
      buckets: { upcoming: [], completed: [] },
    };
    
    setClassNotebooks((prev) =>
      prev.map((notebook) =>
        notebook.id === activeClass.id
          ? { ...notebook, categories: [...notebook.categories, newCategory] }
          : notebook
      )
    );
    setActiveCategoryId(newCategory.id);
    setShowTabTemplateModal(false);
    playSound('success');
  };

  // Open calendar popup with items for a specific date
  const handleOpenCalendarPopup = (date?: Date) => {
    playSound('notification');
    
    const targetDate = date || new Date();
    setSelectedDate(targetDate);
    
    // Get date in YYYY-MM-DD format
    const dateStr = targetDate.toISOString().split('T')[0];
    
    // Collect all items with this due date from all categories
    const dateItems: any[] = [];
    
    // Add calendar events for this date
    calendarEvents.forEach(event => {
      if (event.due_date.startsWith(dateStr)) {
        dateItems.push({
          ...event,
          title: event.title,
          id: event.id,
          category: event.category || 'event',
          categoryLabel: event.category ? event.category.charAt(0).toUpperCase() + event.category.slice(1) : 'Event',
          course: 'Calendar',
          status: event.status,
          isCalendarEvent: true
        });
      }
    });
    
    classNotebooks.forEach(notebook => {
      notebook.categories?.forEach((cat: any) => {
        // Check upcoming items
        (cat.buckets?.upcoming || []).forEach((item: any) => {
          if (item.dueDate && item.dueDate.startsWith(dateStr)) {
            // For personal organizer categories, use category name as course
            const isPersonalCategory = ['work', 'home', 'travel'].includes(cat.id);
            const courseName = isPersonalCategory ? cat.id.toUpperCase() : notebook.title;
            
            dateItems.push({
              ...item,
              category: cat.id,
              categoryLabel: cat.label,
              course: courseName,
              status: item.status || 'upcoming',
              isCalendarEvent: false
            });
          }
        });
        
        // Check completed items
        (cat.buckets?.completed || []).forEach((item: any) => {
          if (item.dueDate && item.dueDate.startsWith(dateStr)) {
            // For personal organizer categories, use category name as course
            const isPersonalCategory = ['work', 'home', 'travel'].includes(cat.id);
            const courseName = isPersonalCategory ? cat.id.toUpperCase() : notebook.title;
            
            dateItems.push({
              ...item,
              category: cat.id,
              categoryLabel: cat.label,
              course: courseName,
              status: 'completed',
              isCalendarEvent: false
            });
          }
        });
      });
    });
    
    // Sort items by due date/time (chronological order)
    dateItems.sort((a, b) => {
      const dateA = new Date(a.dueDate || a.due_date || dateStr);
      const dateB = new Date(b.dueDate || b.due_date || dateStr);
      return dateA.getTime() - dateB.getTime();
    });
    
    setCalendarPopupItems(dateItems);
    setShowCalendarPopup(true);
  };

  // Get items for a specific date
  const getItemsForDate = (year: number, month: number, day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const items: any[] = [];
    
    // Debug logging for March dates
    if (month === 2 && day >= 1 && day <= 15) {
    }
    
    // Add calendar events for this date
    calendarEvents.forEach(event => {
      if (event.due_date.startsWith(dateStr)) {
        items.push({ ...event, category: event.category || 'event' });
        if (month === 2 && day >= 1 && day <= 15) {
        }
      }
    });
    
    classNotebooks.forEach(notebook => {
      notebook.categories?.forEach((cat: any) => {
        (cat.buckets?.upcoming || []).forEach((item: any) => {
          if (item.dueDate && item.dueDate.startsWith(dateStr)) {
            // For personal organizer categories, use category name as course
            const isPersonalCategory = ['work', 'home', 'travel'].includes(cat.id);
            const courseName = isPersonalCategory ? cat.id.toUpperCase() : notebook.title;
            
            items.push({ 
              ...item, 
              category: cat.id,
              course: courseName  // Add course field: WORK, HOME, TRAVEL, or class name
            });
            if (month === 2 && day >= 1 && day <= 15) {
              console.log(`  ✅ Found notebook item: "${item.title}" (${cat.id})`);
            }
          }
        });
        (cat.buckets?.completed || []).forEach((item: any) => {
          if (item.dueDate && item.dueDate.startsWith(dateStr)) {
            // For personal organizer categories, use category name as course
            const isPersonalCategory = ['work', 'home', 'travel'].includes(cat.id);
            const courseName = isPersonalCategory ? cat.id.toUpperCase() : notebook.title;
            
            items.push({ 
              ...item, 
              category: cat.id, 
              status: 'completed',
              course: courseName  // Add course field: WORK, HOME, TRAVEL, or class name
            });
            if (month === 2 && day >= 1 && day <= 15) {
              console.log(`  ✅ Found completed item: "${item.title}" (${cat.id})`);
            }
          }
        });
      });
    });
    
    if (month === 2 && day >= 1 && day <= 15) {
    }
    
    return items;
  };

  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const now = new Date();
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const today = now.getDate();
    const isCurrentMonth = year === now.getFullYear() && month === now.getMonth();
    
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    
    const days = [];
    
    // Previous month days
    const prevMonthDays = new Date(year, month, 0).getDate();
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      days.push({ day: prevMonthDays - i, isCurrentMonth: false, items: [] });
    }
    
    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const items = getItemsForDate(year, month, day);
      days.push({ 
        day, 
        isCurrentMonth: true, 
        isToday: isCurrentMonth && day === today, 
        items 
      });
    }
    
    // Next month days to fill grid
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      days.push({ day, isCurrentMonth: false, items: [] });
    }
    
    return days.slice(0, 35); // Max 5 weeks for mini calendar
  };

  // Calendar navigation functions
  const goToPreviousMonth = () => {
    setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    playSound('click');
  };

  const goToNextMonth = () => {
    setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    playSound('click');
  };

  const goToToday = () => {
    setCalendarDate(new Date());
    playSound('success');
  };

  const getMonthYearString = () => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[calendarDate.getMonth()]} ${calendarDate.getFullYear()}`;
  };

  // Add item to notebook (for Home/Work/Travel forms)
  const handleAddNotebookItem = async (item?: { title: string; dueDate?: string; notes?: string; metadata?: any }) => {
    if (!activeClassId || !activeTab) {
      return;
    }
    
    // Check if we're editing an existing item
    if (editingItem) {
      // UPDATE existing item
      const updatedNotebooks = classNotebooks.map(notebook => {
        if (notebook.id === activeClassId) {
          return {
            ...notebook,
            categories: notebook.categories.map(cat => {
              if (cat.id === activeTab) {
                return {
                  ...cat,
                  buckets: {
                    upcoming: (cat.buckets?.upcoming || []).map(i => 
                      i.id === editingItem.id 
                        ? { ...i, title: item!.title, dueDate: item!.dueDate, description: item!.notes, notes: item!.notes, metadata: { ...i.metadata, ...item!.metadata } }
                        : i
                    ),
                    completed: (cat.buckets?.completed || []).map(i => 
                      i.id === editingItem.id 
                        ? { ...i, title: item!.title, dueDate: item!.dueDate, description: item!.notes, notes: item!.notes, metadata: { ...i.metadata, ...item!.metadata } }
                        : i
                    )
                  }
                };
              }
              return cat;
            })
          };
        }
        return notebook;
      });
      
      setClassNotebooks(updatedNotebooks);
      if (user) saveNotebooks(user.id, updatedNotebooks);

      if (user?.id) {
        try {
          const sourceEvent = findCalendarEventBySourceItemId(editingItem.id);
          const meetingCategory = item?.metadata?.category || activeTab;
          const isPersonalCategory = ['work', 'home', 'travel'].includes(meetingCategory);
          const courseName = isPersonalCategory
            ? meetingCategory.toUpperCase()
            : (classNotebooks.find(nb => nb.id === activeClassId)?.title || 'Class');
          const syncNotes = JSON.stringify({
            ...(item?.metadata || {}),
            source: 'mobile-organizer',
            sourceType: 'organizer_item',
            sourceItemId: editingItem.id,
            organizer_tab: activeTab,
            isPersonal: isPersonalCategory,
            meetingCategory,
          });

          if (item?.dueDate) {
            if (sourceEvent) {
              await updateCalendarEvent(sourceEvent.id, {
                title: item.title,
                due_date: item.dueDate,
                description: item.notes || '',
                category: meetingCategory,
                course: courseName,
                notes: syncNotes,
                status: 'pending',
              });
            } else {
              await createCalendarEvent({
                user_id: String(user.id),
                title: item.title,
                date: item.dueDate,
                description: item.notes || '',
                category: meetingCategory,
                course: courseName,
                status: 'pending',
                notes: syncNotes,
              });
            }
          } else if (sourceEvent) {
            await deleteCalendarEvent(sourceEvent.id);
          }

          await loadCalendarEvents();
        } catch (error) {
          console.error('[MobileCalendar] Failed to sync edited notebook item:', error);
        }
      }
      
      // Clear editing state
      setEditingItem(null);
      setActiveFormType(null);
      setShowFormSelector(false);
      return;
    }
    
    // If no item passed, use legacy prompt behavior
    if (!item) {
      const title = window.prompt("Item title?");
      if (!title) return;
      
      const description = window.prompt("Description/notes (optional):");
      const dueDateInput = window.prompt("Due date (YYYY-MM-DD HH:MM)");
      
      item = {
        title,
        dueDate: dueDateInput || undefined,
        notes: description || undefined
      };
    }
    
    const newItemId = `${activeClassId}-${activeTab}-${Date.now()}`;
    
    setClassNotebooks((prev) => {
      const updated = prev.map((notebook) => {
        if (notebook.id !== activeClassId) return notebook;
        
        // DEBUG: Check what categories exist
        console.log('[MOBILE ADD ITEM] Notebook categories:', notebook.categories.map((c: any) => c.id));
        
        const newItem = {
          id: newItemId,
          title: item!.title,
          dueDate: item!.dueDate,
          status: 'upcoming',
          description: item!.notes || undefined,
          metadata: item!.metadata || undefined,
        };
        
        // Check if the category exists, if not create it
        let categoryExists = notebook.categories.some((c: any) => c.id === activeTab);
        if (!categoryExists) {
          notebook.categories.push({
            id: activeTab,
            label: activeTab.charAt(0).toUpperCase() + activeTab.slice(1),
            buckets: { upcoming: [], completed: [] }
          });
        }
        
        return {
          ...notebook,
          categories: notebook.categories.map((category: any) => {
            if (category.id !== activeTab) return category;
            return {
              ...category,
              buckets: {
                ...category.buckets,
                upcoming: [...(category.buckets.upcoming || []), newItem],
              },
            };
          }),
        };
      });
      
      // Save immediately with the updated state
      if (user?.id) {
        setTimeout(() => saveNotebooks(String(user.id), updated), 100);
      }
      
      playSound('success');
      
      // Close any open overlays/forms after successful creation
      console.log('🔄 Closing overlays after item creation...');
      setShowMobileOverlay(false);
      setShowFormSelector(false);
      setActiveFormType(null);
      
      // Force a small delay to ensure UI updates
      setTimeout(() => {
        console.log('✅ Overlays should be closed now');
      }, 100);
      
      return updated;
    });

    if (item.dueDate && user?.id) {
      try {
        const meetingCategory = item.metadata?.category || activeTab;
        const isPersonalCategory = ['work', 'home', 'travel'].includes(meetingCategory);
        const courseName = isPersonalCategory
          ? meetingCategory.toUpperCase()
          : (classNotebooks.find(nb => nb.id === activeClassId)?.title || 'Class');

        await createCalendarEvent({
          user_id: String(user.id),
          title: item.title,
          date: item.dueDate,
          description: item.notes || undefined,
          category: meetingCategory,
          course: courseName,
          status: 'pending',
          notes: JSON.stringify({
            ...item.metadata,
            source: 'mobile-organizer',
            sourceType: 'organizer_item',
            sourceItemId: newItemId,
            organizer_tab: activeTab,
            isPersonal: isPersonalCategory,
            meetingCategory,
          }),
        });

        await loadCalendarEvents();
      } catch (error) {
        console.error('[MobileCalendar] Failed to create synced calendar event:', error);
      }
    }
  };

  // Delete item (move to trash)
  const handleDeleteItem = async (itemId: string, categoryId: string) => {
    if (!window.confirm('Delete this item? You can recover it from the Trash.')) {
      return;
    }
    
    setClassNotebooks((prev) => {
      let deletedItem: any = null;
      
      const updated = prev.map((notebook) => {
        return {
          ...notebook,
          categories: notebook.categories.map((category: any) => {
            if (category.id === categoryId) {
              // Find and remove the item
              const upcomingItem = (category.buckets.upcoming || []).find((item: any) => item.id === itemId);
              const completedItem = (category.buckets.completed || []).find((item: any) => item.id === itemId);
              
              if (upcomingItem || completedItem) {
                deletedItem = { 
                  ...(upcomingItem || completedItem), 
                  deletedAt: new Date().toISOString(),
                  deletedFrom: { notebookId: notebook.id, categoryId }
                };
              }
              
              return {
                ...category,
                buckets: {
                  upcoming: category.buckets.upcoming.filter((item: any) => item.id !== itemId),
                  completed: category.buckets.completed.filter((item: any) => item.id !== itemId),
                },
              };
            }
            return category;
          }),
        };
      });
      
      // Add to trash
      if (deletedItem) {
        setDeletedItems(prev => [...prev, deletedItem]);
      }
      
      // Save to database
      if (user?.id) {
        saveNotebooks(String(user.id), updated);
      }
      return updated;
    });

    try {
      const sourceEvent = findCalendarEventBySourceItemId(itemId);
      if (sourceEvent) {
        await deleteCalendarEvent(sourceEvent.id);
        await loadCalendarEvents();
      }
    } catch (error) {
      console.error('[MobileCalendar] Failed to delete synced calendar event:', error);
    }
    
    setSelectedItemMenu(null);
    playSound('click');
  };

  // Restore item from trash
  const handleRestoreItem = (item: any) => {
    setClassNotebooks((prev) => {
      const updated = prev.map((notebook) => {
        if (notebook.id === item.deletedFrom.notebookId) {
          return {
            ...notebook,
            categories: notebook.categories.map((category: any) => {
              if (category.id === item.deletedFrom.categoryId) {
                const { deletedAt, deletedFrom, ...cleanItem } = item;
                const status = cleanItem.status || 'upcoming';
                return {
                  ...category,
                  buckets: {
                    ...category.buckets,
                    [status]: [...(category.buckets[status] || []), cleanItem],
                  },
                };
              }
              return category;
            }),
          };
        }
        return notebook;
      });
      
      // Remove from trash
      setDeletedItems(prev => prev.filter(i => i.id !== item.id));
      
      // Save to database
      if (user?.id) {
        saveNotebooks(String(user.id), updated);
      }
      return updated;
    });
    
    playSound('success');
  };

  // Move item to different class
  const handleMoveToClass = (itemId: string, currentCategoryId: string, targetNotebookId: string) => {
    setClassNotebooks((prev) => {
      let itemToMove: any = null;
      
      // Remove from current class
      const updated = prev.map((notebook) => {
        const category = notebook.categories?.find((cat: any) => cat.id === currentCategoryId);
        if (category?.buckets) {
          const foundItem = [...(category.buckets.upcoming || []), ...(category.buckets.completed || [])]
            .find((item: any) => item.id === itemId);
          if (foundItem) {
            itemToMove = foundItem;
          }
        }
        
        return {
          ...notebook,
          categories: notebook.categories.map((category: any) => {
            if (category.id === currentCategoryId) {
              return {
                ...category,
                buckets: {
                  upcoming: (category.buckets.upcoming || []).filter((item: any) => item.id !== itemId),
                  completed: (category.buckets.completed || []).filter((item: any) => item.id !== itemId),
                },
              };
            }
            return category;
          }),
        };
      });
      
      // Add to target class
      if (itemToMove) {
        const finalUpdated = updated.map((notebook) => {
          if (notebook.id === targetNotebookId) {
            return {
              ...notebook,
              categories: notebook.categories.map((category: any) => {
                if (category.id === currentCategoryId) {
                  const status = itemToMove.status || 'upcoming';
                  return {
                    ...category,
                    buckets: {
                      ...category.buckets,
                      [status]: [...(category.buckets[status] || []), itemToMove],
                    },
                  };
                }
                return category;
              }),
            };
          }
          return notebook;
        });
        
        // Save to database
        if (user?.id) {
          saveNotebooks(String(user.id), finalUpdated);
        }
        return finalUpdated;
      }
      
      return updated;
    });
    
    setShowClassSelector(null);
    setSelectedItemMenu(null);
    playSound('success');
  };

  // Add new class
  const handleAddClass = () => {
    if (!newClassName.trim()) return;
    
    if (editingClassId) {
      // Edit existing class
      setClassNotebooks(prev => {
        const updated = prev.map(nb => 
          nb.id === editingClassId
            ? { ...nb, title: newClassName.trim(), professor: newProfessorName.trim() || nb.professor, teacherPhoto: newTeacherPhoto || nb.teacherPhoto }
            : nb
        );
        
        // Save to database
        if (user?.id) {
          saveNotebooks(String(user.id), updated);
        }
        return updated;
      });
      
      setEditingClassId(null);
    } else {
      // Add new class - Include semester and year from activeSemester
      const [semesterName, yearStr] = activeSemester.split(' ');
      const newNotebook = {
        id: `notebook-${Date.now()}`,
        title: newClassName.trim(),
        professor: newProfessorName.trim() || undefined,
        teacherPhoto: newTeacherPhoto || undefined,
        semester: semesterName || 'Spring',
        year: yearStr ? parseInt(yearStr) : new Date().getFullYear(),
        categories: [
          { id: 'discussion', label: 'Discussion', buckets: { upcoming: [], completed: [] } },
          { id: 'assignment', label: 'Assignment', buckets: { upcoming: [], completed: [] } },
          { id: 'experiential', label: 'Experiential', buckets: { upcoming: [], completed: [] } },
          { id: 'journal', label: 'Journal', buckets: { upcoming: [], completed: [] } },
        ],
        created_at: new Date().toISOString(),
      };
      
      setClassNotebooks(prev => {
        const updated = [...prev, newNotebook];
        
        // Save to database
        if (user?.id) {
          saveNotebooks(String(user.id), updated);
        }
        return updated;
      });
    }
    
    setNewClassName('');
    setNewProfessorName('');
    setNewTeacherPhoto('');
    setShowAddClassModal(false);
    playSound('success');
  };

  // Open edit modal for existing class
  const handleEditClass = (classId: string) => {
    const classToEdit = classNotebooks.find(nb => nb.id === classId);
    if (!classToEdit) return;
    
    setEditingClassId(classId);
    setNewClassName(classToEdit.title || '');
    setNewProfessorName(classToEdit.professor || '');
    setNewTeacherPhoto(classToEdit.teacherPhoto || '');
    setShowAddClassModal(true);
    playSound('click');
  };

  // Handle teacher photo upload
  const handleTeacherPhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Photo must be less than 2MB');
      return;
    }
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }
    
    // Read and resize image
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Create canvas to resize image
        const canvas = document.createElement('canvas');
        const maxSize = 200; // Max width/height
        let width = img.width;
        let height = img.height;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        
        // Convert to base64
        const resizedBase64 = canvas.toDataURL('image/jpeg', 0.8);
        setNewTeacherPhoto(resizedBase64);
        playSound('success');
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  // Get uploaded files for current category
  const getUploadedFiles = () => {
    if (!activeTab || !activeClassId) return [];
    
    // Get documents that match current notebook and tab
    const uploadedFiles = documents.filter((doc: any) => 
      doc.notebookId === activeClassId && 
      doc.notebookTabId === activeTab
    );
    
    console.log('📎 getUploadedFiles:', {
      activeTab,
      activeClassId,
      totalDocuments: documents.length,
      matchingFiles: uploadedFiles.length,
      files: uploadedFiles.map(f => ({ id: f.id, title: f.title, notebookId: f.notebookId, tabId: f.notebookTabId }))
    });
    
    return uploadedFiles;
  };

  // Delete uploaded file (document)
  const handleDeleteFile = async (fileId: number) => {
    if (!confirm('Delete this file?')) return;

    try {
      await deleteDocument(fileId);
      await reloadDocuments();
      playSound('success');
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Failed to delete file');
    }
  };

  // Get color scheme for active tab
  const getTabColors = () => {
    const colorSchemes: Record<string, { bg: string; border: string; text: string; accent: string }> = {
      'discussion': { 
        bg: 'from-blue-900/90 to-blue-950/95', 
        border: 'border-blue-400/40',
        text: 'text-blue-100',
        accent: 'bg-blue-500/30 text-blue-200'
      },
      'assignment': { 
        bg: 'from-amber-900/90 to-amber-950/95', 
        border: 'border-amber-400/40',
        text: 'text-amber-100',
        accent: 'bg-amber-500/30 text-amber-200'
      },
      'experiential': { 
        bg: 'from-emerald-900/90 to-emerald-950/95', 
        border: 'border-emerald-400/40',
        text: 'text-emerald-100',
        accent: 'bg-emerald-500/30 text-emerald-200'
      },
      'journal': { 
        bg: 'from-purple-900/90 to-purple-950/95', 
        border: 'border-purple-400/40',
        text: 'text-purple-100',
        accent: 'bg-purple-500/30 text-purple-200'
      },
      'home': { 
        bg: 'from-rose-900/90 to-rose-950/95', 
        border: 'border-rose-400/40',
        text: 'text-rose-100',
        accent: 'bg-rose-500/30 text-rose-200'
      },
      'work': { 
        bg: 'from-indigo-900/90 to-indigo-950/95', 
        border: 'border-indigo-400/40',
        text: 'text-indigo-100',
        accent: 'bg-indigo-500/30 text-indigo-200'
      },
      'travel': { 
        bg: 'from-sky-900/90 to-sky-950/95', 
        border: 'border-sky-400/40',
        text: 'text-sky-100',
        accent: 'bg-sky-500/30 text-sky-200'
      }
    };
    
    return colorSchemes[activeTab] || colorSchemes.discussion;
  };

  const tabColors = getTabColors();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#040109] via-[#10041d] to-[#1a0930] text-white overflow-hidden fixed inset-0">
      {/* Notification Panel */}
      {user?.id && <NotificationPanel userId={String(user.id)} />}
      
      <style>{`
        body {
          overscroll-behavior: none;
          overflow: hidden;
          position: fixed;
          width: 100%;
          height: 100%;
        }
        * {
          overscroll-behavior: none;
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 8px rgba(168, 85, 247, 0.2); transform: scale(1); }
          50% { box-shadow: 0 0 15px rgba(168, 85, 247, 0.4), 0 0 25px rgba(168, 85, 247, 0.3); transform: scale(1.01); }
        }
        @keyframes flash-blue {
          0%, 100% { box-shadow: 0 0 0 rgba(37,99,235,0); }
          50% { box-shadow: 0 0 30px rgba(37,99,235,0.8), 0 0 60px rgba(37,99,235,0.5); }
        }
        @keyframes flash-amber {
          0%, 100% { box-shadow: 0 0 0 rgba(251,191,36,0); }
          50% { box-shadow: 0 0 30px rgba(251,191,36,0.8), 0 0 60px rgba(251,191,36,0.5); }
        }
        @keyframes flash-emerald {
          0%, 100% { box-shadow: 0 0 0 rgba(16,185,129,0); }
          50% { box-shadow: 0 0 30px rgba(16,185,129,0.8), 0 0 60px rgba(16,185,129,0.5); }
        }
        @keyframes flash-cyan {
          0%, 100% { box-shadow: 0 0 0 rgba(6,182,212,0); }
          50% { box-shadow: 0 0 30px rgba(6,182,212,0.8), 0 0 60px rgba(6,182,212,0.5); }
        }
        @keyframes flash-pink {
          0%, 100% { box-shadow: 0 0 0 rgba(236,72,153,0); }
          50% { box-shadow: 0 0 30px rgba(236,72,153,0.8), 0 0 60px rgba(236,72,153,0.5); }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes pulse-alert {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
        }
        .pulse-glow { animation: pulse-glow 3s ease-in-out infinite; }
        .flash-blue { animation: flash-blue 2s ease-in-out infinite; }
        .flash-amber { animation: flash-amber 2s ease-in-out infinite; }
        .flash-emerald { animation: flash-emerald 2s ease-in-out infinite; }
        .flash-cyan { animation: flash-cyan 2s ease-in-out infinite; }
        .flash-pink { animation: flash-pink 2s ease-in-out infinite; }
        .hover-flash-blue:hover { animation: flash-blue 2s ease-in-out infinite !important; }
        .hover-flash-amber:hover { animation: flash-amber 2s ease-in-out infinite !important; }
        .hover-flash-emerald:hover { animation: flash-emerald 2s ease-in-out infinite !important; }
        .hover-flash-cyan:hover { animation: flash-cyan 2s ease-in-out infinite !important; }
        .hover-flash-pink:hover { animation: flash-pink 2s ease-in-out infinite !important; }
        .notebook-tab {
          clip-path: polygon(10% 0%, 90% 0%, 100% 100%, 0% 100%);
        }
        .notebook-tab.active {
          clip-path: polygon(5% 0%, 95% 0%, 100% 100%, 0% 100%);
        }
        .pulse-glow { animation: pulse-glow 3s ease-in-out infinite; }
        .flash-blue { animation: flash-blue 2s ease-in-out infinite; }
        .flash-amber { animation: flash-amber 2s ease-in-out infinite; }
        .sidebar { transform: translateX(-100%); transition: transform 300ms ease-out; }
        .sidebar.open { transform: translateX(0); }
        .overlay { opacity: 0; pointer-events: none; transition: opacity 300ms ease-out; }
        .overlay.visible { opacity: 1; pointer-events: all; }
        .tab-overlay { transform: translateY(100%); opacity: 0; transition: all 300ms ease-out; }
        .tab-overlay.open { transform: translateY(0); opacity: 1; }
      `}</style>

      {/* Fixed Glass Header */}
      <div className="fixed top-0 left-0 right-0 px-4 py-3 border-b border-white/10 shadow-lg z-50 bg-white/5 backdrop-blur-2xl">
        <div className="flex items-center justify-between">
          {/* Left: Logo with Hamburger */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-amber-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
              </svg>
            </button>
            <div className="flex flex-col">
              <h1 className="text-2xl font-black bg-gradient-to-r from-amber-300 via-amber-200 to-amber-400 bg-clip-text text-transparent">
                WHIPT
              </h1>
              <p className="text-[10px] text-white">Where'd I Put That?</p>
            </div>
          </div>
          
          {/* Right: Icons */}
          <div className="flex items-center gap-2">
            <button onClick={() => { playSound('click'); navigate('/organizer/messages'); }} className="relative p-1.5 hover:bg-white/10 rounded-lg" title="Messages">
              <svg className="w-4 h-4 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
              </svg>
              {unreadMessageCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center animate-pulse">
                  {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                </span>
              )}
            </button>
            <button 
              onClick={toggleOnlineStatus}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              title={isOnline ? "You're online (click to go offline)" : "You're offline (click to go online)"}
            >
              <div className={`w-2 h-2 rounded-full transition-colors ${
                isOnline ? 'bg-green-400 animate-pulse' : 'bg-gray-500'
              }`}></div>
            </button>
            <button onClick={toggleSound} className="relative p-1.5 hover:bg-white/10 rounded-lg" title={soundEnabled ? "Sound On" : "Sound Off"}>
              <svg className="w-4 h-4 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {soundEnabled ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                ) : (
                  <>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    <line x1="3" y1="3" x2="21" y2="21" strokeLinecap="round" strokeWidth="2" className="text-red-400" />
                  </>
                )}
              </svg>
            </button>
            <button onClick={() => { playSound('notification'); navigate('/organizer/settings'); }} className="p-1.5 hover:bg-white/10 rounded-lg" title="Notifications">
              <svg className="w-4 h-4 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
              </svg>
            </button>
            {user?.avatar ? (
              <img 
                src={user.avatar} 
                alt={user.name}
                onClick={() => navigate('/organizer/settings')}
                className="w-7 h-7 rounded-full object-cover cursor-pointer hover:ring-2 hover:ring-amber-400 transition-all"
              />
            ) : (
              <div 
                onClick={() => navigate('/organizer/settings')}
                className="w-7 h-7 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold cursor-pointer hover:ring-2 hover:ring-amber-400 transition-all"
              >
                {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Overlay/Backdrop for Sidebar */}
      <div
        className={`overlay fixed inset-0 bg-black/60 backdrop-blur-sm z-40 ${sidebarOpen ? 'visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
      ></div>

      {/* Slide-out Sidebar */}
      <div 
        className="fixed top-0 left-0 bottom-0 w-4/5 max-w-[320px] bg-slate-900/95 backdrop-blur-xl shadow-2xl z-50 overflow-y-auto transition-transform duration-300 ease-out"
        style={{
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)'
        }}
      >
        <div className="p-4 space-y-4">
          {/* Semester Selector - Enhanced */}
          <div className="bg-gradient-to-br from-purple-900/60 via-purple-800/40 to-indigo-900/60 border-2 border-purple-400/50 rounded-2xl p-4 space-y-3 shadow-xl shadow-purple-900/30">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                  <span className="text-lg">🗓️</span>
                </div>
                <h3 className="text-base font-bold text-white">Academic Period</h3>
              </div>
              <button 
                onClick={() => { playSound('click'); setShowSemesterModal(true); }}
                className="text-xs text-emerald-200 bg-emerald-600/20 border border-emerald-400/40 px-3 py-1.5 rounded-full hover:bg-emerald-600/30 font-semibold transition-all"
              >
                ➕ New
              </button>
            </div>
            <select 
              className="w-full px-4 py-3 rounded-xl bg-black/50 border-2 border-purple-400/40 text-white text-base font-bold shadow-inner hover:border-purple-400/60 transition-all focus:outline-none focus:border-purple-300 focus:ring-2 focus:ring-purple-500/50"
              value={activeSemester}
              onChange={(e) => {
                const newSemester = e.target.value;
                setActiveSemester(newSemester);
                localStorage.setItem('whipt_active_semester', newSemester);
                playSound('click');
              }}
            >
              {(() => {
                // Generate semester options from notebooks AND include active semester
                const semesters = new Set<string>();
                
                // Always add the active semester first
                semesters.add(activeSemester);
                
                // Add semesters from existing classes
                classNotebooks.forEach(nb => {
                  const semester = nb.semester || 'Spring';
                  const year = nb.year || new Date().getFullYear();
                  semesters.add(`${semester} ${year}`);
                });
                
                // Add current semester if somehow still empty
                if (semesters.size === 0) {
                  semesters.add(`Spring ${new Date().getFullYear()}`);
                }
                
                return Array.from(semesters).sort().reverse().map(sem => (
                  <option key={sem} value={sem}>{sem}</option>
                ));
              })()}
            </select>
            
            {/* Clear Old Calendar Events Button */}
            <button
              onClick={async () => {
                if (confirm(`Clear all calendar events from previous semesters?\n\nThis will delete calendar events that don't match ${activeSemester}. This action cannot be undone.`)) {
                  try {
                    // Delete all calendar events for this user
                    // (In a real implementation, you'd filter by date ranges for old semesters)
                    if (user?.id) {
                      const db = (await import('@/database/client')).default;
                      await db.execute({
                        sql: `DELETE FROM calendar_events WHERE user_id = ?`,
                        args: [user.id]
                      });
                      playSound('success');
                      alert(`Calendar cleared! You can now upload your ${activeSemester} calendar.`);
                      setSidebarOpen(false);
                    }
                  } catch (error) {
                    console.error('Error clearing calendar:', error);
                    alert('Failed to clear calendar. Please try again.');
                  }
                }
              }}
              className="w-full px-4 py-2 rounded-lg bg-gradient-to-r from-red-600/20 to-rose-600/20 hover:from-red-600/40 hover:to-rose-600/40 border border-red-400/30 text-red-200 text-sm font-semibold transition-all flex items-center justify-center gap-2"
            >
              <span>🗑️</span>
              <span>Clear Old Calendar</span>
            </button>
          </div>

          {/* Class Switcher - Enhanced */}
          <div className="bg-gradient-to-br from-indigo-900/60 via-blue-800/40 to-slate-900/60 border-2 border-indigo-400/50 rounded-2xl p-4 space-y-3 shadow-xl shadow-indigo-900/30">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center">
                  <span className="text-lg">📚</span>
                </div>
                <h3 className="text-base font-bold text-white">Your Classes</h3>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => { playSound('click'); setShowArchivedClasses(!showArchivedClasses); }}
                  className={`text-xs px-2 py-1.5 rounded-full font-semibold transition-all ${
                    showArchivedClasses 
                      ? 'text-amber-200 bg-amber-600/30 border border-amber-400/50' 
                      : 'text-slate-300 bg-slate-600/20 border border-slate-400/30'
                  }`}
                  title={showArchivedClasses ? "Hide Archived" : "Show Archived"}
                >
                  {showArchivedClasses ? '🗃️' : '📚'}
                </button>
                <button 
                  onClick={() => { 
                    playSound('click'); 
                    setShowAddClassModal(true);
                    setSidebarOpen(false); // Close sidebar when opening modal
                  }}
                  className="text-xs text-emerald-200 bg-emerald-600/20 border border-emerald-400/40 px-3 py-1.5 rounded-full hover:bg-emerald-600/30 font-semibold transition-all"
                >
                  ➕ New
                </button>
              </div>
            </div>
            <select 
              className="w-full px-4 py-3 rounded-xl bg-black/50 border-2 border-indigo-400/40 text-white text-base font-semibold shadow-inner hover:border-indigo-400/60 transition-all focus:outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-500/50"
              value={activeClassId}
              onChange={(e) => {
                setActiveClassId(e.target.value);
                playSound('click');
              }}
            >
              {(() => {
                // Filter classes by active semester and archive status
                const filteredClasses = classNotebooks.filter(nb => {
                  const nbSemester = nb.semester || 'Spring';
                  const nbYear = nb.year || new Date().getFullYear();
                  const matchesSemester = `${nbSemester} ${nbYear}` === activeSemester;
                  const matchesArchive = showArchivedClasses || !nb.archived;
                  return matchesSemester && matchesArchive;
                });
                
                return filteredClasses.length > 0 ? (
                  filteredClasses.map((notebook, index) => (
                    <option key={notebook.id} value={notebook.id}>
                      {notebook.title || `Class ${index + 1}`}{notebook.archived ? ' (Archived)' : ''}
                    </option>
                  ))
                ) : (
                  <option>No classes in {activeSemester}</option>
                );
              })()}
            </select>
            
            {/* Bulk Archive Button - Archive all classes in current semester */}
            {(() => {
              const unarchivedClasses = classNotebooks.filter(nb => {
                const nbSemester = nb.semester || 'Spring';
                const nbYear = nb.year || new Date().getFullYear();
                return `${nbSemester} ${nbYear}` === activeSemester && !nb.archived;
              });
              
              return unarchivedClasses.length > 0 && (
                <button
                  onClick={() => {
                    if (confirm(`Archive all ${unarchivedClasses.length} classes in ${activeSemester}? This will clear them from view but preserve your work.`)) {
                      const updated = classNotebooks.map(nb => {
                        const nbSemester = nb.semester || 'Spring';
                        const nbYear = nb.year || new Date().getFullYear();
                        if (`${nbSemester} ${nbYear}` === activeSemester && !nb.archived) {
                          return { ...nb, archived: true };
                        }
                        return nb;
                      });
                      setClassNotebooks(updated);
                      if (user?.id) {
                        saveNotebooks(String(user.id), updated);
                      }
                      playSound('success');
                      setSidebarOpen(false);
                    }
                  }}
                  className="w-full px-4 py-2 rounded-lg bg-gradient-to-r from-amber-600/20 to-orange-600/20 hover:from-amber-600/40 hover:to-orange-600/40 border border-amber-400/30 text-amber-200 text-sm font-semibold transition-all flex items-center justify-center gap-2"
                >
                  <span>🗃️</span>
                  <span>Archive All {unarchivedClasses.length} Classes</span>
                </button>
              );
            })()}
          </div>

          {/* Active Class Info Panel - Enhanced - Only show if class is NOT archived */}
          {activeClassId && classNotebooks.find(nb => nb.id === activeClassId) && (() => {
            const activeClass = classNotebooks.find(nb => nb.id === activeClassId);
            
            // Hide panel if class is archived
            if (activeClass?.archived) return null;
            
            const professor = activeClass?.professor || activeClass?.instructor || activeClass?.course || activeClass?.title;
            
            return (
              <div className="bg-gradient-to-br from-purple-600/30 via-indigo-600/20 to-blue-600/30 border-2 border-purple-400/40 rounded-2xl p-4 shadow-xl shadow-purple-900/30">
                <div className="text-center">
                  <div className="text-xl font-extrabold text-white drop-shadow-lg">
                    {activeClass?.title}
                  </div>
                  <div className="text-xs text-purple-200 mt-1 font-semibold">Active Class</div>
                </div>
                
                {/* Class Details */}
                <div className="mt-4 pt-4 border-t border-white/20 space-y-3">
                  <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3 backdrop-blur-sm">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-lg">👨‍🏫</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-purple-200 font-semibold">Professor</div>
                      <div className="text-sm text-white font-bold truncate">{professor || 'Not assigned'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3 backdrop-blur-sm">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-lg">📅</span>
                    </div>
                    <div className="flex-1">
                      <div className="text-xs text-indigo-200 font-semibold">Semester</div>
                      <div className="text-sm text-white font-bold">
                        {activeClass?.semester || `Spring ${new Date().getFullYear()}`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Action Buttons */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-amber-300 uppercase tracking-wide">Quick Actions</p>
            
            <button 
              onClick={() => { playSound('click'); setShowTrash(true); setSidebarOpen(false); }} 
              className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-red-600/20 to-orange-900/20 backdrop-blur-xl hover:from-red-600/40 hover:to-orange-900/40 border border-red-400/30 rounded-lg transition-all text-left"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
              <div className="flex-1">
                <span className="text-sm font-medium text-red-100">Trash</span>
                {deletedItems.length > 0 && (
                  <span className="ml-2 text-xs bg-red-500/30 px-2 py-0.5 rounded-full">{deletedItems.length}</span>
                )}
              </div>
            </button>
            
            <button onClick={() => { playSound('click'); setShowSnackPanel(true); setSidebarOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-pink-600/20 to-rose-900/20 backdrop-blur-xl hover:from-pink-600/40 hover:to-rose-900/40 border border-pink-400/30 rounded-lg transition-all text-left hover-flash-pink">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
                <path d="M7 2v20" />
                <path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
              </svg>
              <span className="text-sm font-medium text-pink-100">Snack Dial</span>
            </button>
            
            <button onClick={() => { playSound('click'); setSidebarOpen(false); setShowBudgetAssistant(true); }} className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-green-600/20 to-emerald-600/20 hover:from-green-600/40 hover:to-emerald-600/40 border border-emerald-400/30 rounded-lg transition-all text-left hover-flash-emerald">
              <span className="text-xl">💰</span>
              <span className="text-sm font-medium text-emerald-100">Budget Assistant</span>
            </button>
            
            {/* WHIPT WALLET - Coming Soon */}
            <button disabled className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-amber-500/10 to-yellow-600/10 border border-amber-400/20 rounded-lg transition-all text-left opacity-60 cursor-not-allowed">
              <div className="flex items-center gap-3">
                <span className="text-xl">💳</span>
                <span className="text-sm font-medium text-amber-100">WHIPT WALLET</span>
              </div>
              <span className="text-[10px] bg-amber-500/30 text-amber-100 px-2 py-0.5 rounded-full border border-amber-400/30">Soon</span>
            </button>
            
            {/* Gmail Import - Coming Soon */}
            <button disabled className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-600/10 to-blue-700/10 border border-blue-400/20 rounded-lg transition-all text-left opacity-60 cursor-not-allowed">
              <div className="flex items-center gap-3">
                <span className="text-xl">📧</span>
                <span className="text-sm font-medium text-blue-100">Import from Gmail</span>
              </div>
              <span className="text-[10px] bg-blue-500/30 text-blue-100 px-2 py-0.5 rounded-full border border-blue-400/30">Soon</span>
            </button>
            
            <button onClick={() => { playSound('click'); setSidebarOpen(false); setShowAppsPanel(true); }} className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 hover:from-blue-600/40 hover:to-cyan-600/40 border border-cyan-400/30 rounded-lg transition-all text-left hover-flash-cyan">
              <span className="text-xl">🔗</span>
              <span className="text-sm font-medium text-cyan-100">App Integrations</span>
            </button>
            
            <button onClick={() => { playSound('click'); setSidebarOpen(false); navigate('/organizer/messages'); }} className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-purple-600/20 to-pink-600/20 hover:from-purple-600/40 hover:to-pink-600/40 border border-purple-400/30 rounded-lg transition-all text-left hover-flash-pink">
              <div className="flex items-center gap-3">
                <span className="text-xl">💬</span>
                <span className="text-sm font-medium text-purple-100">Messages</span>
              </div>
              {unreadMessageCount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5 animate-pulse">
                  {unreadMessageCount}
                </span>
              )}
            </button>
            
            <button onClick={() => { playSound('click'); setSidebarOpen(false); setShowSuggestions(true); }} className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-yellow-600/20 to-amber-600/20 hover:from-yellow-600/40 hover:to-amber-600/40 border border-yellow-400/30 rounded-lg transition-all text-left">
              <span className="text-xl">💡</span>
              <span className="text-sm font-medium text-yellow-100">Suggestions</span>
            </button>
            
            {/* Test Notifications Button - WHIPT Styled */}
            <button 
              onClick={async () => { 
                playSound('notification'); 
                setSidebarOpen(false);
                
                // Test PERSISTENT notification (works when phone is locked)
                try {
                  await PushNotificationService.testPushNotification();
                  alert('✅ Test notification sent!\n\nCheck your phone - it should appear even when locked.\n\nIf you don\'t see it:\n1. Make sure you allowed notifications\n2. Check your phone isn\'t on Do Not Disturb\n3. Try locking your phone and waiting 5 seconds');
                } catch (error) {
                  console.error('[Test] Notification error:', error);
                  alert('❌ Notification test failed. Please check browser console for details.');
                }
              }} 
              className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-orange-500/20 to-red-500/20 hover:from-orange-500/40 hover:to-red-500/40 border border-orange-400/40 rounded-lg transition-all text-left hover-flash-orange group"
            >
              <span className="text-xl group-hover:scale-110 transition-transform">🔔</span>
              <div className="flex-1">
                <div className="text-sm font-bold text-orange-100">Test Notifications</div>
                <div className="text-xs text-orange-200/60">Works when phone is locked</div>
              </div>
            </button>
            
            <button onClick={() => { playSound('click'); setSidebarOpen(false); setShowAIPanel(true); }} className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-indigo-600/20 to-blue-600/20 backdrop-blur-xl hover:from-indigo-600/40 hover:to-blue-600/40 border border-indigo-400/30 rounded-lg transition-all text-left">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span className="text-sm font-medium text-indigo-100">AI Assistant</span>
            </button>
            
          </div>

          {/* Settings */}
          <div className="pt-4 border-t border-white/10">
            <button onClick={() => { setSidebarOpen(false); navigate('/organizer/settings'); }} className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-slate-700/50 to-slate-600/50 hover:from-slate-700/70 hover:to-slate-600/70 border border-slate-500/50 rounded-lg transition-all text-left">
              <span className="text-xl">⚙️</span>
              <span className="text-sm font-medium text-slate-100">Settings</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tab Overlay (Blurred Background) */}
      {tabOverlayOpen && (
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-md z-[60] overflow-y-auto"
        onClick={(e) => {
          // Only close if clicking the background, not the content
          if (e.target === e.currentTarget) {
            closeTabOverlay();
          }
        }}
      >
        <div className="min-h-full p-4 pt-20">
          <div 
            className={`bg-gradient-to-br ${tabColors.bg} border-2 ${tabColors.border} rounded-2xl p-6 max-w-md mx-auto`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-xl font-bold ${tabColors.text}`}>
                {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Documents
              </h3>
              <button
                onClick={closeTabOverlay}
                className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
              >
                ✕
              </button>
            </div>
            
            {/* SEARCH BAR - Inside Category */}
            <form 
              className="mb-4 space-y-2" 
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSearchQuery(e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder={searchMode === 'all' ? '🔍 Search all categories...' : `🔍 Search ${activeTab ? activeTab.charAt(0).toUpperCase() + activeTab.slice(1) : 'current'} category...`}
                  className="w-full px-4 py-3 pl-10 pr-24 rounded-xl border-2 border-purple-400/40 bg-purple-950/30 backdrop-blur-xl text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-400 transition-all shadow-lg"
                  autoComplete="off"
                />
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-300/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                
                {/* Search Mode Toggle */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchMode(prev => prev === 'current' ? 'all' : 'current');
                    playSound('click');
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 rounded-lg bg-purple-600/40 hover:bg-purple-600/60 border border-purple-400/50 transition-colors"
                  title={searchMode === 'all' ? 'Searching ALL categories - click to search current only' : `Searching ${activeTab || 'current'} only - click to search ALL`}
                >
                  <span className="text-[10px] font-bold text-purple-100">
                    {searchMode === 'all' ? '🌐ALL' : (activeTab ? activeTab.slice(0,3).toUpperCase() : '📁')}
                  </span>
                </button>
                
                {/* Clear Button */}
                {searchQuery && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchQuery('');
                      playSound('click');
                    }}
                    className="absolute right-16 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-purple-500/50 hover:bg-purple-500/70 flex items-center justify-center transition-colors"
                    title="Clear search"
                  >
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Search Results Count */}
              {searchQuery && (
                <div className="text-center text-xs text-purple-200/70">
                  Found {allFilteredItems.length} item{allFilteredItems.length !== 1 ? 's' : ''} {searchMode === 'all' ? 'across all categories' : `in ${activeTab || 'current category'}`}
                </div>
              )}
            </form>
            
            {/* Debug Info */}
            <div className="mb-4 p-3 bg-black/40 rounded-lg border border-white/10">
              <div className="text-xs text-white/60 space-y-1">
                <div className="flex items-center justify-between">
                  <span>🔑 Your userId: {user?.id || 'NOT LOGGED IN'}</span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(String(user?.id || ''));
                      alert('User ID copied to clipboard!');
                    }}
                    className="text-[9px] bg-white/10 px-2 py-1 rounded hover:bg-white/20"
                  >
                    Copy
                  </button>
                </div>
                <div>📄 Documents (entities): {filteredDocuments.length} (type: document)</div>
                <div>📋 Items (notebooks): {filteredOrganizerItems.length} (type: organizer)</div>
                <div>✅ Total Items: {allFilteredItems.length}</div>
                <div className="text-[10px] mt-2">Entities: {documents.length} docs | Notebooks loaded: {classNotebooks.length}</div>
                <div className="text-[10px] mt-1">Loading: {notebooksLoading ? 'Yes ⏳' : 'Done ✅'}</div>
                {classNotebooks.length === 0 && !notebooksLoading && (
                  <div className="text-[10px] mt-2 p-2 bg-red-500/20 border border-red-400/30 rounded">
                    ⚠️ No classes found! Check console (F12) for debug info.
                  </div>
                )}
                {classNotebooks.length > 0 && (
                  <div className="text-[10px] mt-1 border-t border-white/10 pt-1">
                    ✅ Classes loaded: {classNotebooks.map(nb => nb.title || 'Untitled').join(', ')}
                    <div className="mt-2 p-2 bg-blue-500/20 border border-blue-400/30 rounded text-[9px] max-h-40 overflow-y-auto">
                      <div>Active: {activeClassId || 'NONE'}</div>
                      <div className="mt-1">Categories: {classNotebooks.find(nb => nb.id === activeClassId)?.categories?.length || classNotebooks[0]?.categories?.length || 0}</div>
                      <div className="mt-1 border-t border-white/10 pt-1">
                        <div className="font-bold">Category List:</div>
                        {(classNotebooks.find(nb => nb.id === activeClassId) || classNotebooks[0])?.categories?.map((cat: any, idx: number) => (
                          <div key={idx} className="ml-2">• {cat.id || cat.label}</div>
                        )) || 'None'}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Add Document Button */}
            <button
              onClick={async () => {
                if (!user?.id) {
                  alert('Please log in to create documents');
                  return;
                }
                
                playSound('click');
                
                // Create new organizer item in the active class's category
                const activeNotebook = classNotebooks.find(nb => nb.id === activeClassId);
                if (!activeNotebook) {
                  alert('No active class found. Please select a class first.');
                  return;
                }
                
                const newItem = {
                  id: Date.now().toString(),
                  title: `New ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`,
                  notes: '',
                  description: '',
                  dueDate: '',
                  status: 'upcoming',
                  categoryId: activeTab,
                  categoryLabel: activeTab.charAt(0).toUpperCase() + activeTab.slice(1),
                  notebookId: activeClassId,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString()
                };
                // Add item to notebooks
                setClassNotebooks((prev) => {
                  const updated = prev.map((notebook) => {
                    if (notebook.id === activeClassId) {
                      return {
                        ...notebook,
                        categories: notebook.categories.map((category: any) => {
                          if (category.id === activeTab) {
                            return {
                              ...category,
                              buckets: {
                                ...category.buckets,
                                upcoming: [newItem, ...(category.buckets.upcoming || [])] // NEW ITEM AT TOP!
                              }
                            };
                          }
                          return category;
                        })
                      };
                    }
                    return notebook;
                  });
                  // Save to database
                  if (user?.id) {
                    saveNotebooks(String(user.id), updated);
                  }
                  
                  return updated;
                });
                
                playSound('success');
              }}
              className={`w-full mb-4 px-4 py-3 rounded-xl ${tabColors.accent} hover:opacity-90 transition-all font-semibold text-white flex items-center justify-center gap-2 shadow-lg`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Add New {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
            </button>
            
            {/* Delete Stuck Documents Button (Temporary Debug) */}
            <button
              onClick={async () => {
                if (!confirm('Delete all old "New Document", "New Discussion", "New Assignment", and "New Experiential" items from the old system?')) {
                  return;
                }
                
                playSound('click');
                
                let notebookItemsRemoved = 0;
                
                // Remove stuck items from ALL categories (discussion, assignment, experiential)
                setClassNotebooks((prev) => {
                  const updated = prev.map((notebook) => ({
                    ...notebook,
                    categories: notebook.categories.map((category: any) => {
                      const upcomingBefore = category.buckets?.upcoming?.length || 0;
                      const completedBefore = category.buckets?.completed?.length || 0;
                      
                      const upcomingFiltered = (category.buckets?.upcoming || []).filter((item: any) => {
                        // Keep items that DON'T match old system patterns
                        const isOldSystemItem = 
                          item.title === 'New Document' ||
                          item.title === 'New Discussion' ||
                          item.title === 'New Assignment' ||
                          item.title === 'New Experiential' ||
                          item.title === 'New Journal' ||
                          (item.title?.startsWith('New ') && (!item.notes || item.notes === '') && (!item.description || item.description === ''));
                        
                        if (isOldSystemItem) {
                          notebookItemsRemoved++;
                        }
                        return !isOldSystemItem;
                      });
                      
                      const completedFiltered = (category.buckets?.completed || []).filter((item: any) => {
                        const isOldSystemItem = 
                          item.title === 'New Document' ||
                          item.title === 'New Discussion' ||
                          item.title === 'New Assignment' ||
                          item.title === 'New Experiential' ||
                          item.title === 'New Journal' ||
                          (item.title?.startsWith('New ') && (!item.notes || item.notes === '') && (!item.description || item.description === ''));
                        
                        if (isOldSystemItem) {
                          notebookItemsRemoved++;
                        }
                        return !isOldSystemItem;
                      });
                      
                      const upcomingAfter = upcomingFiltered.length;
                      const completedAfter = completedFiltered.length;
                      
                      if (upcomingBefore !== upcomingAfter || completedBefore !== completedAfter) {
                      }
                      
                      return {
                        ...category,
                        buckets: {
                          upcoming: upcomingFiltered,
                          completed: completedFiltered
                        }
                      };
                    })
                  }));
                  // Save to database
                  if (user?.id) {
                    saveNotebooks(String(user.id), updated);
                  }
                  
                  return updated;
                });
                
                // Also delete any stuck document entities
                // For school work tabs (discussion, assignment, experiential), ALL documents should be deleted
                // School work should ONLY use organizer-type items
                const isSchoolWorkTab = ['discussion', 'assignment', 'experiential'].includes(activeTab);
                
                const stuckDocs = filteredDocuments.filter(doc => {
                  // If it's a school work tab, delete ALL document-type items
                  if (isSchoolWorkTab) {
                    return true;
                  }
                  
                  // Otherwise, only delete items matching old system patterns
                  const isStuck = 
                    doc.title === 'New Document' ||
                    doc.title === 'New Discussion' ||
                    doc.title === 'New Assignment' ||
                    doc.title === 'New Experiential' ||
                    doc.title === 'New Journal' ||
                    (doc.title?.startsWith('New ') && (!doc.content || doc.content === ''));
                  
                  if (isStuck) {
                  }
                  return isStuck;
                });
                let deletedCount = 0;
                for (const doc of stuckDocs) {
                  try {
                    await deleteDocument(doc.id);
                    deletedCount++;
                  } catch (err) {
                    console.error('❌ Failed to delete document:', doc.id, err);
                  }
                }
                
                await reloadDocuments();
                playSound('success');
                
                const totalRemoved = notebookItemsRemoved + deletedCount;
                alert(`🗑️ Cleaned up!\n\n• Removed ${notebookItemsRemoved} items from notebooks\n• Deleted ${deletedCount} document entities\n• Total: ${totalRemoved} items removed`);
              }}
              className="w-full mb-4 px-4 py-3 rounded-xl bg-red-600/30 hover:bg-red-600/40 border-2 border-red-400/60 transition-all font-semibold text-white flex items-center justify-center gap-2 shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              🗑️ Delete Stuck Documents
            </button>
            
            {/* Uploaded Files Section */}
            <div className="mb-6">
              {(() => {
                const uploadedFiles = getUploadedFiles();
                if (uploadedFiles.length === 0) {
                  return (
                    <div className="text-center py-4 text-slate-400 text-sm">
                      No uploaded files in this category yet.
                      <br />
                      <span className="text-xs text-slate-500">Use Upload Center to add files.</span>
                    </div>
                  );
                }
                
                return (
                  <>
                    <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      📎 Uploaded Files ({uploadedFiles.length})
                    </h3>
                    <div className="space-y-2">
                      {uploadedFiles.map((file: any) => {
                    // Extract file info from content (base64 data URL)
                    const fileSize = file.content ? Math.round(file.content.length * 0.75 / 1024) : 0;
                    
                    return (
                      <div 
                        key={file.id}
                        className="bg-black/30 border border-white/20 rounded-xl p-3 hover:bg-black/40 transition-all"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                            <svg className="w-5 h-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-white truncate">{file.title}</h4>
                            <p className="text-xs text-white/60 mt-0.5">
                              {fileSize} KB • {file.updated_at ? new Date(file.updated_at).toLocaleDateString() : 'Recently'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-3">
                          <a
                            href={file.content}
                            download={file.title}
                            className="flex-1 px-3 py-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 text-xs font-semibold flex items-center justify-center gap-2 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              playSound('click');
                            }}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download
                          </a>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              playSound('click');
                              
                              // Create a temporary link and open it
                              const link = document.createElement('a');
                              link.href = file.content;
                              link.target = '_blank';
                              link.rel = 'noopener noreferrer';
                              
                              // For better compatibility, create a blob and object URL
                              try {
                                // Convert base64 to blob
                                const byteString = atob(file.content.split(',')[1]);
                                const mimeString = file.content.split(',')[0].split(':')[1].split(';')[0];
                                const ab = new ArrayBuffer(byteString.length);
                                const ia = new Uint8Array(ab);
                                for (let i = 0; i < byteString.length; i++) {
                                  ia[i] = byteString.charCodeAt(i);
                                }
                                const blob = new Blob([ab], { type: mimeString });
                                const url = URL.createObjectURL(blob);
                                
                                // Open in new tab
                                const newWindow = window.open(url, '_blank');
                                if (!newWindow) {
                                  alert('Pop-up blocked! Please allow pop-ups to view files.');
                                }
                                
                                // Clean up after a delay
                                setTimeout(() => URL.revokeObjectURL(url), 1000);
                              } catch (err) {
                                console.error('Error opening file:', err);
                                // Fallback: just open the data URL
                                window.open(file.content, '_blank');
                              }
                            }}
                            className="flex-1 px-3 py-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 text-xs font-semibold flex items-center justify-center gap-2 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            View
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteFile(file.id);
                            }}
                            className="px-3 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 text-xs font-semibold transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                  </>
                );
              })()}
            </div>

            {/* Home/Work/Travel Filter Tabs & Forms */}
            {['home', 'work', 'travel'].includes(activeTab) && (
              <div className="space-y-3">
                {/* Current / History / Templates Toggle */}
                <div className="flex gap-2 bg-slate-800/50 rounded-lg p-1">
                  <button
                    onClick={() => { setShowHistoryView(false); setShowTemplatesView(false); playSound('click'); }}
                    className={`flex-1 px-3 py-2 rounded-md font-semibold text-xs transition-all ${
                      !showHistoryView && !showTemplatesView
                        ? 'bg-emerald-600 text-white shadow-lg' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    📝 Current
                  </button>
                  <button
                    onClick={() => { setShowHistoryView(true); setShowTemplatesView(false); playSound('click'); }}
                    className={`flex-1 px-3 py-2 rounded-md font-semibold text-xs transition-all ${
                      showHistoryView && !showTemplatesView
                        ? 'bg-purple-600 text-white shadow-lg' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    📚 History
                  </button>
                  <button
                    onClick={() => { setShowHistoryView(false); setShowTemplatesView(true); playSound('click'); }}
                    className={`flex-1 px-3 py-2 rounded-md font-semibold text-xs transition-all ${
                      showTemplatesView && !showHistoryView
                        ? 'bg-amber-600 text-white shadow-lg' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    ⭐ Templates
                  </button>
                </div>

                {!showHistoryView && !showTemplatesView ? (
                  <>
                    {/* List Name Input */}
                    <div className="relative">
                      <input
                        type="text"
                        value={currentListName}
                        onChange={(e) => setCurrentListName(e.target.value)}
                        placeholder="List name (optional) - e.g., 'Weekly Groceries', 'Costco Trip'"
                        className="w-full px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-600/50 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-emerald-400/50 focus:bg-slate-800/70 transition-colors"
                      />
                      {currentListName && (
                        <button
                          onClick={() => setCurrentListName('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    
                    {/* Filter Tabs - Horizontal Scroll */}
                    <div className="flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
                      <button 
                        onClick={() => { setActiveItemFilter('all'); playSound('click'); }}
                        className={`flex-shrink-0 snap-start px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                          activeItemFilter === 'all' 
                            ? 'bg-slate-600 text-white shadow-lg' 
                            : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                        }`}
                      >
                        All
                  </button>
                  
                  {activeTab === 'home' && (
                    <>
                      <button onClick={() => { setActiveItemFilter('shopping'); playSound('click'); }} className={`flex-shrink-0 snap-start px-4 py-2 rounded-lg font-semibold text-sm transition-all ${activeItemFilter === 'shopping' ? 'bg-green-600 text-white shadow-lg' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                        🛒 Shopping
                      </button>
                      <button onClick={() => { setActiveItemFilter('bill'); playSound('click'); }} className={`flex-shrink-0 snap-start px-4 py-2 rounded-lg font-semibold text-sm transition-all ${activeItemFilter === 'bill' ? 'bg-blue-600 text-white shadow-lg' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                        💵 Bills
                      </button>
                      <button onClick={() => { setActiveItemFilter('recipe'); playSound('click'); }} className={`flex-shrink-0 snap-start px-4 py-2 rounded-lg font-semibold text-sm transition-all ${activeItemFilter === 'recipe' ? 'bg-orange-600 text-white shadow-lg' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                        🍳 Recipes
                      </button>
                      <button onClick={() => { setActiveItemFilter('home_improvement'); playSound('click'); }} className={`flex-shrink-0 snap-start px-4 py-2 rounded-lg font-semibold text-sm transition-all ${activeItemFilter === 'home_improvement' ? 'bg-purple-600 text-white shadow-lg' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                        🔨 Projects
                      </button>
                      <button onClick={() => { setActiveItemFilter('maintenance'); playSound('click'); }} className={`flex-shrink-0 snap-start px-4 py-2 rounded-lg font-semibold text-sm transition-all ${activeItemFilter === 'maintenance' ? 'bg-yellow-600 text-white shadow-lg' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                        🔧 Maintenance
                      </button>
                    </>
                  )}
                  
                  {activeTab === 'work' && (
                    <>
                      <button onClick={() => { setActiveItemFilter('meeting'); playSound('click'); }} className={`flex-shrink-0 snap-start px-4 py-2 rounded-lg font-semibold text-sm transition-all ${activeItemFilter === 'meeting' ? 'bg-indigo-600 text-white shadow-lg' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                        📹 Meetings
                      </button>
                      <button onClick={() => { setActiveItemFilter('deadline'); playSound('click'); }} className={`flex-shrink-0 snap-start px-4 py-2 rounded-lg font-semibold text-sm transition-all ${activeItemFilter === 'deadline' ? 'bg-red-600 text-white shadow-lg' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                        🎯 Deadlines
                      </button>
                    </>
                  )}
                  
                  {activeTab === 'travel' && (
                    <>
                      <button onClick={() => { setActiveItemFilter('packing'); playSound('click'); }} className={`flex-shrink-0 snap-start px-4 py-2 rounded-lg font-semibold text-sm transition-all ${activeItemFilter === 'packing' ? 'bg-teal-600 text-white shadow-lg' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                        🧳 Packing
                      </button>
                      <button onClick={() => { setActiveItemFilter('itinerary'); playSound('click'); }} className={`flex-shrink-0 snap-start px-4 py-2 rounded-lg font-semibold text-sm transition-all ${activeItemFilter === 'itinerary' ? 'bg-cyan-600 text-white shadow-lg' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                        ✈️ Itinerary
                      </button>
                      <button onClick={() => { setActiveItemFilter('travel_expense'); playSound('click'); }} className={`flex-shrink-0 snap-start px-4 py-2 rounded-lg font-semibold text-sm transition-all ${activeItemFilter === 'travel_expense' ? 'bg-emerald-600 text-white shadow-lg' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                        💰 Budget
                      </button>
                    </>
                  )}
                </div>

                {/* Form Buttons - Context-aware (show only for active filter or all if 'all') */}
                <div className="flex flex-col gap-2">
                  {activeTab === 'home' && (
                    <>
                      {(activeItemFilter === 'all' || activeItemFilter === 'shopping') && <ShoppingListForm onAdd={handleAddNotebookItem} />}
                      {(activeItemFilter === 'all' || activeItemFilter === 'bill') && <BillsTrackerForm onAdd={handleAddNotebookItem} />}
                      {(activeItemFilter === 'all' || activeItemFilter === 'recipe') && <RecipeForm onAdd={handleAddNotebookItem} />}
                      {(activeItemFilter === 'all' || activeItemFilter === 'home_improvement') && <HomeImprovementForm onAdd={handleAddNotebookItem} />}
                      {(activeItemFilter === 'all' || activeItemFilter === 'maintenance') && <MaintenanceReminderForm onAdd={handleAddNotebookItem} />}
                    </>
                  )}
                  
                  {activeTab === 'work' && (
                    <>
                      {(activeItemFilter === 'all' || activeItemFilter === 'meeting') && <MeetingForm onAdd={handleAddNotebookItem} />}
                      {(activeItemFilter === 'all' || activeItemFilter === 'deadline') && <DeadlineForm onAdd={handleAddNotebookItem} />}
                    </>
                  )}
                  
                  {activeTab === 'travel' && (
                    <>
                      {(activeItemFilter === 'all' || activeItemFilter === 'packing') && <PackingListForm onAdd={handleAddNotebookItem} />}
                      {(activeItemFilter === 'all' || activeItemFilter === 'itinerary') && <ItineraryForm onAdd={handleAddNotebookItem} />}
                      {(activeItemFilter === 'all' || activeItemFilter === 'travel_expense') && <TravelBudgetForm onAdd={handleAddNotebookItem} />}
                    </>
                  )}
                </div>
                  </>
                ) : showHistoryView ? (
                  /* History View */
                  <div className="space-y-3">
                    {(() => {
                      const history = getGroupedHistory();
                      
                      if (history.length === 0) {
                        return (
                          <div className="text-center py-12 text-slate-400">
                            <div className="text-4xl mb-3">📚</div>
                            <p className="font-semibold">{showHiddenHistory ? 'No Hidden History' : 'No History Yet'}</p>
                            <p className="text-sm mt-1">{showHiddenHistory ? 'Hidden items will appear here' : 'Complete some items to build your history'}</p>
                          </div>
                        );
                      }
                      
                      return (
                        <>
                          {history.map(({ date, items, listName, hidden }) => (
                            <div key={date} className={`bg-black/30 border rounded-xl p-4 ${hidden ? 'border-gray-600/30 opacity-70' : 'border-purple-400/30'}`}>
                          {/* Date Header */}
                          <div className="flex items-start justify-between mb-3 pb-3 border-b border-purple-400/20">
                            <div className="flex-1">
                              {listName && (
                                <h3 className="text-xl font-bold text-purple-100 mb-1">
                                  {listName}
                                </h3>
                              )}
                              <h4 className="text-sm font-semibold text-purple-300">
                                {new Date(date).toLocaleDateString('en-US', { 
                                  weekday: 'short', 
                                  month: 'short', 
                                  day: 'numeric', 
                                  year: 'numeric' 
                                })}
                              </h4>
                              <p className="text-xs text-slate-400">{items.length} item{items.length !== 1 ? 's' : ''}</p>
                            </div>
                            <div className="flex flex-col gap-2">
                              <button
                                onClick={() => handleToggleHideList(date, hidden)}
                                className={`px-3 py-1.5 rounded-lg ${hidden ? 'bg-gray-500/20 hover:bg-gray-500/30 text-gray-200 border-gray-400/50' : 'bg-slate-500/20 hover:bg-slate-500/30 text-slate-200 border-slate-400/50'} text-xs font-semibold border transition-colors flex items-center gap-1`}
                                title={hidden ? 'Unhide this list' : 'Hide this list'}
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  {hidden ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                  )}
                                </svg>
                                {hidden ? 'Unhide' : 'Hide'}
                              </button>
                              <button
                                onClick={() => handleRenameList(date, listName)}
                                className="px-3 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 text-xs font-semibold border border-blue-400/50 transition-colors flex items-center gap-1"
                                title="Rename this list"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Rename
                              </button>
                              <button
                                onClick={() => handleSaveAsTemplate(date)}
                                className="px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 text-xs font-semibold border border-amber-400/50 transition-colors flex items-center gap-1"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                </svg>
                                Save
                              </button>
                              <button
                                onClick={() => handleRestoreGroup(date)}
                                className="px-3 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 text-xs font-semibold border border-emerald-400/50 transition-colors flex items-center gap-1"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Reuse List
                              </button>
                            </div>
                          </div>
                          
                          {/* Items List */}
                          <div className="space-y-2">
                            {items.map((item: any) => (
                              <div key={item.id} className="flex items-center gap-3 p-2 rounded-lg bg-slate-800/30">
                                <div className="w-5 h-5 rounded border-2 border-emerald-400/50 bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                                  <svg className="w-3 h-3 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-slate-300 truncate">{item.title}</p>
                                  {item.metadata?.quantity && (
                                    <p className="text-xs text-slate-500">Qty: {item.metadata.quantity}</p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                          
                          {/* Show/Hide Hidden Lists Toggle */}
                          <button
                            onClick={() => {
                              setShowHiddenHistory(!showHiddenHistory);
                              playSound('click');
                            }}
                            className="w-full px-4 py-2 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-slate-300 text-sm font-semibold transition-colors flex items-center justify-center gap-2 mt-4"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {showHiddenHistory ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              )}
                            </svg>
                            {showHiddenHistory ? 'Hide Hidden Lists' : 'Show Hidden Lists'}
                          </button>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  /* Templates View */
                  <div className="space-y-3">
                    {/* Save Current List Button */}
                    {allFilteredItems.filter(item => item.data.status !== 'completed').length > 0 && (
                      <button
                        onClick={() => handleSaveAsTemplate()}
                        className="w-full px-4 py-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold transition-colors flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                        Save Current List as Template
                      </button>
                    )}
                    
                    {/* Templates List */}
                    {(() => {
                      const filteredTemplates = listTemplates.filter(t => 
                        t.category === activeTab && 
                        (activeItemFilter === 'all' || t.itemType === activeItemFilter)
                      );
                      
                      if (filteredTemplates.length === 0) {
                        return (
                          <div className="text-center py-12 text-slate-400">
                            <div className="text-4xl mb-3">⭐</div>
                            <p className="font-semibold">No Templates Yet</p>
                            <p className="text-sm mt-1">Save lists you use often for quick access</p>
                          </div>
                        );
                      }
                      
                      return filteredTemplates.map((template) => (
                        <div key={template.id} className="bg-black/30 border border-amber-400/30 rounded-xl p-4">
                          {/* Template Header */}
                          <div className="flex items-start justify-between mb-3 pb-3 border-b border-amber-400/20">
                            <div className="flex-1">
                              <h4 className="text-lg font-semibold text-amber-200 flex items-center gap-2">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                                {template.name}
                              </h4>
                              <p className="text-xs text-slate-400 mt-1">
                                {template.items.length} item{template.items.length !== 1 ? 's' : ''}
                                {template.lastUsed && ` • Last used ${new Date(template.lastUsed).toLocaleDateString()}`}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleLoadTemplate(template.id)}
                                className="px-3 py-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 text-sm font-semibold border border-emerald-400/50 transition-colors"
                              >
                                Load
                              </button>
                              <button
                                onClick={() => handleDeleteTemplate(template.id)}
                                className="px-3 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 text-sm font-semibold border border-red-400/50 transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                          
                          {/* Items Preview */}
                          <div className="space-y-1">
                            {template.items.slice(0, 3).map((item: any, idx: number) => (
                              <div key={idx} className="text-xs text-slate-400 truncate">
                                • {item.title}
                              </div>
                            ))}
                            {template.items.length > 3 && (
                              <div className="text-xs text-slate-500 italic">
                                +{template.items.length - 3} more...
                              </div>
                            )}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>
            )}
            
            {!showHistoryView && !showTemplatesView && allFilteredItems.length > 0 ? (
              <div className="space-y-3">
                {allFilteredItems.map((item) => {
                  const dueBadge = getDueDateBadge(item.data?.dueDate);
                  const isCompleted = item.data?.status === 'completed';
                  const dueDate = item.data?.dueDate;
                  const dueSoon = dueDate ? (() => {
                    const due = new Date(dueDate);
                    const now = new Date();
                    const daysUntilDue = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                    return daysUntilDue <= 3 && daysUntilDue >= 0;
                  })() : false;
                  
                  return (
                    <div 
                      key={`${item.type}-${item.id}`}
                      className="bg-black/30 border border-white/20 rounded-xl p-4 hover:bg-black/40 transition-all"
                    >
                      {/* Checkbox + Title */}
                      <div className="flex items-center gap-3 mb-3">
                        <input
                          type="checkbox"
                          checked={isCompleted}
                          onChange={() => {
                            if (item.type === 'organizer') {
                              handleToggleCompleted(item.id, item.data.categoryId);
                            } else if (item.type === 'calendar-event') {
                              // Toggle calendar event completion
                              const newStatus = isCompleted ? 'pending' : 'completed';
                              updateCalendarEvent(item.id, { status: newStatus }).then(() => {
                                if (user?.id) {
                                  getCalendarEvents(String(user.id)).then(setCalendarEvents);
                                }
                              });
                            }
                          }}
                          className="w-5 h-5 rounded border-2 border-white/50 bg-black/40 checked:bg-emerald-500 checked:border-emerald-400 cursor-pointer"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className={`text-base font-semibold ${isCompleted ? 'line-through text-emerald-300/70' : 'text-white'}`}>
                            {item.title}
                          </h4>
                          {searchMode === 'all' && item.data?.categoryLabel && (
                            <div className="text-[10px] text-purple-300 mt-0.5 font-medium">
                              📁 {item.data.categoryLabel}
                            </div>
                          )}
                          {item.type === 'calendar-event' && (
                            <div className="text-[10px] text-cyan-300 mt-0.5 font-medium flex items-center gap-1">
                              📅 Calendar Event
                            </div>
                          )}
                        </div>
                        <span className="text-[10px] text-white/50 uppercase">
                          {item.type === 'document' ? '📄' : item.type === 'calendar-event' ? '📅' : '📋'}
                        </span>
                      </div>

                      {/* Action Buttons Row */}
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {/* Move Button */}
                        {item.type === 'organizer' && (
                          <div className="relative">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMoveMenuId(openMoveMenuId === item.id ? null : item.id);
                                playSound('click');
                              }}
                              className="px-2 py-1 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 text-xs font-semibold border border-purple-400/50 transition-colors"
                            >
                              Move →
                            </button>
                            {openMoveMenuId === item.id && (
                              <div className="absolute left-0 top-full mt-2 bg-gray-900 border-2 border-purple-400/50 rounded-lg shadow-2xl z-50 min-w-[180px] max-h-[300px] overflow-y-auto">
                                {/* Categories Section */}
                                <div className="border-b border-purple-400/30 py-1">
                                  <div className="px-3 py-1 text-[10px] text-purple-300/70 uppercase tracking-wider">Move to Category:</div>
                                  {['discussion', 'assignment', 'experiential', 'journal', 'home', 'work', 'travel']
                                    .filter(cat => cat !== activeTab)
                                    .map(cat => (
                                      <button
                                        key={cat}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleMoveItem(item.id, item.data.categoryId, cat);
                                          setOpenMoveMenuId(null);
                                        }}
                                        className="w-full px-3 py-2 text-left text-xs text-purple-200 hover:bg-purple-500/20 transition-colors"
                                      >
                                        → {cat.charAt(0).toUpperCase() + cat.slice(1)}
                                      </button>
                                    ))}
                                </div>
                                
                                {/* Classes Section */}
                                {classNotebooks.length > 1 && (
                                  <div className="py-1">
                                    <div className="px-3 py-1 text-[10px] text-purple-300/70 uppercase tracking-wider">Move to Class:</div>
                                    {classNotebooks
                                      .filter(nb => nb.id !== item.data.notebookId)
                                      .map(notebook => (
                                        <button
                                          key={notebook.id}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleMoveToClass(item.id, item.data.categoryId, notebook.id);
                                            setOpenMoveMenuId(null);
                                          }}
                                          className="w-full px-3 py-2 text-left text-xs text-purple-200 hover:bg-purple-500/20 transition-colors truncate"
                                        >
                                          → {notebook.title || 'Untitled Class'}
                                        </button>
                                      ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Complete Button */}
                        {item.type === 'organizer' && (
                          <button
                            onClick={() => handleToggleCompleted(item.id, item.data.categoryId)}
                            className="p-1.5 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-300 transition-colors"
                            title={isCompleted ? "Mark incomplete" : "Mark as complete"}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        )}

                        {/* Edit Button */}
                        {item.type === 'organizer' && (
                          <button
                            onClick={() => {
                              setEditingItem(item);
                              setEditItemTitle(item.title);
                            }}
                            className="p-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 transition-colors"
                            title="Edit item"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                        )}

                        {/* Edit/Delete Buttons for Calendar Events */}
                        {item.type === 'calendar-event' && !isCompleted && (
                          <>
                            <button
                              onClick={() => handleEditEvent(item.data)}
                              className="p-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 transition-colors"
                              title="Edit event"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteEvent(item.id)}
                              className="p-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-colors"
                              title="Delete event"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </>
                        )}

                        {/* Date Display */}
                        {dueDate && (
                          <div className="flex items-center gap-2 text-xs ml-auto">
                            <svg className="w-3.5 h-3.5 text-amber-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-amber-200">
                              {new Date(dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                            {/* DUE SOON Badge */}
                            {dueSoon && !isCompleted && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-red-500/30 text-red-100 border border-red-400/60 animate-pulse"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                </svg>
                                DUE SOON
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Description/Content */}
                      {item.content && (
                        <p className={`text-sm ${tabColors.text} line-clamp-2 mt-2`}>
                          {item.content.replace(/<[^>]*>/g, '').substring(0, 100)}...
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">📄</div>
                <p className={`text-lg font-semibold ${tabColors.text} mb-2`}>
                  No documents yet
                </p>
                <p className="text-sm text-white/60 mb-4">
                  Create your first {activeTab} document to get started
                </p>
                <button
                  onClick={() => {
                    closeTabOverlay();
                    playSound('success');
                    // TODO: Navigate to document creation
                    alert('Document creation coming soon!');
                  }}
                  className={`px-4 py-2 rounded-lg ${tabColors.accent} hover:opacity-80 transition-all font-semibold`}
                >
                  ➕ Create Document
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Form Selector Modal - COMPLETELY SEPARATE, FULL SCREEN */}
      {showFormSelector && (
        <div className="fixed inset-0 z-[10000] bg-slate-900 flex flex-col">
          <div className="p-8 border-b border-white/20 flex items-center justify-between flex-shrink-0">
            <h2 className="text-4xl font-bold text-white">What would you like to add?</h2>
            <button
              onClick={() => setShowFormSelector(false)}
              className="w-16 h-16 bg-white/10 hover:bg-white/20 border-2 border-white/30 rounded-2xl flex items-center justify-center text-white text-4xl transition-all touch-manipulation"
            >
              ×
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-8">
            <div className="grid grid-cols-2 gap-8 max-w-3xl mx-auto">
              
              {/* Work forms */}
              {mobileOverlayData.type === 'work' && (
                <>
                  <button onClick={() => { setActiveFormType('meeting'); setShowFormSelector(false); }} className="aspect-square bg-gradient-to-br from-indigo-500/30 to-indigo-700/30 border-4 border-indigo-400/50 rounded-3xl flex flex-col items-center justify-center gap-4 active:scale-95 transition-transform touch-manipulation shadow-2xl">
                    <span className="text-8xl">📹</span>
                    <span className="text-white text-2xl font-bold">Meeting</span>
                  </button>
                  <button onClick={() => { setActiveFormType('deadline'); setShowFormSelector(false); }} className="aspect-square bg-gradient-to-br from-red-500/30 to-red-700/30 border-4 border-red-400/50 rounded-3xl flex flex-col items-center justify-center gap-4 active:scale-95 transition-transform touch-manipulation shadow-2xl">
                    <span className="text-8xl">🎯</span>
                    <span className="text-white text-2xl font-bold">Deadline</span>
                  </button>
                </>
              )}
              
              {/* Home forms */}
              {mobileOverlayData.type === 'home' && (
                <>
                  <button onClick={() => { setActiveFormType('shopping'); setShowFormSelector(false); }} className="aspect-square bg-gradient-to-br from-green-500/30 to-green-700/30 border-4 border-green-400/50 rounded-3xl flex flex-col items-center justify-center gap-4 active:scale-95 transition-transform touch-manipulation shadow-2xl">
                    <span className="text-8xl">🛒</span>
                    <span className="text-white text-2xl font-bold">Shopping</span>
                  </button>
                  <button onClick={() => { setActiveFormType('bill'); setShowFormSelector(false); }} className="aspect-square bg-gradient-to-br from-blue-500/30 to-blue-700/30 border-4 border-blue-400/50 rounded-3xl flex flex-col items-center justify-center gap-4 active:scale-95 transition-transform touch-manipulation shadow-2xl">
                    <span className="text-8xl">💵</span>
                    <span className="text-white text-2xl font-bold">Bill</span>
                  </button>
                  <button onClick={() => { setActiveFormType('recipe'); setShowFormSelector(false); }} className="aspect-square bg-gradient-to-br from-orange-500/30 to-orange-700/30 border-4 border-orange-400/50 rounded-3xl flex flex-col items-center justify-center gap-4 active:scale-95 transition-transform touch-manipulation shadow-2xl">
                    <span className="text-8xl">🍳</span>
                    <span className="text-white text-2xl font-bold">Recipe</span>
                  </button>
                  <button onClick={() => { setActiveFormType('project'); setShowFormSelector(false); }} className="aspect-square bg-gradient-to-br from-purple-500/30 to-purple-700/30 border-4 border-purple-400/50 rounded-3xl flex flex-col items-center justify-center gap-4 active:scale-95 transition-transform touch-manipulation shadow-2xl">
                    <span className="text-8xl">🔨</span>
                    <span className="text-white text-2xl font-bold">Project</span>
                  </button>
                  <button onClick={() => { setActiveFormType('maintenance'); setShowFormSelector(false); }} className="aspect-square bg-gradient-to-br from-yellow-500/30 to-yellow-700/30 border-4 border-yellow-400/50 rounded-3xl flex flex-col items-center justify-center gap-4 active:scale-95 transition-transform touch-manipulation shadow-2xl">
                    <span className="text-8xl">🔧</span>
                    <span className="text-white text-2xl font-bold">Maintenance</span>
                  </button>
                </>
              )}
              
              {/* Travel forms */}
              {mobileOverlayData.type === 'travel' && (
                <>
                  <button onClick={() => { setActiveFormType('packing'); setShowFormSelector(false); }} className="aspect-square bg-gradient-to-br from-teal-500/30 to-teal-700/30 border-4 border-teal-400/50 rounded-3xl flex flex-col items-center justify-center gap-4 active:scale-95 transition-transform touch-manipulation shadow-2xl">
                    <span className="text-8xl">🧳</span>
                    <span className="text-white text-2xl font-bold">Packing</span>
                  </button>
                  <button onClick={() => { setActiveFormType('itinerary'); setShowFormSelector(false); }} className="aspect-square bg-gradient-to-br from-cyan-500/30 to-cyan-700/30 border-4 border-cyan-400/50 rounded-3xl flex flex-col items-center justify-center gap-4 active:scale-95 transition-transform touch-manipulation shadow-2xl">
                    <span className="text-8xl">✈️</span>
                    <span className="text-white text-2xl font-bold">Itinerary</span>
                  </button>
                </>
              )}

              {/* Class category forms - Discussion, Assignment, Experiential, Journal */}
              {(mobileOverlayData.type === 'discussion' || mobileOverlayData.type === 'assignment' || mobileOverlayData.type === 'experiential' || mobileOverlayData.type === 'journal') && (
                <>
                  <button onClick={() => { setActiveFormType('class-item'); setShowFormSelector(false); }} className="aspect-square bg-gradient-to-br from-violet-500/30 to-violet-700/30 border-4 border-violet-400/50 rounded-3xl flex flex-col items-center justify-center gap-4 active:scale-95 transition-transform touch-manipulation shadow-2xl col-span-2">
                    <span className="text-8xl">
                      {mobileOverlayData.type === 'discussion' ? '💬' : 
                       mobileOverlayData.type === 'experiential' ? '🧪' : 
                       mobileOverlayData.type === 'journal' ? '📓' : '📝'}
                    </span>
                    <span className="text-white text-2xl font-bold">
                      {mobileOverlayData.type === 'discussion' ? 'Discussion Item' : 
                       mobileOverlayData.type === 'experiential' ? 'Experiential Item' : 
                       mobileOverlayData.type === 'journal' ? 'Journal Entry' : 'Assignment Item'}
                    </span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Active Form Modal - COMPLETELY SEPARATE, FULL SCREEN */}
      {activeFormType && (
        <div className="fixed inset-0 z-[10001] bg-slate-900 flex flex-col">
          <div className="p-6 border-b border-white/20 flex items-center justify-between flex-shrink-0">
            <h2 className="text-3xl font-bold text-white">
              {activeFormType === 'meeting' && '📹 Meeting'}
              {activeFormType === 'deadline' && '🎯 Deadline'}
              {activeFormType === 'shopping' && '🛒 Shopping List'}
              {activeFormType === 'bill' && '💵 Bill Tracker'}
              {activeFormType === 'recipe' && '🍳 Recipe'}
              {activeFormType === 'project' && '🔨 Home Project'}
              {activeFormType === 'maintenance' && '🔧 Maintenance'}
              {activeFormType === 'packing' && '🧳 Packing List'}
              {activeFormType === 'itinerary' && '✈️ Itinerary'}
            </h2>
            <button
              onClick={() => setActiveFormType(null)}
              className="w-14 h-14 bg-white/10 hover:bg-white/20 border-2 border-white/30 rounded-xl flex items-center justify-center text-white text-3xl transition-all touch-manipulation"
            >
              ←
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-6">
            <div className="w-full max-w-2xl mx-auto">
              {activeFormType === 'meeting' && <MeetingForm onAdd={handleAddNotebookItem} editingItem={editingItem} />}
              {activeFormType === 'deadline' && <DeadlineForm onAdd={handleAddNotebookItem} editingItem={editingItem} />}
              {activeFormType === 'shopping' && <ShoppingListForm onAdd={handleAddNotebookItem} editingItem={editingItem} />}
              {activeFormType === 'bill' && <BillsTrackerForm onAdd={handleAddNotebookItem} editingItem={editingItem} />}
              {activeFormType === 'recipe' && <RecipeForm onAdd={handleAddNotebookItem} editingItem={editingItem} />}
              {activeFormType === 'project' && <HomeImprovementForm onAdd={handleAddNotebookItem} editingItem={editingItem} />}
              {activeFormType === 'maintenance' && <MaintenanceReminderForm onAdd={handleAddNotebookItem} editingItem={editingItem} />}
              {activeFormType === 'packing' && <PackingListForm onAdd={handleAddNotebookItem} editingItem={editingItem} />}
              {activeFormType === 'itinerary' && <ItineraryForm onAdd={handleAddNotebookItem} editingItem={editingItem} />}
              {activeFormType === 'class-item' && <ClassItemForm onAdd={handleAddNotebookItem} editingItem={editingItem} categoryType={mobileOverlayData.type as any} />}
            </div>
          </div>
        </div>
      )}


      {/* Edit Item Dialog */}
      {editingItem && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[70] flex items-center justify-center p-4">
          <div className={`bg-gradient-to-br ${tabColors.bg} border-2 ${tabColors.border} rounded-2xl p-6 max-w-md w-full`}>
            <h3 className={`text-xl font-bold ${tabColors.text} mb-4`}>✏️ Edit Title</h3>
            <input
              type="text"
              value={editItemTitle}
              onChange={(e) => setEditItemTitle(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/20 text-white text-base mb-4 focus:outline-none focus:border-white/40"
              placeholder="Enter new title..."
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setEditingItem(null);
                  setEditItemTitle('');
                }}
                className="flex-1 px-4 py-3 rounded-lg bg-white/10 hover:bg-white/20 text-white font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (editItemTitle.trim()) {
                    handleUpdateItem(editingItem.id, editingItem.data.categoryId, editItemTitle.trim());
                  }
                }}
                className={`flex-1 px-4 py-3 rounded-lg ${tabColors.accent} hover:opacity-80 text-white font-semibold transition-all`}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Music Player Overlay - Fixed positioning below header */}
      {showMusicPlayer && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-start justify-center overflow-y-auto" onClick={() => setShowMusicPlayer(false)} style={{paddingTop: '80px'}}>
          <div className="w-full max-w-md px-4 pb-8" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-br from-purple-900/95 to-pink-900/95 border-2 border-purple-400/50 rounded-3xl p-6 space-y-4 shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white">🎵 Now Playing</h3>
                <button
                  onClick={() => setShowMusicPlayer(false)}
                  className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                >
                  ✕
                </button>
              </div>

              {/* Album Art */}
              <div className="w-full aspect-square bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg">
                <span className="text-6xl">🎧</span>
              </div>

              {/* Track Info */}
              <div className="text-center">
                <h4 className="text-xl font-bold text-white">Lo-Fi Study Beats</h4>
                <p className="text-sm text-purple-200/80">Chill Vibes Playlist</p>
              </div>

              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="w-full h-2 bg-purple-900/50 rounded-full overflow-hidden">
                  <div className="w-2/3 h-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
                </div>
                <div className="flex justify-between text-xs text-purple-200/60">
                  <span>2:34</span>
                  <span>3:45</span>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-center gap-6 pt-2">
                <button className="text-purple-200 hover:text-white transition-colors">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"></path></svg>
                </button>
                <button className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white hover:brightness-110 transition-all shadow-lg">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"></path></svg>
                </button>
                <button className="text-purple-200 hover:text-white transition-colors">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"></path></svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Side Tabs - Fixed position siblings of main content */}
      {/* AI Assistant Tab - Below music - ALWAYS VISIBLE */}
      {!showMusicPlayer && (
        <div className="fixed right-0 top-[35%] z-40">
          <div
            onClick={() => setShowAIPanel(true)}
            className="transition-all duration-500 cursor-pointer group translate-x-[calc(100%-2rem)] hover:translate-x-0"
          >
          <div className="backdrop-blur-xl bg-gradient-to-r from-blue-600 to-cyan-600 rounded-l-2xl p-4 pl-3 shadow-[0_0_20px_rgba(59,130,246,0.5)] border-l border-t border-b border-blue-400">
              <div className="flex items-center gap-3">
                <div className="relative -ml-2">
                  <svg className="w-6 h-6 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-400 rounded-full animate-ping" />
                </div>
                <span className="text-white font-black whitespace-nowrap tracking-wider">AI ASSISTANT</span>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Snack Dial Tab - Below AI - ALWAYS VISIBLE */}
        {!showMusicPlayer && (
          <div className="fixed right-0 top-[50%] z-40">
            <div
              onClick={() => setShowSnackPanel(true)}
              className="transition-all duration-500 cursor-pointer group translate-x-[calc(100%-2rem)] hover:translate-x-0"
            >
            <div className="backdrop-blur-xl bg-gradient-to-r from-pink-600 to-rose-900 rounded-l-2xl p-4 pl-3 shadow-[0_0_20px_rgba(236,72,153,0.5)] border-l border-t border-b border-pink-400">
              <div className="flex items-center gap-3">
                <div className="relative -ml-2">
                  <svg className="w-6 h-6 text-white animate-pulse" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
                    <path d="M7 2v20" />
                    <path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
                  </svg>
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-yellow-400 rounded-full animate-ping" />
                </div>
                <span className="text-white font-black whitespace-nowrap tracking-wider">SNACK DIAL</span>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* To-Do List Tab - Below Snack - ALWAYS VISIBLE */}
        {!showMusicPlayer && (
          <div className="fixed right-0 top-[65%] z-40">
            <div
              onClick={() => setShowTodoList(true)}
              className="transition-all duration-500 cursor-pointer group translate-x-[calc(100%-2rem)] hover:translate-x-0"
            >
            <div className="backdrop-blur-xl bg-gradient-to-r from-emerald-600 to-teal-600 rounded-l-2xl p-4 pl-3 shadow-[0_0_20px_rgba(16,185,129,0.5)] border-l border-t border-b border-emerald-400 relative">
              <div className="flex items-center gap-3">
                <div className="relative -ml-2">
                  <svg className="w-6 h-6 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  {(() => {
                    const savedSettings = localStorage.getItem('whipt_todolist_badge');
                    const badgeCount = savedSettings ? parseInt(savedSettings) : 0;
                    return badgeCount > 0 ? (
                      <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full text-white text-xs font-bold flex items-center justify-center">
                        {badgeCount}
                      </span>
                    ) : null;
                  })()}
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-cyan-400 rounded-full animate-ping" />
                </div>
                <span className="text-white font-black whitespace-nowrap tracking-wider">TO-DO LIST</span>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Messages Tab - Below To-Do */}
        {!showAIPanel && !showMusicPlayer && (
          <div className="fixed right-0 top-[80%] z-40">
            <div
              onClick={() => navigate('/organizer/messages')}
              className="transition-all duration-500 cursor-pointer group translate-x-[calc(100%-2rem)] hover:translate-x-0"
            >
            <div className="backdrop-blur-xl bg-gradient-to-r from-purple-600 to-pink-600 rounded-l-2xl p-4 pl-3 shadow-[0_0_20px_rgba(168,85,247,0.5)] border-l border-t border-b border-purple-400">
              <div className="flex items-center gap-3">
                <div className="relative -ml-2">
                  <svg className="w-6 h-6 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  {unreadMessageCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse border-2 border-purple-600">
                      {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                    </span>
                  )}
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-rose-400 rounded-full animate-ping" />
                </div>
                <span className="text-white font-black whitespace-nowrap tracking-wider">MESSAGES</span>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Main Content - Scrollable container */}
        <div className="pt-[64px] h-screen overflow-y-auto pb-20" style={{overscrollBehavior: 'contain'}}>
          <div className="px-4 py-4">
            <div className="rounded-3xl border-2 border-amber-500/20 bg-gradient-to-br from-[#2a1204]/90 via-[#1a0a02]/95 to-[#0a0501]/98 shadow-2xl px-4 py-4 space-y-3">
            
            {/* Class Tabs - Show actual classes with notebook-style tabs */}
            <div className="-mx-4 px-4 -mt-2">
              {(() => {
                // Filter classes by active semester and archive status
                const filteredClasses = classNotebooks.filter(nb => {
                  const nbSemester = nb.semester || 'Spring';
                  const nbYear = nb.year || new Date().getFullYear();
                  const matchesSemester = `${nbSemester} ${nbYear}` === activeSemester;
                  const matchesArchive = showArchivedClasses || !nb.archived;
                  return matchesSemester && matchesArchive;
                });
                
                return filteredClasses.length > 0 ? (
                <div>
                  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x' }}>
                    {filteredClasses.map((notebook, index) => {
                      const isActive = notebook.id === activeClassId;
                      // Color rotation for each class
                      const colors = [
                        { from: 'from-purple-600', to: 'to-purple-800', shadow: 'shadow-purple-500/50', inactive: 'from-purple-600/20 to-purple-800/10' },
                        { from: 'from-blue-600', to: 'to-blue-800', shadow: 'shadow-blue-500/50', inactive: 'from-blue-600/20 to-blue-800/10' },
                        { from: 'from-emerald-600', to: 'to-emerald-800', shadow: 'shadow-emerald-500/50', inactive: 'from-emerald-600/20 to-emerald-800/10' },
                        { from: 'from-amber-600', to: 'to-amber-800', shadow: 'shadow-amber-500/50', inactive: 'from-amber-600/20 to-amber-800/10' },
                        { from: 'from-pink-600', to: 'to-pink-800', shadow: 'shadow-pink-500/50', inactive: 'from-pink-600/20 to-pink-800/10' },
                      ];
                      const color = colors[index % colors.length];
                      return (
                        <div key={notebook.id} className="relative flex-shrink-0 group min-w-[140px]">
                          {/* Archive button - Smaller, always visible on mobile */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              
                              // Confirmation for archiving
                              if (!notebook.archived && !window.confirm(`Archive class "${notebook.title}"?\n\nYou can restore it anytime by toggling archived classes.`)) {
                                return;
                              }
                              
                              const updated = classNotebooks.map(nb => 
                                nb.id === notebook.id ? { ...nb, archived: !nb.archived } : nb
                              );
                              setClassNotebooks(updated);
                              if (user?.id) {
                                saveNotebooks(String(user.id), updated);
                              }
                              playSound('success');
                            }}
                            className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500/80 hover:bg-amber-600 active:scale-95 flex items-center justify-center z-30 transition-all"
                            title={notebook.archived ? "Unarchive class" : "Archive class"}
                          >
                            <span className="text-white text-xs font-black leading-none">{notebook.archived ? '↑' : '↓'}</span>
                          </button>
                          {/* Edit button - Smaller, always visible on mobile */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClass(notebook.id);
                            }}
                            className="absolute -top-1 right-4 w-5 h-5 rounded-full bg-slate-700/80 hover:bg-slate-600 active:scale-95 flex items-center justify-center z-30 transition-all"
                            title="Edit class"
                          >
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              playSound('click');
                              setActiveClassId(notebook.id);
                            }}
                            className={`notebook-tab w-full px-6 py-3 pt-5 text-sm font-semibold relative bg-gradient-to-b ${
                              isActive
                                ? `${color.from} ${color.to} text-white shadow-lg ${color.shadow} animate-[slideUp_0.3s_ease-out]` 
                                : `${color.inactive} text-white/70 hover:text-white hover:scale-105`
                            } transition-all duration-300 flex items-center gap-2`}
                            style={{
                              clipPath: isActive 
                                ? 'polygon(5% 0%, 95% 0%, 100% 100%, 0% 100%)'
                                : 'polygon(10% 0%, 90% 0%, 100% 100%, 0% 100%)'
                            }}
                          >
                            {notebook.teacherPhoto ? (
                              <img 
                                src={notebook.teacherPhoto} 
                                alt="Teacher" 
                                className="w-8 h-8 rounded-full object-cover border-2 border-white/30"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-lg">
                                {notebook.title?.[0] || '📚'}
                              </div>
                            )}
                            <span className="flex items-center gap-1">
                              {notebook.title}
                              {notebook.archived && <span className="text-xs opacity-60">🗃️</span>}
                            </span>
                          </button>
                        </div>
                      );
                    })}
                    <button 
                      onClick={() => { playSound('click'); setShowAddClassModal(true); }} 
                      className="notebook-tab flex-shrink-0 px-6 py-3 bg-gradient-to-b from-emerald-600/20 to-emerald-600/10 text-emerald-300 text-sm font-semibold hover:from-emerald-600/30 hover:to-emerald-600/15 transition-all border border-dashed border-emerald-400/30"
                      style={{ clipPath: 'polygon(10% 0%, 90% 0%, 100% 100%, 0% 100%)' }}
                    >
                      ➕ Add Class
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-1 overflow-x-auto pb-2">
                  <button 
                    onClick={() => { playSound('click'); setShowAddClassModal(true); }} 
                    className="notebook-tab flex-shrink-0 px-6 py-3 border-2 border-dashed border-emerald-400/40 bg-gradient-to-b from-emerald-600/15 to-emerald-600/5 backdrop-blur-xl text-emerald-200 text-sm font-semibold transition-all hover:from-emerald-600/25 hover:to-emerald-600/10"
                  >
                    ➕ Add Class to {activeSemester}
                  </button>
                </div>
              );
              })()}
            </div>

            {/* Add Buttons */}
            <div className="grid grid-cols-2 gap-2">
              <div className="relative">
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-32 h-10 bg-amber-500/20 rounded-full blur-lg pulse-glow"></div>
                </div>
                <button onClick={() => setShowCategorySelector(true)} className="flash-amber relative flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border-2 border-amber-400/40 bg-gradient-to-r from-amber-600/20 to-orange-600/20 backdrop-blur-xl shadow-lg hover:from-amber-600/40 hover:to-orange-600/40 transition-all w-full">
                  <svg className="w-3.5 h-3.5 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
                  <span className="text-xs font-semibold text-amber-200">Schoolwork</span>
                </button>
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-32 h-10 bg-blue-500/20 rounded-full blur-lg pulse-glow"></div>
                </div>
                <button onClick={() => setShowCalendarImport(true)} className="flash-blue relative flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border-2 border-blue-400/40 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 backdrop-blur-xl shadow-lg hover:from-blue-600/40 hover:to-cyan-600/40 transition-all w-full">
                  <svg className="w-3.5 h-3.5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
                  <span className="text-xs font-semibold text-blue-200">Import Calendar</span>
                </button>
              </div>
            </div>

            {/* Ticker - Rolodex Style with Sliding Animation */}
            <div className="relative h-14 rounded-2xl border border-white/20 bg-black/40 shadow-inner overflow-hidden">
              <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                <div 
                  className="text-center w-full px-12 animate-[slideUp_0.5s_ease-out]"
                  key={tickerIndex}
                >
                  <div className="text-sm font-bold text-white">{tickerItems[tickerIndex].text}</div>
                  <div className="text-xs text-amber-300 mt-0.5">📅 Due: {tickerItems[tickerIndex].date}</div>
                </div>
              </div>
              {/* Rolodex indicators */}
              <div className="absolute left-2 top-1/2 -translate-y-1/2 z-10">
                <button 
                  onClick={() => setTickerIndex((prev) => (prev - 1 + tickerItems.length) % tickerItems.length)}
                  className="text-white/40 hover:text-white transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
                </button>
              </div>
              <div className="absolute right-2 top-1/2 -translate-y-1/2 z-10">
                <button 
                  onClick={() => setTickerIndex((prev) => (prev + 1) % tickerItems.length)}
                  className="text-white/40 hover:text-white transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                </button>
              </div>
            </div>

            {/* VERTICAL STACKED TABS */}
            <div className="space-y-2">
              {/* Add New Tab Button */}
              <button onClick={handleAddNotebookTab} className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-amber-400/60 bg-amber-600/10 text-amber-100 hover:border-amber-400 hover:bg-amber-600/20 hover:text-white transition-all shadow-lg">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
                <span className="text-sm font-semibold">Add Tab</span>
              </button>

              {/* School Header */}
              <div className="px-2 py-1 text-xs font-bold text-white/50 uppercase tracking-widest">
                School
              </div>

              <button onClick={() => openTabOverlay('discussion')} className="w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 border-blue-400/60 bg-blue-600/30 backdrop-blur-xl shadow-lg hover:scale-[1.02] hover:bg-blue-600/40 transition-all hover-flash-blue">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <svg className="w-5 h-5 text-blue-100 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path></svg>
                  <div className="text-left flex-1 min-w-0">
                    <div className="text-sm font-bold text-blue-100">Discussion</div>
                    <div className="text-xs text-blue-200/60 mt-0.5">
                      {(() => {
                        const activeClass = classNotebooks.find(nb => nb.id === activeClassId);
                        return activeClass?.courseCode || activeClass?.code || activeClass?.title?.split(' - ')[0] || activeClass?.title || '';
                      })()}
                    </div>
                  </div>
                </div>
                {(() => {
                  const activeNotebook = classNotebooks.find(nb => nb.id === activeClassId);
                  const category = activeNotebook?.categories?.find((c: any) => c.id === 'discussion');
                  const upcomingItems = category?.buckets?.upcoming || [];
                  const dueSoonCount = upcomingItems.filter((item: any) => {
                    if (!item.dueDate) return false;
                    const dueDate = new Date(item.dueDate);
                    const now = new Date();
                    const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                    return daysUntilDue <= 3 && daysUntilDue >= 0;
                  }).length;
                  return dueSoonCount > 0 ? (
                    <span className="text-xs text-red-300 animate-pulse">⚠️ {dueSoonCount} Due Soon</span>
                  ) : null;
                })()}
              </button>

              <button onClick={() => openTabOverlay('assignment')} className="w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 border-amber-400/60 bg-amber-600/30 backdrop-blur-xl shadow-lg hover:scale-[1.02] hover:bg-amber-600/40 transition-all hover-flash-amber">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <svg className="w-5 h-5 text-amber-100 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                  <div className="text-left flex-1 min-w-0">
                    <div className="text-sm font-bold text-amber-100">Assignment</div>
                    <div className="text-xs text-amber-200/60 mt-0.5">
                      {(() => {
                        const activeClass = classNotebooks.find(nb => nb.id === activeClassId);
                        return activeClass?.courseCode || activeClass?.code || activeClass?.title?.split(' - ')[0] || activeClass?.title || '';
                      })()}
                    </div>
                  </div>
                </div>
                {(() => {
                  const activeNotebook = classNotebooks.find(nb => nb.id === activeClassId);
                  const category = activeNotebook?.categories?.find((c: any) => c.id === 'assignment');
                  const upcomingItems = category?.buckets?.upcoming || [];
                  const dueSoonCount = upcomingItems.filter((item: any) => {
                    if (!item.dueDate) return false;
                    const dueDate = new Date(item.dueDate);
                    const now = new Date();
                    const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                    return daysUntilDue <= 3 && daysUntilDue >= 0;
                  }).length;
                  return dueSoonCount > 0 ? (
                    <span className="text-xs text-red-300 animate-pulse">⚠️ {dueSoonCount} Due Soon</span>
                  ) : null;
                })()}
              </button>

              <button onClick={() => openTabOverlay('experiential')} className="w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 border-emerald-400/60 bg-emerald-600/30 backdrop-blur-xl shadow-lg hover:scale-[1.02] hover:bg-emerald-600/40 transition-all hover-flash-emerald">
                <div className="flex items-center gap-3 flex-1">
                  <svg className="w-5 h-5 text-emerald-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"></path></svg>
                  <div className="text-left flex-1">
                    <div className="text-sm font-bold text-emerald-100">Experiential</div>
                    <div className="text-xs text-emerald-200/60 mt-0.5">
                      {(() => {
                        const activeClass = classNotebooks.find(nb => nb.id === activeClassId);
                        return activeClass?.courseCode || activeClass?.code || activeClass?.title?.split(' - ')[0] || activeClass?.title || '';
                      })()}
                    </div>
                  </div>
                </div>
                {(() => {
                  const activeNotebook = classNotebooks.find(nb => nb.id === activeClassId);
                  const category = activeNotebook?.categories?.find((c: any) => c.id === 'experiential');
                  const upcomingItems = category?.buckets?.upcoming || [];
                  const dueSoonCount = upcomingItems.filter((item: any) => {
                    if (!item.dueDate) return false;
                    const dueDate = new Date(item.dueDate);
                    const now = new Date();
                    const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                    return daysUntilDue <= 3 && daysUntilDue >= 0;
                  }).length;
                  return dueSoonCount > 0 ? (
                    <span className="text-xs text-red-300 animate-pulse">⚠️ {dueSoonCount} Due Soon</span>
                  ) : null;
                })()}
              </button>

              <button onClick={() => { playSound('click'); navigate('/organizer/journal'); }} className="w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 border-purple-400/60 bg-purple-600/30 backdrop-blur-xl shadow-lg hover:scale-[1.02] hover:bg-purple-600/40 transition-all hover-flash-pink">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-purple-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>
                  <div className="text-left flex-1">
                    <div className="text-sm font-bold text-purple-100">Journal</div>
                    <div className="text-xs text-purple-200/60 mt-0.5">
                      {(() => {
                        const activeClass = classNotebooks.find(nb => nb.id === activeClassId);
                        return activeClass?.courseCode || activeClass?.code || activeClass?.title?.split(' - ')[0] || activeClass?.title || '';
                      })()}
                    </div>
                  </div>
                </div>
              </button>

              {/* Personal Header */}
              <div className="px-2 py-1 text-xs font-bold text-white/50 uppercase tracking-widest mt-4">
                Personal
              </div>

              <button onClick={() => openTabOverlay('home')} className="w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 border-rose-400/60 bg-rose-600/30 backdrop-blur-xl shadow-lg hover:scale-[1.02] hover:bg-rose-600/40 transition-all">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-rose-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path></svg>
                  <div className="text-left flex-1">
                    <div className="text-sm font-bold text-rose-100">Home</div>
                    <div className="text-xs text-rose-200/60 mt-0.5">Shopping, bills, recipes, maintenance</div>
                  </div>
                </div>
              </button>

              <button onClick={() => openTabOverlay('work')} className="w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 border-indigo-400/60 bg-indigo-600/30 backdrop-blur-xl shadow-lg hover:scale-[1.02] hover:bg-indigo-600/40 transition-all">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-indigo-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                  <div className="text-left flex-1">
                    <div className="text-sm font-bold text-indigo-100">Work</div>
                    <div className="text-xs text-indigo-200/60 mt-0.5">Meetings, deadlines, PTO tracking</div>
                  </div>
                </div>
              </button>

              <button onClick={() => openTabOverlay('travel')} className="w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 border-sky-400/60 bg-sky-600/30 backdrop-blur-xl shadow-lg hover:scale-[1.02] hover:bg-sky-600/40 transition-all">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-sky-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path></svg>
                  <div className="text-left flex-1">
                    <div className="text-sm font-bold text-sky-100">Travel</div>
                    <div className="text-xs text-sky-200/60 mt-0.5">Itineraries, tickets, bookings</div>
                  </div>
                </div>
              </button>

              {/* Calendar */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="text-xs font-semibold text-white/90 tracking-wide">📅 Calendar</h3>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                        calendarSyncState === 'ok'
                          ? 'bg-emerald-500/20 text-emerald-200 border-emerald-400/50'
                          : calendarSyncState === 'syncing'
                          ? 'bg-amber-500/20 text-amber-200 border-amber-400/50'
                          : calendarSyncState === 'error'
                          ? 'bg-red-500/20 text-red-200 border-red-400/50'
                          : 'bg-slate-500/20 text-slate-200 border-slate-400/50'
                      }`}
                      title={calendarSyncError || 'Calendar sync status'}
                    >
                      {getCalendarSyncStatusText()}
                    </span>
                  </div>
                  <button
                    onClick={() => handleOpenEventForm()}
                    className="px-2 py-1 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 text-xs font-semibold border border-amber-400/50 transition-colors flex items-center gap-1"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Event
                  </button>
                </div>
                <div className="flash-amber border-2 border-amber-400/50 bg-amber-600/20 backdrop-blur-xl rounded-2xl p-3 shadow-lg" style={{ touchAction: 'pan-y' }}>
                  <div className="flex items-center justify-between mb-3">
                    <button 
                      onClick={goToPreviousMonth}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-amber-200 hover:text-white hover:bg-amber-500/20 transition-all"
                      title="Previous month"
                    >
                      ‹
                    </button>
                    <button
                      onClick={goToToday}
                      className="flex-1 mx-2 text-sm font-bold text-amber-100 hover:text-white transition-colors"
                      title="Jump to today"
                    >
                      {getMonthYearString()}
                    </button>
                    <button 
                      onClick={goToNextMonth}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-amber-200 hover:text-white hover:bg-amber-500/20 transition-all"
                      title="Next month"
                    >
                      ›
                    </button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                      <div key={i} className="text-center text-[9px] text-amber-200/60 font-semibold">{d}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-[10px]" style={{ touchAction: 'pan-y' }}>
                    {generateCalendarDays().map((dayData, index) => {
                      const hasItems = dayData.items.length > 0;
                      const categoryColors: Record<string, string> = {
                        discussion: 'bg-purple-500/40',
                        assignment: 'bg-amber-500/40',
                        experiential: 'bg-green-500/40',
                        journal: 'bg-slate-500/40',
                        event: 'bg-cyan-500/40',
                        meeting: 'bg-purple-500/40',
                        appointment: 'bg-blue-500/40',
                        reminder: 'bg-green-500/40',
                        'all-day': 'bg-amber-500/40'
                      };
                      
                      // Get the first item's category color
                      const firstItemCategory = dayData.items[0]?.category;
                      const bgColor = hasItems && firstItemCategory ? (categoryColors[firstItemCategory] || 'bg-white/10') : 'bg-white/10';
                      
                      return (
                        <div
                          key={index}
                          onClick={() => {
                            if (dayData.isCurrentMonth) {
                              const now = new Date();
                              const clickedDate = new Date(now.getFullYear(), now.getMonth(), dayData.day);
                              handleOpenCalendarPopup(clickedDate);
                            }
                          }}
                          className={`aspect-square flex items-center justify-center relative rounded ${
                            !dayData.isCurrentMonth ? 'text-slate-500' :
                            dayData.isToday ? 'text-white bg-amber-600 font-bold cursor-pointer hover:brightness-110' :
                            hasItems ? `text-white ${bgColor} cursor-pointer hover:brightness-110` :
                            'text-white bg-white/10 cursor-pointer hover:bg-white/20'
                          } transition-all`}
                        >
                          {dayData.day}
                          {hasItems && (
                            <span className="absolute top-0 right-0 w-1.5 h-1.5 bg-white rounded-full shadow-lg"></span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 pt-2 border-t border-amber-400/20 text-center">
                    <p className="text-[10px] text-amber-200/80">
                      📌 {generateCalendarDays().filter(d => d.isCurrentMonth && d.items.length > 0).length} deadlines this month
                    </p>
                  </div>
                </div>
              </div>

              {/* Administrative Header */}
              <div className="px-2 py-1 text-xs font-bold text-white/50 uppercase tracking-widest mt-4">
                Administrative
              </div>

              <button onClick={() => { playSound('click'); navigate('/organizer/virtual-room'); }} className="w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 border-pink-400/60 bg-pink-600/30 backdrop-blur-xl shadow-lg hover:scale-[1.02] hover:bg-pink-600/40 transition-all hover-flash-pink">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-pink-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                  <div className="text-left flex-1">
                    <div className="text-sm font-bold text-pink-100">Virtual Room Notes</div>
                    <div className="text-xs text-pink-200/60 mt-0.5">
                      {(() => {
                        const activeClass = classNotebooks.find(nb => nb.id === activeClassId);
                        return activeClass?.courseCode || activeClass?.code || activeClass?.title?.split(' - ')[0] || activeClass?.title || '';
                      })()}
                    </div>
                  </div>
                </div>
              </button>

              {/* PRACTICE ROOM BUTTON */}
              <button onClick={() => { playSound('click'); setShowPracticeRoom(true); }} className="w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 border-cyan-400/60 bg-cyan-600/30 backdrop-blur-xl shadow-lg hover:scale-[1.02] hover:bg-cyan-600/40 transition-all">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-cyan-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>
                  <div className="text-left flex-1">
                    <div className="text-sm font-bold text-cyan-100">🧪 Practice Video Room</div>
                    <div className="text-xs text-cyan-200/60 mt-0.5">Test keyboard behavior</div>
                  </div>
                </div>
              </button>

            </div>

            {/* Document Hub - REBUILT */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-white tracking-wide">📁 Document Hub</h3>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    playSound('click');
                    setShowAllDocuments(!showAllDocuments);
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  style={{ pointerEvents: 'auto', zIndex: 9999, position: 'relative' }}
                  className="px-3 py-1 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-200 text-xs font-semibold active:bg-green-500/40 cursor-pointer border-2 border-green-500"
                >
                  {showAllDocuments ? 'Hide' : 'View All'}
                </button>
              </div>
              <div className="border-2 border-green-400/50 bg-green-600/20 rounded-xl p-4">
                {!documents || documents.length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-sm text-green-200/60">No documents yet</p>
                    <p className="text-xs text-green-300/40 mt-1">Upload files to see them here</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(showAllDocuments ? documents : documents.slice(0, 3)).map((doc) => {
                      const isUploadedFile = doc.content && typeof doc.content === 'string' && doc.content.startsWith('data:');
                      
                      return (
                        <button 
                          key={doc.id}
                          type="button"
                          onTouchStart={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
                          }}
                          onTouchEnd={(e) => {
                            e.currentTarget.style.backgroundColor = '';
                            e.preventDefault();
                            e.stopPropagation();
                            
                            alert(`Touch: ${doc.title}\nType: ${isUploadedFile ? 'FILE' : 'DOC'}\nWill navigate: ${!isUploadedFile}`);
                            
                            playSound('click');
                            
                            if (isUploadedFile) {
                              alert('Opening file...');
                              // Open uploaded file in new tab
                              try {
                                const base64Data = doc.content.split(',')[1];
                                const mimeType = doc.content.split(',')[0].split(':')[1].split(';')[0];
                                const byteCharacters = atob(base64Data);
                                const byteNumbers = new Array(byteCharacters.length);
                                for (let i = 0; i < byteCharacters.length; i++) {
                                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                                }
                                const byteArray = new Uint8Array(byteNumbers);
                                const blob = new Blob([byteArray], { type: mimeType });
                                const blobUrl = URL.createObjectURL(blob);
                                const opened = window.open(blobUrl, '_blank');
                                if (!opened) alert('Pop-up blocked! Allow pop-ups to view files.');
                                setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
                              } catch (error) {
                                alert('Error opening file: ' + error.message);
                              }
                            } else {
                              alert('Navigating to Documents Hub...');
                              // Navigate to Documents Hub page
                              navigate('/organizer/documents');
                            }
                          }}
                          className="w-full flex items-center justify-between hover:bg-white/10 active:bg-white/20 p-3 rounded-lg transition-colors text-left"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="text-lg">{isUploadedFile ? '📎' : '📄'}</span>
                            <span className="text-sm text-green-100 truncate font-medium">{doc.title || 'Untitled'}</span>
                          </div>
                          <span className="text-xs text-white/50 ml-2 flex-shrink-0">
                            {doc.created_at 
                              ? new Date(doc.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                              : 'New'}
                          </span>
                        </button>
                      );
                    })}
                    {documents.length > 3 && (
                      <div className="text-center pt-2 border-t border-white/10">
                        <span className="text-xs text-green-300">
                          +{documents.length - 3} more document{documents.length - 3 !== 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Upload Center */}
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-white/90 tracking-wide">📤 Upload Center</h3>
              <button onClick={() => setShowUploadPanel(true)} className="w-full border-2 border-dashed border-blue-400/50 bg-blue-600/15 backdrop-blur-xl rounded-xl p-4 hover:bg-blue-600/25 transition-all shadow-lg">
                <div className="flex flex-col items-center gap-1">
                  <svg className="w-6 h-6 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
                  <span className="text-xs text-blue-100 font-semibold">Tap to upload files</span>
                  <span className="text-[10px] text-white/60">PDF, DOCX, TXT • Max 10MB</span>
                </div>
              </button>
            </div>

            {/* WHIPT Transcripts */}
            <div className="pulse-glow border-2 border-purple-400/50 bg-purple-600/20 backdrop-blur-xl rounded-2xl p-4 shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                    <span className="text-2xl">🎤</span>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white">WHIPT Transcripts</h3>
                    <p className="text-xs text-purple-100">Audio/Video analysis</p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    playSound('click');
                    setShowTranscriptsPanel(true);
                  }}
                  className="px-3 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-bold hover:brightness-110 transition-all"
                >
                  Open
                </button>
              </div>
            </div>

            {/* Session Recordings Button */}
            <div className="mx-4 mt-2">
              <button
                onClick={() => {
                  playSound('click');
                  setShowRecordingsPanel(true);
                }}
                className="w-full flex items-center justify-between p-4 rounded-2xl border-2 border-rose-400/40 bg-gradient-to-br from-rose-900/50 to-pink-900/40 shadow-lg active:scale-[0.98] transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg">
                    <span className="text-xl">📹</span>
                  </div>
                  <div className="text-left">
                    <h3 className="text-base font-bold text-white">Session Recordings</h3>
                    <p className="text-xs text-rose-100">Play, download, share recorded sessions</p>
                  </div>
                </div>
                <button className="px-3 py-2 rounded-lg bg-gradient-to-r from-rose-600 to-pink-600 text-white text-sm font-bold hover:brightness-110 transition-all">
                  Open
                </button>
              </button>
            </div>

            {/* Unified Virtual Room Card */}
            <UnifiedVirtualRoomCard
              onLaunchRoom={handleLaunchRoom}
              onCopyLink={handleCopyLink}
              onSchedule={handleScheduleMeeting}
              onAgenda={handleCreateAgenda}
              onAddToCalendar={handleAddToCalendar}
              playSound={playSound}
            />



            {/* Video Advertisement - WHIPT Commercial */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold text-white/90 tracking-wide">📢 Sponsored</h3>
              </div>
              <WhiptVideoAd autoPlay={true} />
            </div>

            {/* Health & Fitness */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-white/90 tracking-wide">💪 Health & Fitness</h3>
                <span className="px-2 py-1 bg-yellow-500/30 text-yellow-100 text-[10px] font-bold rounded-full border border-yellow-400/40">COMING SOON</span>
              </div>
              <div className="border-2 border-rose-400/50 bg-rose-600/20 backdrop-blur-xl rounded-xl p-4 shadow-lg opacity-60">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-rose-100">🏃 Steps Today</span>
                    <span className="text-white font-bold">8,234 / 10,000</span>
                  </div>
                  <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-rose-400 to-rose-600" style={{width: '82%'}}></div>
                  </div>
                  <button className="w-full mt-2 px-3 py-2 bg-rose-500/30 rounded-lg text-sm text-rose-100 font-semibold hover:bg-rose-500/40 transition-all">View Details</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Music Player Dock - Independent of all panels */}
      <MusicPlayerDock hideTab={showSnackPanel || showAIPanel || showTodoList} />

      {/* 
        ⚠️ CRITICAL: DO NOT COMMENT OUT THE LINE BELOW ⚠️
        To-Do List PIP Widget - MUST REMAIN ENABLED
        This has been accidentally commented out multiple times.
        If you need to disable it, use a feature flag instead.
      */}
      <TodoListPIP isOpen={showTodoList} onClose={() => setShowTodoList(false)} />

      {/* Late Night Snack Panel - Slides from right */}
      <div 
        className="fixed top-16 right-0 bottom-0 w-full max-w-md z-50 transition-transform duration-300 ease-in-out"
        style={{ transform: showSnackPanel ? 'translateX(0)' : 'translateX(100%)' }}
      >
        <div className="h-full bg-slate-900 shadow-2xl flex flex-col">
          <div className="bg-gradient-to-r from-orange-600 to-red-600 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
                <path d="M7 2v20" />
                <path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
              </svg>
              Snack Dial
            </h2>
            <button onClick={() => setShowSnackPanel(false)} className="text-white hover:bg-white/20 p-1 rounded">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <LateNightSnackPanel 
              spots={snackSpots}
              loading={snackLoading}
              onCreate={handleCreateSnackSpot}
              onUpdate={handleUpdateSnackSpot}
              onDelete={handleDeleteSnackSpot}
              onMarkOrdered={handleMarkSnackOrdered}
            />
          </div>
        </div>
      </div>

      {/* Backdrop for panels */}
      {(showSnackPanel || showAIPanel || showBudgetAssistant || showAppsPanel) && (
        <div 
          onClick={() => {
            setShowSnackPanel(false);
            setShowAIPanel(false);
            setShowBudgetAssistant(false);
            setShowAppsPanel(false);
          }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
        />
      )}

      {/* AI Assistant Panel */}
      <OrganizerAIPanel 
        isOpen={showAIPanel} 
        onClose={() => setShowAIPanel(false)} 
      />

      {/* Budget Assistant Panel - Slides from right */}
      <div 
        className="fixed top-16 right-0 bottom-0 w-full max-w-md z-50 transition-transform duration-300 ease-in-out"
        style={{ transform: showBudgetAssistant ? 'translateX(0)' : 'translateX(100%)' }}
      >
        <div className="h-full bg-gradient-to-br from-emerald-900 to-green-950 shadow-2xl flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-emerald-700">
            <h2 className="text-xl font-bold text-white">💰 Budget Assistant</h2>
            <button onClick={() => setShowBudgetAssistant(false)} className="text-white hover:bg-white/20 p-1 rounded">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <BudgetAssistant onClose={() => setShowBudgetAssistant(false)} />
          </div>
        </div>
      </div>

      {/* Apps Integration Panel - Slides from right */}
      <div 
        className="fixed top-16 right-0 bottom-0 w-full max-w-md z-50 transition-transform duration-300 ease-in-out"
        style={{ transform: showAppsPanel ? 'translateX(0)' : 'translateX(100%)' }}
      >
        <div className="h-full bg-gradient-to-br from-cyan-900 to-blue-950 shadow-2xl flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-cyan-700">
            <h2 className="text-xl font-bold text-white">🔗 App Integrations</h2>
            <button onClick={() => setShowAppsPanel(false)} className="text-white hover:bg-white/20 p-1 rounded">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <AppsIntegrationPanel onClose={() => setShowAppsPanel(false)} />
          </div>
        </div>
      </div>

      {/* Suggestions Panel */}
      <SuggestionBox 
        isOpen={showSuggestions} 
        onClose={() => setShowSuggestions(false)} 
      />

      {/* Calendar Import Modal */}
      {/* Event Form Popup */}
      {showEventForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-end justify-center z-[100] p-0" onClick={() => setShowEventForm(false)}>
          <div 
            className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-t-3xl w-full max-w-2xl shadow-2xl border-t-2 border-amber-400/50 max-h-[90vh] overflow-y-auto" 
            onClick={(e) => e.stopPropagation()}
            style={{ animation: 'slideUp 0.3s ease-out' }}
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-br from-slate-900 to-slate-800 border-b border-white/10 p-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">
                {editingEvent ? '✏️ Edit Event' : '➕ Add Event'}
              </h3>
              <button
                onClick={() => setShowEventForm(false)}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Form */}
            <div className="p-4 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Title *</label>
                <input
                  type="text"
                  value={eventFormData.title}
                  onChange={(e) => setEventFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Team Meeting, Doctor Appointment, etc."
                  className="w-full px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-600/50 text-white placeholder-slate-500 focus:outline-none focus:border-amber-400/50"
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Type</label>
                <select
                  value={eventFormData.type}
                  onChange={(e) => setEventFormData(prev => ({ ...prev, type: e.target.value as any }))}
                  className="w-full px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-600/50 text-white focus:outline-none focus:border-amber-400/50"
                >
                  <option value="meeting">🤝 Meeting</option>
                  <option value="appointment">🏥 Appointment</option>
                  <option value="reminder">🔔 Reminder</option>
                  <option value="all-day">📅 All-day Event</option>
                </select>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Date *</label>
                  <input
                    type="date"
                    value={eventFormData.date}
                    onChange={(e) => setEventFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-600/50 text-white focus:outline-none focus:border-amber-400/50"
                  />
                </div>
                {eventFormData.type !== 'all-day' && (
                  <div>
                    <label className="block text-sm font-semibold text-white mb-2">Time</label>
                    <input
                      type="time"
                      value={eventFormData.time}
                      onChange={(e) => setEventFormData(prev => ({ ...prev, time: e.target.value }))}
                      className="w-full px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-600/50 text-white focus:outline-none focus:border-amber-400/50"
                    />
                  </div>
                )}
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">📍 Location (optional)</label>
                <input
                  type="text"
                  value={eventFormData.location}
                  onChange={(e) => setEventFormData(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Office, Zoom link, address..."
                  className="w-full px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-600/50 text-white placeholder-slate-500 focus:outline-none focus:border-amber-400/50"
                />
              </div>

              {/* Attendees */}
              {eventFormData.type === 'meeting' && (
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">👥 Attendees (optional)</label>
                  <input
                    type="text"
                    value={eventFormData.attendees}
                    onChange={(e) => setEventFormData(prev => ({ ...prev, attendees: e.target.value }))}
                    placeholder="John, Sarah, team@company.com..."
                    className="w-full px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-600/50 text-white placeholder-slate-500 focus:outline-none focus:border-amber-400/50"
                  />
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">📝 Notes (optional)</label>
                <textarea
                  value={eventFormData.notes}
                  onChange={(e) => setEventFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional details..."
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-600/50 text-white placeholder-slate-500 focus:outline-none focus:border-amber-400/50 resize-none"
                />
              </div>

              {/* Reminder */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">🔔 Reminder</label>
                <select
                  value={eventFormData.reminder}
                  onChange={(e) => setEventFormData(prev => ({ ...prev, reminder: e.target.value }))}
                  className="w-full px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-600/50 text-white focus:outline-none focus:border-amber-400/50"
                >
                  <option value="none">No reminder</option>
                  <option value="15min">15 minutes before</option>
                  <option value="1hour">1 hour before</option>
                  <option value="1day">1 day before</option>
                </select>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Category</label>
                <select
                  value={eventFormData.category}
                  onChange={(e) => setEventFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-600/50 text-white focus:outline-none focus:border-amber-400/50"
                >
                  <option value="personal">Personal</option>
                  <option value="work">Work</option>
                  <option value="home">Home</option>
                  <option value="travel">Travel</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowEventForm(false)}
                  className="flex-1 px-4 py-3 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEvent}
                  className="flex-1 px-4 py-3 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold transition-colors shadow-lg"
                >
                  {editingEvent ? 'Update Event' : 'Save Event'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CalendarImportModal
        isOpen={showCalendarImport}
        onClose={() => setShowCalendarImport(false)}
        onEventsImported={async (events) => {
          setShowCalendarImport(false);
          setImportedEvents(events);
          setShowEventAssignment(true);
        }}
        mode="student"
      />

      {/* Event Assignment Modal (mobile) */}
      {showEventAssignment && (
        <EventAssignmentModal
          isOpen={showEventAssignment}
          onClose={() => setShowEventAssignment(false)}
          events={importedEvents}
          notebooks={classNotebooks.map(nb => ({ id: nb.id, name: nb.title }))}
          onAssign={async (assignments) => {
            setShowEventAssignment(false);
            setImportedEvents([]);
            // Save assigned events to calendar_events table and notebooks
            if (user?.id && assignments.length > 0) {
              let savedCount = 0;
              for (const assignment of assignments) {
                const event = assignment.event;
                try {
                  await createCalendarEvent({
                    user_id: user.id,
                    title: event.title,
                    date: event.startDate.toISOString(),
                    startTime: event.startDate.toLocaleTimeString(),
                    endTime: event.endDate.toLocaleTimeString(),
                    location: event.location,
                    description: event.description,
                    category: assignment.category,
                    course: event.courseCode || event.source,
                  });
                  savedCount++;

                  // Also add to the notebook
                  if (assignment.notebookId) {
                    setClassNotebooks((prev) => {
                      const updated = prev.map((notebook) => {
                        if (notebook.id !== assignment.notebookId) return notebook;
                        return {
                          ...notebook,
                          categories: notebook.categories.map((cat: any) => {
                            if (cat.id !== assignment.category) return cat;
                            const bucket = cat.buckets?.upcoming || [];
                            return {
                              ...cat,
                              buckets: {
                                ...cat.buckets,
                                upcoming: [
                                  ...bucket,
                                  {
                                    id: `event_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                                    title: event.title,
                                    description: event.description,
                                    dueDate: event.startDate.toISOString(),
                                    location: event.location,
                                    completed: false,
                                    createdAt: new Date().toISOString(),
                                  },
                                ],
                              },
                            };
                          }),
                        };
                      });
                      saveNotebooks(String(user.id), updated);
                      return updated;
                    });
                  }
                } catch (err) {
                  console.warn('Failed to save imported event:', event.title, err);
                }
              }
              console.log(`📅 Saved ${savedCount}/${assignments.length} imported events`);
            }
            loadCalendarEvents();
          }}
        />
      )}

      {/* Category Selector for Adding Schoolwork */}
      {showCategorySelector && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowCategorySelector(false)}>
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl border border-white/10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Add Schoolwork</h3>
              <button onClick={() => setShowCategorySelector(false)} className="text-white/70 hover:text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-white/60 text-sm mb-6">Choose a category for your new work:</p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowCategorySelector(false);
                  openTabOverlay('discussion');
                }}
                className="w-full flex items-center gap-3 px-4 py-4 rounded-xl border-2 border-blue-400/60 bg-blue-600/30 hover:bg-blue-600/40 transition-all"
              >
                <svg className="w-6 h-6 text-blue-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span className="text-base font-semibold text-blue-100">Discussion</span>
              </button>
              <button
                onClick={() => {
                  setShowCategorySelector(false);
                  openTabOverlay('assignment');
                }}
                className="w-full flex items-center gap-3 px-4 py-4 rounded-xl border-2 border-amber-400/60 bg-amber-600/30 hover:bg-amber-600/40 transition-all"
              >
                <svg className="w-6 h-6 text-amber-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-base font-semibold text-amber-100">Assignment</span>
              </button>
              <button
                onClick={() => {
                  setShowCategorySelector(false);
                  openTabOverlay('experiential');
                }}
                className="w-full flex items-center gap-3 px-4 py-4 rounded-xl border-2 border-emerald-400/60 bg-emerald-600/30 hover:bg-emerald-600/40 transition-all"
              >
                <svg className="w-6 h-6 text-emerald-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
                <span className="text-base font-semibold text-emerald-100">Experiential</span>
              </button>
              <button
                onClick={() => {
                  setShowCategorySelector(false);
                  openTabOverlay('journal');
                }}
                className="w-full flex items-center gap-3 px-4 py-4 rounded-xl border-2 border-purple-400/60 bg-purple-600/30 hover:bg-purple-600/40 transition-all"
              >
                <svg className="w-6 h-6 text-purple-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <span className="text-base font-semibold text-purple-100">Journal</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Virtual Room Modal */}
      {virtualRoomOpen && virtualRoomContext && (() => {
        const activeClass = classNotebooks.find(nb => nb.id === activeClassId);
        // Get agenda for this room category (if exists)
        const roomCategory = virtualRoomContext.id;
        const agendaText = roomAgendas[roomCategory] || '';
        const parsedAgenda = parseAgenda(agendaText);
        
        return (
          <MobileVirtualRoomWrapper
            tabLabel={virtualRoomContext.tabLabel}
            roomTitle={virtualRoomContext.roomTitle}
            description={virtualRoomContext.description}
            classId={activeClass?.id || activeClassId}
            tabId={virtualRoomContext.id}
            roomId={virtualRoomContext.id}
            participants={virtualRoomContext.participants}
            snackSpots={snackSpots}
            snackLoading={snackLoading}
            onCreateSnack={handleCreateSnackSpot}
            onUpdateSnack={handleUpdateSnackSpot}
            onDeleteSnack={handleDeleteSnackSpot}
            onMarkSnackOrdered={handleMarkSnackOrdered}
            agenda={parsedAgenda}
            minimized={virtualRoomMinimized}
            onMinimize={() => setVirtualRoomMinimized(!virtualRoomMinimized)}
            onClose={() => {
              setVirtualRoomOpen(false);
              setVirtualRoomMinimized(false);
              setVirtualRoomContext(null);
            }}
          />
        );
      })()}

      {/* Add/Edit Class Modal */}
      {showAddClassModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[70] flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-indigo-900/95 to-slate-900/95 border-2 border-indigo-400/50 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">
              {editingClassId ? '✏️ Edit Class' : '📚 Add New Class'}
            </h3>
            <input
              type="text"
              value={newClassName}
              onChange={(e) => setNewClassName(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/20 text-white text-base mb-4 focus:outline-none focus:border-white/40"
              placeholder="Enter class name (e.g., PSY 101)..."
              autoFocus
              onKeyPress={(e) => e.key === 'Enter' && handleAddClass()}
            />
            
            {/* Professor Name */}
            <input
              type="text"
              value={newProfessorName}
              onChange={(e) => setNewProfessorName(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/20 text-white text-base mb-4 focus:outline-none focus:border-white/40"
              placeholder="Professor name (optional)..."
              onKeyPress={(e) => e.key === 'Enter' && handleAddClass()}
            />
            
            {/* Teacher Photo Upload */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-indigo-200 mb-2">
                👨‍🏫 Teacher Photo (Optional)
              </label>
              <div className="flex items-center gap-4">
                {newTeacherPhoto ? (
                  <div className="relative">
                    <img 
                      src={newTeacherPhoto} 
                      alt="Teacher" 
                      className="w-16 h-16 rounded-full object-cover border-2 border-indigo-400/50"
                    />
                    <button
                      onClick={() => setNewTeacherPhoto('')}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs hover:bg-red-600"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-full bg-indigo-500/20 border-2 border-dashed border-indigo-400/40 flex items-center justify-center">
                    <span className="text-2xl">👤</span>
                  </div>
                )}
                <label className="flex-1 px-4 py-2 rounded-lg bg-indigo-600/30 border border-indigo-400/30 text-indigo-200 text-sm font-medium hover:bg-indigo-600/40 cursor-pointer text-center transition-all">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleTeacherPhotoUpload}
                    className="hidden"
                  />
                  {newTeacherPhoto ? 'Change Photo' : 'Upload Photo'}
                </label>
              </div>
              <p className="text-xs text-slate-400 mt-2">Max 2MB • Will be resized to 200×200px</p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAddClassModal(false);
                  setEditingClassId(null);
                  setNewClassName('');
                  setNewProfessorName('');
                  setNewTeacherPhoto('');
                }}
                className="flex-1 px-4 py-3 rounded-lg bg-white/10 hover:bg-white/20 text-white font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleAddClass}
                className="flex-1 px-4 py-3 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:opacity-80 text-white font-semibold transition-all"
              >
                {editingClassId ? 'Save Changes' : 'Add Class'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Create New Semester Modal */}
      {showSemesterModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[70] flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-purple-900/95 to-indigo-900/95 border-2 border-purple-400/50 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">
              🗓️ Create New Semester
            </h3>
            
            <div className="space-y-4">
              {/* Semester Selection */}
              <div>
                <label className="block text-sm font-semibold text-purple-200 mb-2">
                  Semester
                </label>
                <select
                  value={newSemesterName}
                  onChange={(e) => setNewSemesterName(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/20 text-white text-base focus:outline-none focus:border-white/40"
                >
                  <option value="Spring">Spring</option>
                  <option value="Summer">Summer</option>
                  <option value="Fall">Fall</option>
                  <option value="Winter">Winter</option>
                </select>
              </div>

              {/* Year Selection */}
              <div>
                <label className="block text-sm font-semibold text-purple-200 mb-2">
                  Year
                </label>
                <input
                  type="number"
                  value={newSemesterYear}
                  onChange={(e) => setNewSemesterYear(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/20 text-white text-base focus:outline-none focus:border-white/40"
                  placeholder="2026"
                  min="2020"
                  max="2030"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowSemesterModal(false);
                  setNewSemesterName('Spring');
                  setNewSemesterYear(new Date().getFullYear().toString());
                }}
                className="flex-1 px-4 py-3 rounded-lg bg-white/10 hover:bg-white/20 text-white font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const newSemester = `${newSemesterName} ${newSemesterYear}`;
                  setActiveSemester(newSemester);
                  localStorage.setItem('whipt_active_semester', newSemester);
                  setShowSemesterModal(false);
                  setNewSemesterName('Spring');
                  setNewSemesterYear(new Date().getFullYear().toString());
                  playSound('success');
                }}
                className="flex-1 px-4 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:opacity-80 text-white font-semibold transition-all"
              >
                Create Semester
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trash Modal */}
      {showTrash && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[70] flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-red-900/95 to-orange-900/95 border-2 border-red-400/50 rounded-2xl p-6 max-w-md w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">🗑️ Trash</h3>
              <button
                onClick={() => setShowTrash(false)}
                className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
              >
                ✕
              </button>
            </div>
            
            {deletedItems.length > 0 ? (
              <div className="space-y-3 overflow-y-auto flex-1">
                {deletedItems.map((item) => (
                  <div 
                    key={item.id}
                    className="bg-black/30 border border-white/20 rounded-xl p-4"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-semibold text-white line-through">{item.title}</h4>
                        <span className="text-[10px] text-white/50">
                          Deleted {new Date(item.deletedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRestoreItem(item)}
                      className="w-full mt-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-all"
                    >
                      ↩️ Restore
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">🗑️</div>
                <p className="text-lg font-semibold text-white mb-2">Trash is Empty</p>
                <p className="text-sm text-white/60">Deleted items will appear here</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Test Notification Results Modal - WHIPT Themed */}
      {showTestNotificationModal && (
        <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#050309] to-[#0a0612] border-2 border-orange-400/30 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="text-3xl">🔔</span>
                <h3 className="text-xl font-bold text-orange-100">Notification Test</h3>
              </div>
              <button 
                onClick={() => setShowTestNotificationModal(false)} 
                className="text-slate-400 hover:text-white text-2xl"
              >
                ✕
              </button>
            </div>
            
            {testNotificationResults.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">✅</div>
                <p className="text-lg font-bold text-green-100 mb-2">All Caught Up!</p>
                <p className="text-sm text-slate-300">No items due in the next 24 hours 🎉</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-orange-200 mb-4">
                  Found {testNotificationResults.length} item{testNotificationResults.length !== 1 ? 's' : ''} due soon:
                </p>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {testNotificationResults.map((item, idx) => {
                    const emoji = item.categoryLabel.toLowerCase().includes('assignment') ? '📝' :
                                 item.categoryLabel.toLowerCase().includes('discussion') ? '💬' :
                                 item.categoryLabel.toLowerCase().includes('experiential') ? '🧪' : '📚';
                    const isOverdue = item.isOverdue;
                    const timeText = isOverdue 
                      ? `${item.daysOverdue} day${item.daysOverdue !== 1 ? 's' : ''} overdue` 
                      : item.hoursUntilDue <= 1 ? 'Less than 1 hour' : `${item.hoursUntilDue} hours`;
                    
                    return (
                      <div key={idx} className={`bg-black/40 border rounded-lg p-3 ${isOverdue ? 'border-red-500/40' : 'border-orange-400/20'}`}>
                        <div className="flex items-start gap-2">
                          <span className="text-xl flex-shrink-0">{isOverdue ? '⚠️' : emoji}</span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-bold ${isOverdue ? 'text-red-200' : 'text-orange-100'}`}>{item.classTitle}</p>
                            <p className="text-xs text-white mt-1">{item.title}</p>
                            <p className={`text-xs mt-1 ${isOverdue ? 'text-red-300' : 'text-orange-300/80'}`}>
                              {isOverdue ? '⚠️' : '⏰'} {isOverdue ? 'OVERDUE' : 'Due in'}: {timeText}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button
                  onClick={() => {
                    // Trigger actual push notifications
                    testNotificationResults.forEach(item => {
                      const emoji = item.categoryLabel.toLowerCase().includes('assignment') ? '📝' :
                                   item.categoryLabel.toLowerCase().includes('discussion') ? '💬' :
                                   item.categoryLabel.toLowerCase().includes('experiential') ? '🧪' : '📚';
                      const timeText = item.hoursUntilDue <= 1 ? 'in less than 1 hour' : `in ${item.hoursUntilDue} hours`;
                      setTimeout(() => {
                        if (window.Notification && Notification.permission === 'granted') {
                          new Notification(`${emoji} Due Soon: ${item.classTitle}`, {
                            body: `${item.title}\nDue ${timeText}`,
                            icon: '/whipt-icon.png',
                            vibrate: [200, 100, 200],
                          });
                        }
                      }, idx * 500); // Stagger notifications by 0.5s
                    });
                    setShowTestNotificationModal(false);
                  }}
                  className="w-full mt-4 px-4 py-3 rounded-lg bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold hover:from-orange-600 hover:to-red-600 transition-all"
                >
                  Send Push Notifications
                </button>
              </div>
            )}
            
            <button
              onClick={() => setShowTestNotificationModal(false)}
              className="w-full mt-3 px-4 py-2 rounded-lg bg-white/10 text-slate-300 hover:bg-white/20 text-sm transition-all"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Calendar Popup - Today's Items */}
      {showCalendarPopup && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-2 border-amber-400/60 rounded-2xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-600/20 to-orange-600/20 border-b border-amber-400/30 p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-bold text-amber-100">
                  📅 {selectedDate && selectedDate.toDateString() === new Date().toDateString() ? "Today's Schedule" : "Schedule"}
                </h3>
                <button
                  onClick={() => setShowCalendarPopup(false)}
                  className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                >
                  ✕
                </button>
              </div>
              <p className="text-sm text-amber-200/80">
                {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>

            {/* Items List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {calendarPopupItems.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-6xl mb-3">🎉</div>
                  <p className="text-lg font-semibold text-white mb-2">
                    {selectedDate && selectedDate.toDateString() === new Date().toDateString() 
                      ? "No items due today!" 
                      : "No items due on this date!"}
                  </p>
                  <p className="text-sm text-slate-400">
                    {selectedDate && selectedDate.toDateString() === new Date().toDateString() 
                      ? "Enjoy your free time" 
                      : "This day is clear"}
                  </p>
                </div>
              ) : (
                calendarPopupItems.map((item, idx) => {
                  const isCompleted = item.status === 'completed';
                  const isCalendarEvent = item.isCalendarEvent;
                  const categoryColors: Record<string, string> = {
                    discussion: 'from-purple-500 to-blue-500',
                    assignment: 'from-amber-500 to-orange-500',
                    experiential: 'from-green-500 to-emerald-500',
                    journal: 'from-slate-500 to-slate-600',
                    event: 'from-cyan-500 to-blue-500',
                    meeting: 'from-purple-500 to-pink-500',
                    appointment: 'from-blue-500 to-indigo-500',
                    reminder: 'from-green-500 to-teal-500',
                    'all-day': 'from-amber-500 to-yellow-500'
                  };
                  const gradient = categoryColors[item.category] || 'from-purple-500 to-blue-500';

                  return (
                    <div
                      key={idx}
                      className={`bg-black/40 border border-white/20 rounded-xl p-4 ${isCompleted ? 'opacity-60' : ''} hover:border-white/40 transition-all cursor-pointer`}
                      onClick={() => {
                        if (!isCalendarEvent) {
                          // For notebook items, navigate to the class and open the item
                          const notebook = classNotebooks.find(nb => nb.title === item.course);
                          if (notebook) {
                            setActiveClassId(notebook.id);
                            setActiveTab(item.category);
                            setShowCalendarPopup(false);
                            playSound('click');
                          }
                        }
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`}>
                          <span className="text-white text-lg">
                            {isCalendarEvent ? (
                              item.category === 'meeting' ? '🤝' :
                              item.category === 'appointment' ? '🏥' :
                              item.category === 'reminder' ? '🔔' :
                              item.category === 'all-day' ? '📅' : '📆'
                            ) : (
                              item.category === 'discussion' ? '💬' : 
                              item.category === 'assignment' ? '📝' : 
                              item.category === 'experiential' ? '🎯' : '📓'
                            )}
                          </span>
                        </div>

                        <div className="flex-1 min-w-0">
                          <h4 className={`font-semibold text-base mb-1 ${isCompleted ? 'line-through text-emerald-300/70' : 'text-white'}`}>
                            {item.title}
                          </h4>
                          
                          {/* Only show course name if it's NOT a personal organizer event (WORK/HOME/TRAVEL) */}
                          {item.course && !['WORK', 'HOME', 'TRAVEL'].includes(item.course) && (
                            <p className="text-xs text-purple-300 mb-1">📁 {item.course}</p>
                          )}

                          {item.description && (
                            <p className="text-sm text-slate-400 mb-2">{item.description}</p>
                          )}

                          <div className="flex items-center gap-2 text-xs flex-wrap">
                            <span className={`px-2 py-0.5 rounded-full bg-gradient-to-r ${gradient} text-white font-semibold`}>
                              {item.categoryLabel}
                            </span>
                            {isCompleted && (
                              <span className="text-emerald-400">✓ Completed</span>
                            )}
                          </div>

                          {/* Action buttons */}
                          {!isCompleted && (
                            <div className="flex gap-2 mt-3">
                              {isCalendarEvent ? (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditEvent(item);
                                      setShowCalendarPopup(false);
                                    }}
                                    className="flex-1 px-3 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 text-xs font-semibold border border-blue-400/50 transition-colors flex items-center justify-center gap-1"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    Edit
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteEvent(item.id);
                                    }}
                                    className="flex-1 px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 text-xs font-semibold border border-red-400/50 transition-colors flex items-center justify-center gap-1"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    Delete
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Mark as complete
                                      const notebook = classNotebooks.find(nb => nb.title === item.course);
                                      if (notebook) {
                                        handleCompleteItem(notebook.id, item.category, item.id);
                                        setShowCalendarPopup(false);
                                        playSound('success');
                                      }
                                    }}
                                    className="flex-1 px-3 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 text-xs font-semibold border border-emerald-400/50 transition-colors flex items-center justify-center gap-1"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    Complete
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Navigate to the class to view/edit
                                      const notebook = classNotebooks.find(nb => nb.title === item.course);
                                      if (notebook) {
                                        setActiveClassId(notebook.id);
                                        setActiveTab(item.category);
                                        setShowCalendarPopup(false);
                                        playSound('click');
                                      }
                                    }}
                                    className="flex-1 px-3 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 text-xs font-semibold border border-blue-400/50 transition-colors flex items-center justify-center gap-1"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                    View
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer Buttons */}
            <div className="border-t border-white/10 p-4 space-y-2">
              <button
                onClick={() => {
                  handleOpenEventForm(selectedDate || undefined);
                  setShowCalendarPopup(false);
                }}
                className="w-full px-4 py-2.5 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-200 font-semibold border border-green-400/50 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Add Event to This Day
              </button>
              <button
                onClick={() => {
                  setShowCalendarPopup(false);
                  navigate('/organizer/calendar');
                }}
                className="w-full px-4 py-3 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-slate-900 font-bold hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg"
              >
                📅 View Full Calendar
              </button>
              
              <button
                onClick={() => setShowCalendarPopup(false)}
                className="w-full px-4 py-2 rounded-lg bg-white/10 text-slate-300 hover:bg-white/20 text-sm transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Center Modal */}
      {showUploadPanel && (() => {
        const activeClass = classNotebooks.find(nb => nb.id === activeClassId);
        if (!activeClass) return null;
        
        return (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4">
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-2 border-blue-400/60 rounded-2xl max-w-md w-full overflow-hidden flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border-b border-blue-400/30 p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-bold text-blue-100">📤 Upload Center</h3>
                  <button
                    onClick={() => {
                      setShowUploadPanel(false);
                      setUploadTabSelection('');
                    }}
                    className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                  >
                    ✕
                  </button>
                </div>
                <p className="text-sm text-blue-200/80">Upload PDFs, Word docs, or text files</p>
              </div>

              {/* Content */}
              <div className="p-4 space-y-4">
                {/* Tab Selection */}
                <div>
                  <label className="text-sm text-slate-300 mb-2 block">Upload to which category?</label>
                  <select 
                    value={uploadTabSelection}
                    onChange={(e) => setUploadTabSelection(e.target.value)}
                    className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-400"
                  >
                    <option value="">Select a category...</option>
                    {activeClass.categories?.filter((c: any) => c.id !== 'snack').map((cat: any) => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                {/* File Input */}
                <div className="border-2 border-dashed border-blue-400/30 rounded-xl p-6 text-center bg-blue-500/5">
                  <svg className="w-12 h-12 text-blue-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                  </svg>
                  <p className="text-blue-200 mb-2 text-sm">Tap to select files</p>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.txt"
                    className="hidden"
                    id="mobile-file-upload"
                    onChange={(e) => {
                      if (e.target.files && uploadTabSelection) {
                        handleFileUpload(e.target.files);
                      } else if (!uploadTabSelection) {
                        alert('Please select a category first');
                      }
                    }}
                    disabled={uploadingFiles}
                  />
                  <label
                    htmlFor="mobile-file-upload"
                    className={`inline-block px-4 py-2 rounded-lg text-sm ${
                      uploadingFiles 
                        ? 'bg-gray-500/20 text-gray-400' 
                        : 'bg-blue-500/20 text-blue-200 hover:bg-blue-500/30'
                    } transition-colors`}
                  >
                    {uploadingFiles ? 'Uploading...' : 'Choose Files'}
                  </label>
                  <p className="text-xs text-slate-400 mt-3">
                    PDF, DOC, DOCX, TXT • Max 10MB per file
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-white/10 p-4">
                <button
                  onClick={() => {
                    setShowUploadPanel(false);
                    setUploadTabSelection('');
                  }}
                  className="w-full px-4 py-2 rounded-lg bg-white/10 text-slate-300 hover:bg-white/20 text-sm transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Tab Template Modal */}
      {showTabTemplateModal && (
        <TabTemplateModal
          isOpen={showTabTemplateModal}
          onClose={() => setShowTabTemplateModal(false)}
          onSelect={handleTemplateSelected}
        />
      )}

      {/* Schedule Meeting Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#050309] border border-purple-400/30 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Schedule Meeting</h3>
              <button onClick={() => setShowScheduleModal(false)} className="text-slate-400 hover:text-white text-2xl">
                ×
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Meeting Title</label>
                <input
                  id="mobile-schedule-title"
                  type="text"
                  defaultValue={scheduleForRoom ? scheduleForRoom.charAt(0).toUpperCase() + scheduleForRoom.slice(1) + " Session" : "Study Session"}
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-slate-300 mb-2 block">Date</label>
                  <input
                    id="mobile-schedule-date"
                    type="date"
                    defaultValue={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                    className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 [color-scheme:dark]"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-300 mb-2 block">Time</label>
                  <input
                    id="mobile-schedule-time"
                    type="time"
                    defaultValue="14:00"
                    className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 [color-scheme:dark]"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Duration</label>
                <select id="mobile-schedule-duration" className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200">
                  <option>30 minutes</option>
                  <option>1 hour</option>
                  <option>1.5 hours</option>
                  <option>2 hours</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Recurring</label>
                <select 
                  id="mobile-schedule-recurring" 
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200"
                  onChange={(e) => {
                    const customOptions = document.getElementById('mobile-schedule-custom-options');
                    if (customOptions) {
                      customOptions.style.display = e.target.value === 'custom' ? 'block' : 'none';
                    }
                  }}
                >
                  <option value="none">One-time meeting</option>
                  <option value="daily">Every Day</option>
                  <option value="weekly">Every Week</option>
                  <option value="biweekly">Every 2 Weeks</option>
                  <option value="monthly">Every Month</option>
                  <option value="custom">Custom...</option>
                </select>
              </div>
              <div id="mobile-schedule-custom-options" style={{ display: 'none' }} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-slate-300 mb-2 block">Repeat every</label>
                    <input
                      id="mobile-schedule-custom-interval"
                      type="number"
                      min="1"
                      defaultValue="1"
                      className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-300 mb-2 block">Period</label>
                    <select id="mobile-schedule-custom-period" className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200">
                      <option value="days">Day(s)</option>
                      <option value="weeks">Week(s)</option>
                      <option value="months">Month(s)</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-slate-300 mb-2 block">Number of occurrences</label>
                  <input
                    id="mobile-schedule-custom-occurrences"
                    type="number"
                    min="1"
                    max="52"
                    defaultValue="4"
                    className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Invite participants (comma separated emails)</label>
                <textarea
                  id="mobile-schedule-participants"
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-slate-200 h-20 resize-none"
                  placeholder="student1@university.edu, student2@university.edu"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const title = (document.getElementById('mobile-schedule-title') as HTMLInputElement)?.value;
                    const date = (document.getElementById('mobile-schedule-date') as HTMLInputElement)?.value;
                    const time = (document.getElementById('mobile-schedule-time') as HTMLInputElement)?.value;
                    
                    if (date && time && title) {
                      playSound('success');
                      alert(`📅 Meeting scheduled!\n\n${title}\n${new Date(date).toLocaleDateString()} at ${time}\n\nCalendar event will be created.`);
                      setShowScheduleModal(false);
                    } else {
                      alert('Please fill in all fields');
                    }
                  }}
                  className="flex-1 px-4 py-2 rounded-lg bg-purple-500/20 text-purple-200 hover:bg-purple-500/30 text-sm font-semibold"
                >
                  Schedule
                </button>
                <button
                  onClick={() => setShowScheduleModal(false)}
                  className="px-4 py-2 rounded-lg bg-white/5 text-slate-300 hover:bg-white/10 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Agenda Modal */}
      {showAgendaModal && agendaForRoom && (
        <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#050309] border border-blue-400/30 rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">📋 Meeting Agenda</h3>
              <button onClick={() => {
                setShowAgendaModal(false);
                setAgendaForRoom('');
              }} className="text-slate-400 hover:text-white text-2xl">
                ×
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-300 mb-2 block">
                  Room: <span className="text-blue-400 font-semibold">
                    {agendaForRoom.charAt(0).toUpperCase() + agendaForRoom.slice(1)}
                  </span>
                </label>
              </div>
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Agenda Topics</label>
                <textarea
                  id="mobile-agenda-textarea"
                  defaultValue={roomAgendas[agendaForRoom] || ''}
                  placeholder="Example:\n\n1. Welcome (5 min)\n2. Review (10 min)\n3. Main topic (30 min)\n4. Q&A (10 min)\n5. Closing (5 min)"
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-sm text-slate-200 h-64 resize-none font-mono"
                />
                <p className="text-xs text-slate-500 mt-2">
                  💡 This agenda will be included when you share the invite link
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    const agendaText = (document.getElementById('mobile-agenda-textarea') as HTMLTextAreaElement)?.value?.trim();
                    
                    if (agendaText) {
                      setRoomAgendas(prev => ({
                        ...prev,
                        [agendaForRoom]: agendaText
                      }));
                      playSound('success');
                      alert('✅ Agenda saved!');
                    } else {
                      setRoomAgendas(prev => {
                        const updated = { ...prev };
                        delete updated[agendaForRoom];
                        return updated;
                      });
                      alert('Agenda cleared.');
                    }
                    
                    setShowAgendaModal(false);
                    setAgendaForRoom('');
                  }}
                  className="flex-1 px-4 py-2 rounded-lg bg-blue-500/20 text-blue-200 hover:bg-blue-500/30 text-sm font-semibold"
                >
                  Save Agenda
                </button>
                <button
                  onClick={() => {
                    setShowAgendaModal(false);
                    setAgendaForRoom('');
                  }}
                  className="px-4 py-2 rounded-lg bg-white/5 text-slate-300 hover:bg-white/10 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Link Copied Modal - WHIPT Styled */}
      {showLinkCopiedModal && (
        <div className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-gradient-to-br from-purple-900/95 via-indigo-900/95 to-purple-900/95 border-2 border-purple-400/50 rounded-3xl p-8 max-w-lg w-full mx-4 shadow-[0_25px_80px_rgba(168,85,247,0.5)] animate-in zoom-in-95 duration-300">
            {/* Logo/Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-xl animate-pulse">
                <span className="text-4xl">🔗</span>
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-black text-white mb-2 tracking-wide">
                Link Copied!
              </h2>
              <p className="text-sm text-purple-200 uppercase tracking-[0.3em] font-semibold">
                WHIPT • {copiedRoomName} Room
              </p>
            </div>

            {/* Message */}
            <div className="bg-black/30 rounded-2xl p-6 mb-6 border border-purple-400/20">
              <p className="text-amber-200 text-center text-sm leading-relaxed mb-4 font-medium">
                ✨ Paste into <span className="font-bold text-amber-100">email</span> or <span className="font-bold text-amber-100">text</span> to invite participants!
              </p>
              
              {/* Link Display */}
              <div className="bg-slate-950/80 rounded-xl p-4 border border-white/10">
                <p className="text-xs text-slate-400 mb-2 uppercase tracking-wider font-semibold">Your Invite Link:</p>
                <p className="text-sm text-slate-200 break-all font-mono leading-relaxed">
                  {copiedLink}
                </p>
              </div>
            </div>

            {/* Share Options */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <button
                onClick={() => {
                  window.location.href = `sms:?&body=Join my WHIPT session: ${copiedLink}`;
                }}
                className="flex flex-col items-center gap-2 p-3 rounded-xl bg-gradient-to-br from-green-600/20 to-emerald-600/20 border border-green-400/30 hover:from-green-600/30 hover:to-emerald-600/30 transition-all"
              >
                <span className="text-2xl">💬</span>
                <span className="text-xs text-green-200 font-semibold">Text</span>
              </button>
              <button
                onClick={() => {
                  window.location.href = `mailto:?subject=Join my WHIPT Session&body=Join me: ${copiedLink}`;
                }}
                className="flex flex-col items-center gap-2 p-3 rounded-xl bg-gradient-to-br from-blue-600/20 to-cyan-600/20 border border-blue-400/30 hover:from-blue-600/30 hover:to-cyan-600/30 transition-all"
              >
                <span className="text-2xl">📧</span>
                <span className="text-xs text-blue-200 font-semibold">Email</span>
              </button>
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(copiedLink);
                    playSound('click');
                  } catch (err) {
                    // Already copied
                  }
                }}
                className="flex flex-col items-center gap-2 p-3 rounded-xl bg-gradient-to-br from-purple-600/20 to-pink-600/20 border border-purple-400/30 hover:from-purple-600/30 hover:to-pink-600/30 transition-all"
              >
                <span className="text-2xl">📋</span>
                <span className="text-xs text-purple-200 font-semibold">Copy</span>
              </button>
            </div>

            {/* Close Button */}
            <button
              onClick={() => {
                setShowLinkCopiedModal(false);
                setCopiedLink('');
              }}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold text-sm uppercase tracking-wider transition-all shadow-lg"
            >
              Got It!
            </button>
          </div>
        </div>
      )}

      {/* WHIPT Transcripts Panel */}
      {showTranscriptsPanel && !transcriptsPanelMinimized && (
        <div className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-md flex items-start md:items-center justify-center">
          <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-purple-400/30 rounded-none md:rounded-3xl w-full max-w-5xl h-full md:h-auto md:max-h-[90vh] flex flex-col shadow-2xl overflow-y-auto">
            <WhiptTranscriptsPanel 
              onClose={() => setShowTranscriptsPanel(false)} 
              embedded={true}
              onMinimize={(minimized) => setTranscriptsPanelMinimized(minimized)}
            />
          </div>
        </div>
      )}
      
      {/* Minimized Transcripts Floating Button */}
      {showTranscriptsPanel && transcriptsPanelMinimized && (
        <button
          onClick={() => setTranscriptsPanelMinimized(false)}
          className="fixed bottom-32 left-4 z-[10000] px-4 py-3 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold shadow-2xl hover:shadow-purple-500/50 active:scale-95 transition-all flex items-center gap-2"
          title="Restore Transcription Panel"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="text-sm">Transcription</span>
        </button>
      )}

      {/* Session Recordings Panel */}
      {showRecordingsPanel && !recordingsPanelMinimized && (
        <SessionRecordingsPanel
          onClose={() => setShowRecordingsPanel(false)}
          onMinimize={(minimized) => setRecordingsPanelMinimized(minimized)}
        />
      )}
      {showRecordingsPanel && recordingsPanelMinimized && (
        <button
          onClick={() => setRecordingsPanelMinimized(false)}
          className="fixed bottom-4 right-4 z-[10000] px-4 py-3 rounded-2xl bg-gradient-to-r from-rose-600 to-pink-700 text-white font-bold shadow-2xl border border-rose-400/50 hover:brightness-110 transition-all flex items-center gap-3"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <span className="text-sm">Recordings</span>
        </button>
      )}

      {/* Upload Panel */}
      {showUploadPanel && (
        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 to-slate-950 border-2 border-blue-400/30 rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">📤 Upload Files</h3>
              <button
                onClick={() => {
                  setShowUploadPanel(false);
                  setUploadTabSelection('');
                }}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-sm text-slate-300 mb-4">
              Upload PDFs, Word documents, or text files to any category.
            </p>

            {/* Category Selection */}
            <div className="mb-4">
              <label className="text-sm text-slate-300 mb-2 block">Upload to which category?</label>
              <select
                value={uploadTabSelection}
                onChange={(e) => setUploadTabSelection(e.target.value)}
                className="w-full rounded-lg border border-white/20 bg-black/40 px-3 py-2 text-sm text-white"
              >
                <option value="">Select a category...</option>
                <option value="discussion">Discussion</option>
                <option value="assignment">Assignment</option>
                <option value="experiential">Experiential</option>
                <option value="journal">Journal</option>
              </select>
            </div>

            {/* File Input */}
            <div className="mb-6">
              <input
                type="file"
                id="file-upload-input"
                multiple
                accept=".pdf,.doc,.docx,.txt"
                onChange={(e) => handleFileUpload(e.target.files)}
                className="hidden"
                disabled={uploadingFiles || !uploadTabSelection}
              />
              <label
                htmlFor="file-upload-input"
                className={`
                  block border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
                  ${uploadTabSelection 
                    ? 'border-blue-400/50 bg-blue-600/10 hover:bg-blue-600/20' 
                    : 'border-white/20 bg-white/5 opacity-50 cursor-not-allowed'}
                `}
              >
                <svg className="w-12 h-12 text-blue-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-blue-200 mb-2">
                  {uploadingFiles ? 'Uploading...' : 'Drag & drop files here'}
                </p>
                <p className="text-xs text-slate-400 mb-4">or click to browse</p>
                <p className="text-xs text-slate-500">Max 10MB per file</p>
              </label>
            </div>

            {/* Close Button */}
            <button
              onClick={() => {
                setShowUploadPanel(false);
                setUploadTabSelection('');
              }}
              className="w-full px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white font-semibold transition-colors"
              disabled={uploadingFiles}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* WHIPT WALLET Panel */}
      {showDigitalWallet && (
        <DigitalWalletPanel
          documents={documents
            .filter(doc => doc.notebookTabId === 'travel' && doc.content && typeof doc.content === 'string')
            .map(doc => ({
              id: doc.id.toString(),
              title: doc.title,
              category: (doc.category as any) || 'other',
              content: doc.content,
              isProtected: doc.isProtected || false,
              created_at: doc.created_at || new Date().toISOString(),
              brand: (doc as any).brand,
              brandColor: (doc as any).brandColor,
              confirmationNumber: (doc as any).confirmationNumber,
              flightNumber: (doc as any).flightNumber,
            }))}
          onClose={() => setShowDigitalWallet(false)}
          onUnlock={async (docId: string, password: string) => {
            // Handle document unlocking with password
            // TODO: Implement actual unlock logic
            return true;
          }}
          onPreview={(doc) => {
            // Handle document preview
            if (!doc.isProtected && doc.content) {
              window.open(doc.content, '_blank');
            }
          }}
          onDownload={(doc) => {
            if (!doc.isProtected && doc.content) {
              const link = document.createElement('a');
              link.href = doc.content;
              link.download = doc.title;
              link.click();
            }
          }}
        />
      )}

      {/* Gmail Import Modal */}
      {showGmailImport && (
        <GmailImportModal
          isOpen={showGmailImport}
          onClose={() => setShowGmailImport(false)}
          accessToken={session?.provider_token || null}
          onImport={async (filename, content, password) => {
            try {
              // Convert base64 data URL to Blob
              const response = await fetch(content);
              const blob = await response.blob();
              const file = new File([blob], filename, { type: blob.type || 'application/pdf' });
              
              // Handle file upload - you'll need to implement this based on your mobile upload logic
              alert('Document imported successfully!');
            } catch (error) {
              console.error('Failed to import from Gmail:', error);
              alert('Failed to import document. Please try again.');
            }
          }}
        />
      )}

      {/* PWA Install Prompt */}
      <InstallPWAPrompt />

      {/* Simple Practice Video Room */}
      {showPracticeRoom && (
        <SimpleVideoRoomPractice onClose={() => setShowPracticeRoom(false)} />
      )}
      
      {/* Mobile Overlay Popup */}
      {showMobileOverlay && mobileOverlayData.type && !showFormSelector && !activeFormType && (
        <>
          <div 
            className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-md"
            onClick={closeTabOverlay}
          />
          
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
            <div 
              className="w-full max-w-2xl max-h-[85vh] pointer-events-auto animate-in fade-in zoom-in duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-br from-slate-900/95 via-purple-900/20 to-slate-900/95 backdrop-blur-3xl rounded-2xl border border-white/10 shadow-[0_25px_80px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col">
                
                {/* Header */}
                <div className={`${(() => {
                  const colors = {
                    discussion: 'bg-blue-900/20 border-b-blue-400/20',
                    assignment: 'bg-amber-900/20 border-b-amber-400/20',
                    experiential: 'bg-emerald-900/20 border-b-emerald-400/20',
                    journal: 'bg-purple-900/20 border-b-purple-400/20',
                    video: 'bg-pink-900/20 border-b-pink-400/20',
                    home: 'bg-rose-900/20 border-b-rose-400/20',
                    work: 'bg-indigo-900/20 border-b-indigo-400/20',
                    travel: 'bg-sky-900/20 border-b-sky-400/20'
                  };
                  return colors[mobileOverlayData.type as keyof typeof colors] || colors.discussion;
                })()} backdrop-blur-2xl border-b px-6 py-4 flex items-center justify-between`}>
                  <div>
                    <h2 className="text-2xl font-bold text-white capitalize">
                      {mobileOverlayData.type === 'video' ? 'Social/Family' : mobileOverlayData.type}
                    </h2>
                    {/* Only show class name for academic tabs, not personal organizer tabs */}
                    {!['work', 'home', 'travel'].includes(mobileOverlayData.type) && mobileOverlayData.className && (
                      <p className="text-white/70 text-sm mt-1">{mobileOverlayData.className}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        // For class categories, open form directly without selector
                        if (['discussion', 'assignment', 'experiential', 'journal'].includes(mobileOverlayData.type)) {
                          setActiveFormType('class-item');
                        } else {
                          setShowFormSelector(true);
                        }
                      }}
                      className="px-4 py-2.5 bg-emerald-500/20 border-2 border-emerald-400/30 text-emerald-200 text-base font-semibold rounded-xl hover:bg-emerald-500/30 transition-all flex items-center gap-2 touch-manipulation"
                    >
                      <span className="text-xl">+</span>
                      <span>New</span>
                    </button>
                    <button
                      onClick={closeTabOverlay}
                      className="w-12 h-12 bg-white/5 hover:bg-white/10 border-2 border-white/20 rounded-xl flex items-center justify-center text-white text-2xl transition-all hover:rotate-90"
                    >
                      ×
                    </button>
                  </div>
                </div>
                
                {/* Content */}
                <div className="flex-1 overflow-y-auto px-4 py-3 max-h-[70vh]">
                  {(() => {
                    const activeClass = classNotebooks.find(c => c.id === mobileOverlayData.classId);
                    const category = activeClass?.categories?.find(cat => cat.id === mobileOverlayData.categoryId);
                    
                    const upcomingItems = sortByDueDate(filterOutVirtualRoomItems(category?.buckets?.upcoming || []));
                    const completedItems = sortByDueDate(filterOutVirtualRoomItems(category?.buckets?.completed || []));
                    
                    // Group items by listName
                    const groupedUpcoming: Record<string, any[]> = {};
                    const groupedCompleted: Record<string, any[]> = {};
                    
                    upcomingItems.forEach(item => {
                      const listName = item.metadata?.listName || item.listName || 'Uncategorized';
                      if (!groupedUpcoming[listName]) groupedUpcoming[listName] = [];
                      groupedUpcoming[listName].push(item);
                    });
                    
                    completedItems.forEach(item => {
                      const listName = item.metadata?.listName || item.listName || 'Uncategorized';
                      if (!groupedCompleted[listName]) groupedCompleted[listName] = [];
                      groupedCompleted[listName].push(item);
                    });
                    
                    const allListNames = [...new Set([...Object.keys(groupedUpcoming), ...Object.keys(groupedCompleted)])];
                    
                    // Color palette for list names
                    const getListColor = (index: number) => {
                      const colors = [
                        'from-pink-400 via-rose-400 to-red-400',
                        'from-purple-400 via-violet-400 to-indigo-400',
                        'from-blue-400 via-cyan-400 to-teal-400',
                        'from-green-400 via-emerald-400 to-lime-400',
                        'from-yellow-400 via-amber-400 to-orange-400',
                        'from-fuchsia-400 via-pink-400 to-rose-400',
                        'from-indigo-400 via-blue-400 to-cyan-400',
                        'from-teal-400 via-green-400 to-emerald-400'
                      ];
                      return colors[index % colors.length];
                    };
                    
                    return (
                      <>
                        {allListNames.length > 0 ? (
                          allListNames.map((listName, index) => {
                            const upcoming = groupedUpcoming[listName] || [];
                            const completed = groupedCompleted[listName] || [];
                            
                            return (
                              <div key={listName} className="mb-6">
                                {/* Animated List Name Header */}
                                <div className="mb-4 pb-3 border-b border-white/20">
                                  <h3 className={`
                                    text-2xl font-black uppercase tracking-wider text-center
                                    bg-gradient-to-r ${getListColor(index)}
                                    bg-clip-text text-transparent
                                    animate-[pulse_3s_ease-in-out_infinite,scale_4s_ease-in-out_infinite]
                                    drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]
                                    cursor-default
                                  `}
                                  style={{
                                    animation: 'pulse 3s ease-in-out infinite, gentleScale 4s ease-in-out infinite'
                                  }}
                                  >
                                    {listName}
                                  </h3>
                                </div>
                                
                                {/* In Progress Items */}
                                {upcoming.length > 0 && (
                                  <div className="mb-4">
                                    <h4 className="text-sm font-bold text-amber-400 mb-2">
                                      📝 In Progress ({upcoming.length})
                                    </h4>
                                    <div className="space-y-2">
                                      {upcoming.map((item) => (
                                        <div 
                                          key={item.id}
                                          className="bg-white/5 border border-white/10 rounded-lg p-3 group"
                                        >
                                          <div className="flex items-start justify-between gap-2 mb-2">
                                            <h5 className="text-amber-400 font-semibold text-sm flex-1">{item.title}</h5>
                                            <div className="flex gap-1">
                                              {/* Share button for meetings with virtual room */}
                                              {item.metadata?.virtualRoom?.roomUrl && (
                                                <button
                                                  onClick={() => {
                                                    const url = item.metadata.virtualRoom.roomUrl;
                                                    const title = item.title;
                                                    const date = item.dueDate ? new Date(item.dueDate).toLocaleString() : 'TBD';
                                                    const shareText = `Join me for "${title}" on ${date}\n\nVirtual Room: ${url}`;
                                                    
                                                    // Try native share API first
                                                    if (navigator.share) {
                                                      navigator.share({
                                                        title: `Meeting: ${title}`,
                                                        text: shareText,
                                                        url: url
                                                      }).catch(() => {
                                                        // Fallback to copy
                                                        navigator.clipboard.writeText(shareText);
                                                        alert('Meeting link copied! Share it via your preferred app.');
                                                      });
                                                    } else {
                                                      // Fallback to clipboard
                                                      navigator.clipboard.writeText(shareText);
                                                      alert('Meeting link copied to clipboard!');
                                                    }
                                                  }}
                                                  className="p-1.5 bg-blue-500/20 border border-blue-400/30 text-blue-300 text-xs rounded hover:bg-blue-500/30 transition-all"
                                                  title="Share Meeting Link"
                                                >
                                                  🔗
                                                </button>
                                              )}
                                              {/* Edit button */}
                                              <button
                                                onClick={() => {
                                                  // Set editing item and open appropriate form
                                                  setEditingItem(item);
                                                  const formTypeMap: Record<string, string> = {
                                                    'shopping': 'shopping',
                                                    'bill': 'bill',
                                                    'recipe': 'recipe',
                                                    'meeting': 'meeting',
                                                    'deadline': 'deadline',
                                                    'packing': 'packing',
                                                    'itinerary': 'itinerary',
                                                    'project': 'project',
                                                    'maintenance': 'maintenance'
                                                  };
                                                  const formType = formTypeMap[item.metadata?.type] || item.metadata?.type || 'shopping';
                                                  setActiveFormType(formType);
                                                  setShowFormSelector(false);
                                                }}
                                                className="p-1.5 bg-purple-500/20 border border-purple-400/30 text-purple-300 text-xs rounded hover:bg-purple-500/30 transition-all"
                                                title="Edit"
                                              >
                                                ✏️
                                              </button>
                                              <button
                                                onClick={() => {
                                                  const updatedNotebooks = classNotebooks.map(notebook => {
                                                    if (notebook.id === mobileOverlayData.classId) {
                                                      return {
                                                        ...notebook,
                                                        categories: notebook.categories.map(cat => {
                                                          if (cat.id === mobileOverlayData.categoryId) {
                                                            return {
                                                              ...cat,
                                                              buckets: {
                                                                ...cat.buckets,
                                                                upcoming: (cat.buckets?.upcoming || []).filter(i => i.id !== item.id)
                                                              }
                                                            };
                                                          }
                                                          return cat;
                                                        })
                                                      };
                                                    }
                                                    return notebook;
                                                  });
                                                  
                                                  setClassNotebooks(updatedNotebooks);
                                                  if (user) saveNotebooks(user.id, updatedNotebooks);
                                                }}
                                                className="p-1.5 bg-red-500/20 border border-red-400/30 text-red-300 text-xs rounded hover:bg-red-500/30 transition-all"
                                                title="Delete"
                                              >
                                                🗑️
                                              </button>
                                              <button
                                                onClick={() => {
                                                  const updatedNotebooks = classNotebooks.map(notebook => {
                                                    if (notebook.id === mobileOverlayData.classId) {
                                                      return {
                                                        ...notebook,
                                                        categories: notebook.categories.map(cat => {
                                                          if (cat.id === mobileOverlayData.categoryId) {
                                                            return {
                                                              ...cat,
                                                              buckets: {
                                                                upcoming: (cat.buckets?.upcoming || []).filter(i => i.id !== item.id),
                                                                completed: [
                                                                  ...(cat.buckets?.completed || []),
                                                                  { ...item, completed: true, status: 'completed' }
                                                                ]
                                                              }
                                                            };
                                                          }
                                                          return cat;
                                                        })
                                                      };
                                                    }
                                                    return notebook;
                                                  });
                                                  
                                                  setClassNotebooks(updatedNotebooks);
                                                  if (user) saveNotebooks(user.id, updatedNotebooks);
                                                }}
                                                className="p-1.5 bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-xs rounded hover:bg-emerald-500/30 transition-all"
                                                title="Mark Complete"
                                              >
                                                ✓
                                              </button>
                                            </div>
                                          </div>
                                          {item.description && (
                                            <p className="text-slate-300 text-xs mb-2">{item.description}</p>
                                          )}
                                          {item.notes && (
                                            <p className="text-slate-300 text-xs mb-2">{item.notes}</p>
                                          )}
                                          {item.dueDate && (
                                            <span className="text-slate-400 text-xs">
                                              Due: {new Date(item.dueDate).toLocaleDateString()}
                                            </span>
                                          )}
                                          {/* Virtual Room Link - Make it clickable */}
                                          {item.metadata?.virtualRoom?.roomUrl && (
                                            <div className="mt-2">
                                              <button
                                                onClick={() => {
                                                  // Extract room ID from URL and navigate with autojoin
                                                  const roomId = item.metadata.virtualRoom.roomId;
                                                  if (roomId) {
                                                    window.location.href = `/organizer?room=${roomId}&autojoin=1`;
                                                  } else {
                                                    // Fallback - try to open the full URL
                                                    window.open(item.metadata.virtualRoom.roomUrl, '_blank');
                                                  }
                                                }}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                                              >
                                                🎥 Join Virtual Room →
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Completed Items */}
                                {completed.length > 0 && (
                                  <div>
                                    <h4 className="text-sm font-bold text-green-400 mb-2">
                                      ✅ Completed ({completed.length})
                                    </h4>
                                    <div className="space-y-2">
                                      {completed.map((item) => (
                                        <div 
                                          key={item.id}
                                          className="bg-white/5 border border-white/10 rounded-lg p-3 opacity-60"
                                        >
                                          <div className="flex items-start justify-between gap-2">
                                            <h5 className="text-green-400 font-semibold text-sm line-through flex-1">{item.title}</h5>
                                            <div className="flex gap-1">
                                              {/* Edit button for completed items */}
                                              <button
                                                onClick={() => {
                                                  // Set editing item and open appropriate form
                                                  setEditingItem(item);
                                                  const formTypeMap: Record<string, string> = {
                                                    'shopping': 'shopping',
                                                    'bill': 'bill',
                                                    'recipe': 'recipe',
                                                    'meeting': 'meeting',
                                                    'deadline': 'deadline',
                                                    'packing': 'packing',
                                                    'itinerary': 'itinerary',
                                                    'project': 'project',
                                                    'maintenance': 'maintenance'
                                                  };
                                                  const formType = formTypeMap[item.metadata?.type] || item.metadata?.type || 'shopping';
                                                  setActiveFormType(formType);
                                                  setShowFormSelector(false);
                                                }}
                                                className="p-1.5 bg-purple-500/20 border border-purple-400/30 text-purple-300 text-xs rounded hover:bg-purple-500/30 transition-all"
                                                title="Edit"
                                              >
                                                ✏️
                                              </button>
                                              <button
                                                onClick={() => {
                                                  const updatedNotebooks = classNotebooks.map(notebook => {
                                                    if (notebook.id === mobileOverlayData.classId) {
                                                      return {
                                                        ...notebook,
                                                        categories: notebook.categories.map(cat => {
                                                          if (cat.id === mobileOverlayData.categoryId) {
                                                            return {
                                                              ...cat,
                                                              buckets: {
                                                                ...cat.buckets,
                                                                completed: (cat.buckets?.completed || []).filter(i => i.id !== item.id)
                                                              }
                                                            };
                                                          }
                                                          return cat;
                                                        })
                                                      };
                                                    }
                                                    return notebook;
                                                  });
                                                  
                                                  setClassNotebooks(updatedNotebooks);
                                                  if (user) saveNotebooks(user.id, updatedNotebooks);
                                                }}
                                                className="p-1.5 bg-red-500/20 border border-red-400/30 text-red-300 text-xs rounded hover:bg-red-500/30 transition-all"
                                                title="Delete"
                                              >
                                                🗑️
                                              </button>
                                            </div>
                                          </div>
                                          {item.dueDate && (
                                            <span className="text-slate-500 text-xs mt-1 block">
                                              {new Date(item.dueDate).toLocaleDateString()}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-center py-8 text-slate-400">
                            <p>No items yet. Click "+ New" to create your first list!</p>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MobileOrganizerDashboard;
